/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { initNotifications, requestNotifPermission, pushNotif, subscribeToPortalPush } from '../utils/portalNotifications';
import { createLogger } from '../utils/logger';
import { AIR as AIR_T, AIR_STYLE as AIR, AIR_CSS, AirSelect, AirSwitch, AirPriceRange, AirPriceSeg } from '../styles/airTheme';
import { EventPlan, EventBudget, EventOptionSheet, togglePickIn, picksMessage } from './portal/EventPortal';

// REQUIRED in production: set REACT_APP_API_URL to your backend URL in the build environment
// e.g. REACT_APP_API_URL=https://api.marqlandstudios.com  (no trailing slash)
// Without this, every API call falls back to localhost:5000 and fails in production.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const CONTACT_EMAIL = 'info@marqland.com';

// ─── Logger ───────────────────────────────────────────────────────────────────
const log = createLogger('ClientPortalView');
const sdLog = createLogger('SmartDescription');

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN SYSTEM — "The Digital Concierge" (DESIGN.md)
//
// Surfaces:   #222222 base | #222222 container | #484848 elevated
// Accent:     #e6c273 → #c5a357  (Champagne Gold gradient)
// On-primary: #3f2e00
// Typography: "Noto Serif" for display/headlines | "Manrope" for body
// Rules:      No 1px borders for sections — use tonal shifts + negative space
//             Ghost borders: rgba(white, 0.15) only when containment is needed
//             Shadows: blur 40px, rgba(0,0,0,0.4)
//             Roundedness: xl = 12px
// ─────────────────────────────────────────────────────────────────────────────

const DS = {
  base: '#f7f7f7',
  cont: '#ffffff',
  contHi: '#f7f7f7',
  contTop: '#ebebeb',
  bright: '#dddddd',
  gold: '#b8975a',
  goldHi: '#d4b06a',
  onGold: '#222222',
  outline: '#ebebeb',
  muted: '#929292',
  text: '#222222',
  sub: '#6a6a6a',
  navy: '#222222',
};

const GOLD_GRAD = 'linear-gradient(135deg, #d4b06a, #b8975a)';
const GLASS_BG = '#ffffff';

// ── Shipment tracking tab config ──────────────────────────────────────────────
const SHIP_PAGE_SIZES = [25, 50, 100];
const SHIP_PAGE_BTN = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 34, height: 34, padding: '0 10px', background: '#fff', border: '1px solid #dddddd', borderRadius: 8, color: '#222222', fontSize: 12, fontWeight: 600, fontFamily: "'Jost',sans-serif" };

// Maps a courier/shipping partner name (as stored on the shipment record) to its
// public tracking page. The user still has to paste in the tracking number
// themselves — we just get them to the right page in a new tab.
// Matching is case-insensitive and tolerant of partial names (e.g. "Blue Dart Express"
// still matches "Blue Dart"), so keep the keys as short canonical fragments.
const COURIER_TRACKING_URLS = {
  'delhivery': 'https://www.delhivery.com/tracking',
  'blue dart': 'https://www.bluedart.com/tracking',
  'bluedart': 'https://www.bluedart.com/tracking',
  'dtdc': 'https://www.dtdc.in/tracking.asp',
  'ekart': 'https://ekartlogistics.com/track/',
  'xpressbees': 'https://www.xpressbees.com/track',
  'shadowfax': 'https://www.shadowfax.in/track',
  'india post': 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx',
  'speed post': 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx',
  'fedex': 'https://www.fedex.com/en-in/tracking.html',
  'dhl': 'https://www.dhl.com/in-en/home/tracking.html',
  'ecom express': 'https://ecomexpress.in/tracking/',
  'amazon': 'https://www.amazon.in/gp/your-account/order-history',
  'shree maruti': 'https://www.shreemaruti.com/track',
  'professional couriers': 'https://www.tpcindia.com/track_results.aspx',
  'gati': 'https://www.gati.com/track-your-shipment/',
  'trackon': 'https://trackon.in/',
  'ups': 'https://www.ups.com/track',
};

// Resolves a shipping partner's tracking URL. Falls back to a generic Google
// search for the courier's tracking page when we don't have it mapped, so the
// button is still useful instead of doing nothing.
const getCourierTrackingUrl = (partnerName) => {
  if (!partnerName) return null;
  const key = partnerName.trim().toLowerCase();
  const match = Object.keys(COURIER_TRACKING_URLS).find(k => key.includes(k));
  if (match) return COURIER_TRACKING_URLS[match];
  return `https://www.google.com/search?q=${encodeURIComponent(partnerName + ' courier tracking')}`;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const toINR = v => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const fmtSz = b => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

// ── Input sanitization (XSS hardening) ────────────────────────────────────────
// React already escapes everything it renders to the DOM here (this file never
// uses dangerouslySetInnerHTML), so none of the free-text fields below — chat
// name, chat message, hamper label — can execute a script simply by being
// displayed back in THIS app. This exists as defense-in-depth for every other
// place the same text can end up: the team's admin dashboard, exported PDFs,
// email notifications, or any future consumer of this data that might not
// escape it as carefully as React does. We strip active-content patterns at
// the point of entry so a malicious payload never leaves the client typing it.
//
// Deliberately conservative — it removes tag-like constructs (`<script>`,
// `<img onerror=...>`, `<b>`) and inline event-handler attributes, but leaves
// ordinary punctuation alone (e.g. "<3", "cost < budget" survive untouched
// because a bare `<` not followed by a letter never matches a tag).
const SCRIPT_TAG_RE = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
const HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/g;
const EVENT_HANDLER_RE = /\bon[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const JS_URL_RE = /javascript\s*:/gi;

const sanitizeText = (raw, maxLen = 500) => {
  if (typeof raw !== 'string') return '';
  return raw
    .replace(SCRIPT_TAG_RE, '')
    .replace(HTML_TAG_RE, '')
    .replace(EVENT_HANDLER_RE, '')
    .replace(JS_URL_RE, '')
    .slice(0, maxLen);
};
const fmtT = d => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });

// ── Inline feather-weight icons ───────────────────────────────────────────────
const Ic = {
  send: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
  attach: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>,
  dl: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  ext: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>,
  pin: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  star: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
  file: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  mail: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>,
  close: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  share: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>,
  zoom: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>,
  heart: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>,
  heartO: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>,
  play: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  upArrow: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5" /><polyline points="5 12 12 5 19 12" /></svg>,
  search: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  chevL: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>,
  chevR: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>,
};

// ── Lightbox — supports single image OR gallery navigation ───────────────────
// Call with: setLightbox({ src, alt })              → single image
// Call with: setLightbox({ src, alt, all, idx })    → gallery with prev/next
const Lightbox = ({ src, alt, all, startIdx, onClose }) => {
  const gallery = all && all.length > 1 ? all : null;
  const [cur, setCur] = React.useState(startIdx || 0);
  const imgs = gallery || [src];
  const curSrc = gallery ? imgs[cur] : src;

  const prev = (e) => { e.stopPropagation(); setCur(i => (i - 1 + imgs.length) % imgs.length); };
  const next = (e) => { e.stopPropagation(); setCur(i => (i + 1) % imgs.length); };

  useEffect(() => {
    const fn = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && gallery) setCur(i => (i - 1 + imgs.length) % imgs.length);
      if (e.key === 'ArrowRight' && gallery) setCur(i => (i + 1) % imgs.length);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose, gallery]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(5,8,16,0.96)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, backdropFilter: 'blur(24px)' }}>
      {/* Close */}
      <button onClick={onClose} style={{ position: 'fixed', top: 26, right: 26, background: 'none', border: 'none', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.32)', zIndex: 10000 }}>
        {Ic.close}
      </button>

      {/* Prev arrow */}
      {gallery && (
        <button onClick={prev} style={{ position: 'fixed', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 16, cursor: 'pointer', color: 'rgba(255,255,255,0.22)', zIndex: 10000 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}

      {/* Image */}
      <img src={curSrc} alt={alt} onClick={e => e.stopPropagation()}
        style={{ maxWidth: '82vw', maxHeight: '86vh', borderRadius: 16, objectFit: 'contain', boxShadow: '0 40px 80px rgba(0,0,0,0.4)', transition: 'opacity 0.2s ease' }} />

      {/* Next arrow */}
      {gallery && (
        <button onClick={next} style={{ position: 'fixed', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 16, cursor: 'pointer', color: 'rgba(255,255,255,0.22)', zIndex: 10000 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      )}

      {/* Counter */}
      {gallery && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'rgba(10,20,34,0.8)', color: 'rgba(255,255,255,0.5)', borderRadius: 20, padding: '5px 16px', fontSize: 12, fontWeight: 700, backdropFilter: 'blur(8px)', fontFamily: "'Jost',sans-serif", zIndex: 10000 }}>
          {cur + 1} / {imgs.length}
        </div>
      )}

      {/* Thumbnail strip */}
      {gallery && imgs.length > 1 && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', bottom: 60, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6, padding: '6px 10px', background: 'rgba(10,20,34,0.8)', borderRadius: 10, backdropFilter: 'blur(8px)', zIndex: 10000, maxWidth: '80vw', overflowX: 'auto' }}>
          {imgs.map((src, i) => (
            <div key={i} onClick={() => setCur(i)} style={{ width: 44, height: 44, borderRadius: 6, overflow: 'hidden', flexShrink: 0, border: `2px solid ${i === cur ? '#b8975a' : 'transparent'}`, cursor: 'pointer', transition: 'border-color .2s' }}>
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Attachment chip ────────────────────────────────────────────────────────────
// Files attached on the message board open without any OneDrive sign-in: the
// API streams them (GET /api/portal/public/:slug/attachments/...).
//   Images → in-page lightbox.
//   PDF    → the browser's own PDF viewer in a new tab (phones hand it to
//            their PDF app / preview).
//   Word, Excel, PowerPoint → laptops: Microsoft's free Office web viewer in a
//            new tab (no account needed). Phones/iPads: the file itself, which
//            iOS previews and Android offers to the installed Office app.
//   Download → saved to the device; opening it from there uses whichever
//            app is installed (Word, Excel, Acrobat …).
// A web page can't detect or launch installed desktop apps directly, so
// "open in the native app" on a laptop goes through Download.
const attachmentUrl = (slug, messageId, index, name) =>
  `${API_BASE}/api/portal/public/${slug}/attachments/${messageId}/${index}/${encodeURIComponent(name || 'file')}`;

const OFFICE_EXT = /\.(docx?|xlsx?|pptx?)$/i;
const fileKind = (att) => {
  const name = att.name || '';
  const mime = att.mimeType || '';
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic)$/i.test(name)) return 'image';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name)) return 'pdf';
  if (OFFICE_EXT.test(name)) {
    if (/\.docx?$/i.test(name)) return 'word';
    if (/\.xlsx?$/i.test(name)) return 'excel';
    return 'powerpoint';
  }
  if (mime.startsWith('video/')) return 'video';
  return 'file';
};
const FILE_BADGE = {
  word: { label: 'DOC', color: '#2b579a' }, excel: { label: 'XLS', color: '#217346' },
  powerpoint: { label: 'PPT', color: '#c43e1c' }, pdf: { label: 'PDF', color: '#b91c1c' },
  image: { label: 'IMG', color: '#7c6a46' }, video: { label: 'VID', color: '#6d28d9' }, file: { label: 'FILE', color: '#64748b' },
};
const isTouchDevice = () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

const AttachChip = ({ att, fileUrl, onImage }) => {
  const kind = fileKind(att);
  const badge = FILE_BADGE[kind];
  const isR2 = att.storage === 'r2' || (kind === 'image' && !/sharepoint\.com|1drv\.ms|onedrive/i.test(att.url || ''));
  const viewUrl = isR2 ? att.url : fileUrl;
  const downloadUrl = isR2 ? att.url : `${fileUrl}?download=1`;

  const open = (e) => {
    e.preventDefault();
    log.debug('Opening attachment', { name: att.name, kind });
    if (kind === 'image' && onImage) return onImage(viewUrl);
    let target = viewUrl;
    if (['word', 'excel', 'powerpoint'].includes(kind) && !isTouchDevice()) {
      target = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
    } else if (kind === 'file') {
      target = downloadUrl;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const actionSt = { fontSize: 12, fontWeight: 700, color: '#7d6233', textDecoration: 'none', background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", display: 'inline-flex', alignItems: 'center', gap: 4 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.75)', border: '1px solid #ebebeb', borderRadius: 8, marginTop: 6, maxWidth: 360, minWidth: 0 }}>
      <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 6, background: badge.color, color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Jost',sans-serif" }}>{badge.label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a href={viewUrl} onClick={open} title={att.name}
          style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', fontFamily: "'Jost',sans-serif" }}>{att.name}</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 1 }}>
          {att.size > 0 && <span style={{ fontSize: 12, color: '#999', fontFamily: "'Jost',sans-serif", whiteSpace: 'nowrap' }}>{fmtSz(att.size)}</span>}
          <button onClick={open} style={actionSt}>{Ic.ext} Open</button>
          <a href={downloadUrl} download={att.name} target="_blank" rel="noreferrer" style={actionSt}>{Ic.dl} Download</a>
        </div>
      </div>
    </div>
  );
};

// ── Pending file strip ─────────────────────────────────────────────────────────
const FileStrip = ({ files, onRemove }) => {
  if (!files.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 18px 0' }}>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f7f7f7', border: '1px solid #ebebeb', borderRadius: 4, padding: '4px 10px' }}>
          <span style={{ color: '#7d6233', display: 'flex' }}>{Ic.file}</span>
          <span style={{ fontSize: 12, color: '#6a6a6a', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
          <button onClick={() => onRemove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0, display: 'flex', marginLeft: 2 }}>{Ic.close}</button>
        </div>
      ))}
    </div>
  );
};

// ── Refined Glass Card ─────────────────────────────────────────────────────────
// Follows DESIGN.md "Glass & Gradient Rule": semi-transparent surface-variant + backdrop-blur
const GlassCard = ({ children, style = {}, delay = 0, onHover }) => {
  const ref = useRef(null);
  return (
    <div ref={ref} style={{
      background: '#fff',
      border: '1px solid #ebebeb',
      borderRadius: 4,
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1), box-shadow 0.5s ease',
      animation: `obsidianFadeIn .5s ease ${delay}s both`,
      ...style,
    }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.09)';
        onHover?.('enter');
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)';
        onHover?.('leave');
      }}>
      {children}
    </div>
  );
};

// ── Price Box — uses tonal layering per DESIGN.md ─────────────────────────────
const PBox = ({ label, value, sub, amber }) => (
  <div style={{
    background: amber ? 'rgba(184,151,90,0.08)' : '#ffffff',
    border: `1px solid ${amber ? 'rgba(184,151,90,0.2)' : '#ebebeb'}`,
    borderRadius: 10, padding: '10px 14px', textAlign: 'center',
  }}>
    <div style={{ fontSize: 12, color: amber ? '#b8975a' : '#929292', fontWeight: 700, fontFamily: "'Jost',sans-serif", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: amber ? '#d4b06a' : '#222222', fontFamily: "'Jost',sans-serif" }}>{toINR(value)}</div>
    {sub && <div style={{ fontSize: 12, color: '#929292', marginTop: 1, fontFamily: "'Jost',sans-serif" }}>{sub}</div>}
  </div>
);

// ── YouTube Embed ─────────────────────────────────────────────────────────────
// Extracts video ID from various YouTube URL formats and renders an embed
const getYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

// YouTube's own overlays (title bar, channel, share, "watch on YouTube", related
// videos) cover most of a small player. This plays the video with controls off
// and the iframe unclickable, so none of that appears; a click on the video
// plays / pauses it through the IFrame API's postMessage commands.
const ytCommand = (frame, func) => {
  try { frame?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*'); } catch { /* player not ready */ }
};

const YouTubeEmbed = ({ url, title }) => {
  const [show, setShow] = React.useState(false);
  const [paused, setPaused] = React.useState(false);
  const frame = React.useRef(null);
  const videoId = getYouTubeId(url);
  if (!videoId) return null;
  // Always a landscape frame; the portal doesn't lay out Shorts vertically.
  const aspect = '16/9';
  const thumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const params = new URLSearchParams({
    autoplay: '1', controls: '0', disablekb: '1', fs: '0', rel: '0', modestbranding: '1',
    iv_load_policy: '3', playsinline: '1', loop: '1', playlist: videoId, enablejsapi: '1',
    origin: window.location.origin,
  });
  const roundBtn = { width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.72)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
  const togglePlay = () => { ytCommand(frame.current, paused ? 'playVideo' : 'pauseVideo'); setPaused(p => !p); };
  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', background: '#000', border: `1px solid rgba(255,255,255,0.08)`, marginTop: 12 }}>
      {!show ? (
        <div style={{ position: 'relative', cursor: 'pointer', aspectRatio: aspect, background: '#f7f7f7' }} onClick={() => setShow(true)}>
          <img src={thumb} alt={title || 'Video'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,10,20,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 54, height: 54, background: 'rgba(184,151,90,0.92)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(184,151,90,0.4)', transition: 'transform .2s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
              <span style={{ color: '#222222', marginLeft: 3 }}>{Ic.play}</span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', aspectRatio: aspect, overflow: 'hidden' }}>
          <iframe
            ref={frame}
            width="100%" height="100%"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?${params}`}
            title={title || 'Video'}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            tabIndex={-1}
            style={{ display: 'block', pointerEvents: 'none' }}
          />
          {/* Click anywhere on the video to pause / play */}
          <button type="button" onClick={togglePlay} aria-label={paused ? 'Play video' : 'Pause video'}
            style={{ position: 'absolute', inset: 0, background: paused ? 'rgba(5,10,20,0.35)' : 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {paused && <span style={{ ...roundBtn, width: 54, height: 54, background: 'rgba(184,151,90,0.92)', color: '#222222' }}><span style={{ marginLeft: 3, display: 'flex' }}>{Ic.play}</span></span>}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Brand Video Embed (uploaded / non-YouTube video files) ───────────────────
// Plays directly inline in the card via a native <video> tag — no new tab,
// no login prompt. Used for manually uploaded brand videos, as opposed to
// YouTubeEmbed above which handles youtube.com links.
const BrandVideoEmbed = ({ url, title, poster }) => {
  if (!url) return null;
  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', background: '#000', border: `1px solid rgba(255,255,255,0.08)`, marginTop: 12 }}>
      <video
        controls
        preload="metadata"
        poster={poster || undefined}
        style={{ width: '100%', aspectRatio: '16/9', display: 'block', background: '#000' }}
      >
        <source src={url} />
        Your browser does not support inline video playback.
      </video>
    </div>
  );
};

// ── Product Video Embed — NEW ──────────────────────────────────────────────
// Wraps YouTubeEmbed/BrandVideoEmbed above so this file only needs ONE call
// site regardless of where a product's video actually lives:
//   - item.videoSource === 'link'   → item.videoUrl is already playable as-is
//     (YouTube or a direct brand URL) — no network round trip needed.
//   - item.videoSource === 'upload' → the video is a OneDrive file. Graph's
//     download URL expires in ~1hr, so nothing is cached — this resolves a
//     fresh one right before render, every time the card mounts, via a
//     public (unauthenticated) portal-scoped endpoint rather than the internal
//     /api/v2/products/:id/video-stream route (that one requires staff auth,
//     and clients never get a login for the portal).
//
// Backend: GET /api/portal/public/:slug/products/:productId/video-stream
//   (routes/clientPortalRoutes.js) → { url, expiresInSeconds }. It answers
//   404 unless productId belongs to THIS portal's items or combo items, so a
//   slug can't be used to probe other products' OneDrive links.
const ProductVideoEmbed = ({ item, slug }) => {
  const hasUpload = item.videoSource === 'upload';
  const hasLink   = !hasUpload && Boolean(item.videoUrl);

  const [resolvedUrl, setResolvedUrl] = React.useState(hasLink ? item.videoUrl : null);
  const [resolving, setResolving]     = React.useState(hasUpload);
  const [failed, setFailed]           = React.useState(false);

  React.useEffect(() => {
    if (!hasUpload) return;
    let cancelled = false;
    setResolving(true);
    setFailed(false);
    fetch(`${API_BASE}/api/portal/public/${slug}/products/${item.productId}/video-stream`)
      .then(r => { if (!r.ok) throw new Error('resolve failed'); return r.json(); })
      .then(data => { if (!cancelled) setResolvedUrl(data.url); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setResolving(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUpload, item.productId, slug]);

  if (!hasUpload && !hasLink) return null;
  if (resolving) {
    return (
      <div style={{ borderRadius: 10, background: '#f7f7f7', border: '1px solid #ebebeb', marginTop: 12, aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a6a6a', fontSize: 12, fontFamily: "'Jost',sans-serif" }}>
        Loading video…
      </div>
    );
  }
  if (failed || !resolvedUrl) return null; // fail quietly on the client side — nothing to embed

  return getYouTubeId(resolvedUrl)
    ? <YouTubeEmbed url={resolvedUrl} title={item.name} />
    : <BrandVideoEmbed url={resolvedUrl} title={item.name} poster={item.imageUrl} />;
};

// ── Offsite property video ───────────────────────────────────────────────────
// A video the team saved from the property's website (stored in OneDrive) is
// played instead of the YouTube link. Its playable URL is short-lived, so it is
// asked for on demand:
//   GET /api/portal/public/:slug/offsite/:itemId/video-stream → { url }
// If the saved video can't be reached, the YouTube link (if any) is shown.
const OffsiteVideoEmbed = ({ item, slug }) => {
  const hasSiteVideo = Boolean(item.siteVideo && slug && item._id);
  const [url, setUrl] = React.useState(null);
  const [state, setState] = React.useState(hasSiteVideo ? 'loading' : 'none');

  React.useEffect(() => {
    if (!hasSiteVideo) { setState('none'); return undefined; }
    let cancelled = false;
    setState('loading');
    fetch(`${API_BASE}/api/portal/public/${slug}/offsite/${item._id}/video-stream`)
      .then(r => { if (!r.ok) throw new Error('resolve failed'); return r.json(); })
      .then(data => { if (!cancelled) { setUrl(data.url); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('failed'); });
    return () => { cancelled = true; };
  }, [hasSiteVideo, slug, item._id]);

  if (state === 'ready' && url) return <BrandVideoEmbed url={url} title={item.name} poster={item.imageUrl} />;
  if (state === 'loading') {
    return (
      <div style={{ borderRadius: 10, background: '#f7f7f7', border: '1px solid #ebebeb', marginTop: 12, aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a6a6a', fontSize: 12, fontFamily: "'Jost',sans-serif" }}>
        Loading video…
      </div>
    );
  }
  return item.youtubeUrl && getYouTubeId(item.youtubeUrl) ? <YouTubeEmbed url={item.youtubeUrl} title={item.name} /> : null;
};

// ── Product Image Carousel ────────────────────────────────────────────────────
// Shows additionalImages as swipeable dots-nav carousel below primary image.
// Only rendered when there are ≥1 additional images.
// CHANGES: arrows are hover-only (no white box background), counter badge removed.
const ProductCarousel = ({ images, primaryUrl, productName, onZoom, isMobile = false }) => {
  const all = [primaryUrl, ...images].filter(Boolean);
  const [idx, setIdx] = React.useState(0);
  const [hovered, setHovered] = React.useState(false);
  if (all.length <= 1) return null;

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + all.length) % all.length); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % all.length); };

  const arrowBtn = (side, handler, points) => (
    <button onClick={handler} style={{
      position: 'absolute', [side]: 6, top: '50%', transform: 'translateY(-50%)',
      background: 'rgba(0,0,0,0.32)', border: 'none',
      width: 32, height: 32, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', zIndex: 4,
      color: '#fff',
      opacity: hovered ? 1 : 0,
      transform: hovered ? 'translateY(-50%) scale(1.15)' : 'translateY(-50%) scale(1)',
      transition: 'opacity .18s, transform .18s',
      backdropFilter: 'blur(4px)',
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points={points} />
      </svg>
    </button>
  );

  return (
    <div
      style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Main image — fixed aspect-ratio box so a big/tall source image can't
          blow up the card (and therefore the grid) beyond the viewport.
          Full-resolution image is still shown when zoomed via onZoom. */}
      <div
        onClick={() => onZoom({ src: all[idx], alt: productName, all, startIdx: idx })}
        style={{ cursor: 'zoom-in', position: 'relative', width: '100%', aspectRatio: isMobile ? '1/1' : '4/3', overflow: 'hidden', background: '#f7f7f7' }}
      >
        <img
          src={all[idx]}
          alt={`${productName} — image ${idx + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity 0.25s ease' }}
        />
        {/* Gradient */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(0deg,rgba(10,20,34,0.7),transparent)', pointerEvents: 'none' }} />
      </div>

      {/* Prev / Next arrows — hover-only, rounded pill with blur */}
      {arrowBtn('left', prev, '15 18 9 12 15 6')}
      {arrowBtn('right', next, '9 18 15 12 9 6')}

      {/* Dot indicators */}
      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5, zIndex: 4 }}>
        {all.map((_, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            style={{
              width: i === idx ? 18 : 6, height: 6, borderRadius: 3, border: 'none',
              background: i === idx ? '#b8975a' : 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: 0,
              transition: 'all 0.25s ease',
            }}
          />
        ))}
      </div>

      {/* Thumbnail strip — shown whenever there are ≥2 images */}
      <div style={{ display: 'flex', gap: 4, padding: '6px 10px', background: '#fff', borderTop: '1px solid #ebebeb', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {all.map((src, i) => (
          <div
            key={i}
            onClick={(e) => { e.stopPropagation(); setIdx(i); }}
            style={{
              width: 38, height: 38, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
              border: `2px solid ${i === idx ? '#b8975a' : '#ebebeb'}`,
              cursor: 'pointer', transition: 'border-color 0.2s',
              outline: i === idx ? '1px solid rgba(184,151,90,0.3)' : 'none',
              outlineOffset: 1,
            }}
          >
            <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Product Viewer — full-screen popover opened from a product image ─────────
// Left: every image (and the product video, if any) with prev/next arrows and
// a thumbnail strip. Right: name, price, category and the complete
// description. Phones stack the two halves. Esc / ← / → and swipe work.
const ProductViewer = ({ item, startIdx = 0, price, slug, loved, onToggleWish, onClose }) => {
  const isMobile = useMobile();
  const images = [item.imageUrl, ...(item.additionalImages || [])].filter(Boolean);
  const media = [
    ...images.map(src => ({ kind: 'image', src })),
    ...(productHasVideo(item) ? [{ kind: 'video' }] : []),
  ];
  const [cur, setCur] = React.useState(Math.min(startIdx, Math.max(0, media.length - 1)));
  const touchX = React.useRef(null);
  const go = React.useCallback(d => setCur(i => (i + d + media.length) % media.length), [media.length]);

  useEffect(() => {
    const fn = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && media.length > 1) go(-1);
      if (e.key === 'ArrowRight' && media.length > 1) go(1);
    };
    window.addEventListener('keydown', fn);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', fn); document.body.style.overflow = prevOverflow; };
  }, [onClose, go, media.length]);

  const active = media[cur];
  const arrow = (dir) => (
    <button onClick={e => { e.stopPropagation(); go(dir); }} aria-label={dir < 0 ? 'Previous' : 'Next'}
      style={{
        position: 'absolute', [dir < 0 ? 'left' : 'right']: isMobile ? 8 : 16, top: '50%', transform: 'translateY(-50%)',
        width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius: '50%', border: '1px solid #ebebeb',
        background: 'rgba(255,255,255,0.92)', color: '#222222', cursor: 'pointer', zIndex: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)',
      }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points={dir < 0 ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
      </svg>
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label={item.name} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(5,8,16,0.92)', backdropFilter: 'blur(14px)', display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: isMobile ? 0 : 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 1400, background: '#fff',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'auto' : 'hidden',
          borderRadius: isMobile ? 0 : 6, boxShadow: '0 40px 80px rgba(0,0,0,0.4)',
        }}>
        {/* Close */}
        <button onClick={onClose} aria-label="Close"
          style={{ position: isMobile ? 'fixed' : 'absolute', top: 12, right: 12, zIndex: 10, width: 40, height: 40, borderRadius: '50%', border: 'none', background: isMobile ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.05)', color: isMobile ? '#fff' : '#222222', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {Ic.close}
        </button>

        {/* ── Left: media ── */}
        <div style={{ flex: isMobile ? 'none' : '1 1 62%', minWidth: 0, background: '#f7f7f7', display: 'flex', flexDirection: 'column' }}>
          <div
            onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (touchX.current == null || media.length < 2) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
              touchX.current = null;
            }}
            style={{ position: 'relative', flex: 1, minHeight: isMobile ? '52vh' : 0, height: isMobile ? '52vh' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 32 }}>
            {active?.kind === 'image' && (
              <img src={active.src} alt={`${item.name} — ${cur + 1}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', mixBlendMode: 'multiply' }} />
            )}
            {active?.kind === 'video' && (
              <div style={{ width: '100%', maxWidth: 960 }}>
                <ProductVideoEmbed item={item} slug={slug} />
              </div>
            )}
            {!active && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>No image</div>}
            {media.length > 1 && arrow(-1)}
            {media.length > 1 && arrow(1)}
            {media.length > 1 && (
              <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', color: '#484848', padding: '3px 12px', fontSize: 12, fontFamily: "'Jost',sans-serif", fontVariantNumeric: 'tabular-nums' }}>
                {cur + 1} / {media.length}
              </div>
            )}
          </div>
          {media.length > 1 && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 14px 14px', overflowX: 'auto', justifyContent: isMobile ? 'flex-start' : 'center', flexShrink: 0 }}>
              {media.map((m, i) => (
                <button key={i} onClick={() => setCur(i)} aria-label={m.kind === 'video' ? 'Product video' : `Image ${i + 1}`}
                  style={{ width: 60, height: 72, flexShrink: 0, padding: 0, borderRadius: 0, overflow: 'hidden', cursor: 'pointer', background: '#ebebeb', border: `1px solid ${i === cur ? '#222222' : 'transparent'}`, opacity: i === cur ? 1 : 0.65, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#222222' }}>
                  {m.kind === 'image'
                    ? <img src={m.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', mixBlendMode: 'multiply' }} />
                    : Ic.play}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: details ── */}
        <div className="desc-scroll" style={{ flex: isMobile ? 'none' : '1 1 38%', minWidth: 0, maxWidth: isMobile ? 'none' : 520, overflowY: isMobile ? 'visible' : 'auto', padding: isMobile ? '20px 18px 32px' : '48px 36px 36px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(item.category || item.subCategory) && (
            <div style={{ fontSize: 12, fontWeight: 500, color: '#7d6233', fontFamily: "'Jost',sans-serif" }}>
              {[item.category, item.subCategory].filter(Boolean).join(' · ')}
            </div>
          )}
          <h2 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontWeight: 400, fontSize: isMobile ? 26 : 32, lineHeight: 1.15, color: '#222222', paddingRight: isMobile ? 0 : 36 }}>{item.name}</h2>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 22, fontWeight: 400, color: '#222222', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>{toINR(price)} <span style={{ fontSize: 12, color: '#6a6a6a', letterSpacing: 0 }}>per piece</span></div>
          {onToggleWish && (
            <button onClick={() => onToggleWish(String(item._id))}
              aria-pressed={loved}
              style={{ alignSelf: isMobile ? 'stretch' : 'flex-start', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: 10, minHeight: 48, padding: '0 26px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, border: '1px solid #222222', background: loved ? '#222222' : 'transparent', color: loved ? '#fff' : '#222222' }}>
              <span style={{ display: 'flex', color: loved ? '#d9c49a' : '#222222' }}>{pxHeart(loved)}</span> {loved ? 'Shortlisted' : 'Add to shortlist'}
            </button>
          )}
          <div style={{ height: 1, background: '#ebebeb' }} />
          {item.description
            ? <p style={{ fontSize: 14, lineHeight: 1.8, color: '#6a6a6a', fontFamily: "'Jost',sans-serif", whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.description}</p>
            : <p style={{ fontSize: 13, color: '#929292', fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>No description for this product yet. Ask the team on the message board for details.</p>}
        </div>
      </div>
    </div>
  );
};

// ── Property Attachment Chip (client-facing, in offsite cards) ─────────────────
// Opens the document straight away (no OneDrive sign-in): the API streams it
// (GET /api/portal/public/:slug/offsite/:itemId/attachments/...). PDFs and
// images open in the browser, Office files in Microsoft's free web viewer on
// laptops, anything else downloads.
const propAttachmentUrl = (slug, itemId, index, name) =>
  `${API_BASE}/api/portal/public/${slug}/offsite/${itemId}/attachments/${index}/${encodeURIComponent(name || 'file')}`;

const PropAttachChip = ({ att, fileUrl }) => {
  const kind = fileKind(att);
  const handleClick = (e) => {
    e.preventDefault();
    log.debug('Opening property attachment', { name: att.name, kind });
    let target = fileUrl;
    if (['word', 'excel', 'powerpoint'].includes(kind) && !isTouchDevice()) {
      target = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
    } else if (kind === 'file') {
      target = `${fileUrl}?download=1`;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
  };
  return (
    <a href={fileUrl} target="_blank" rel="noreferrer" onClick={handleClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(184,151,90,0.07)', border: '1px solid rgba(184,151,90,0.2)', borderRadius: 8, textDecoration: 'none', cursor: 'pointer', transition: 'background .15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(184,151,90,0.13)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(184,151,90,0.07)'}>
      <span style={{ color: '#7d6233' }}>{Ic.file}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#222222', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
      {att.size > 0 && <span style={{ fontSize: 12, color: '#929292' }}>{fmtSz(att.size)}</span>}
      <span style={{ color: '#7d6233' }}>{Ic.ext}</span>
    </a>
  );
};

// ── Smart Description — AI-structured property details ───────────────────────
const SECTION_ICONS = {
  travel: '🚗', highlights: '✨', amenities: '🏊', food: '🍽️',
  rooms: '🛏️', policies: '📋', activities: '🎯', info: 'ℹ️',
};

const SmartDescription = ({ text, itemId }) => {
  const [state, setState] = React.useState('idle');
  const [sections, setSections] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const cacheKey = `sd_${itemId}`;

  React.useEffect(() => {
    if (!text || text.length < 80) return;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try { setSections(JSON.parse(cached)); setState('done'); } catch { }
      return;
    }
    parse();
  }, []);

  const parse = async () => {
    sdLog.info('Parsing property description via AI', { itemId, length: text?.length });
    setState('loading');
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          messages: [{
            role: 'user', content:
              `You are a hospitality content formatter. Parse this property description into structured sections. Return ONLY valid JSON, no markdown, no explanation.

Property description:
"""
${text}
"""

Return this exact JSON shape (only include sections that have relevant content, max 5 sections):
{"sections":[{"type":"travel","title":"Getting There","points":["..."]},{"type":"highlights","title":"Highlights","points":["..."]},{"type":"amenities","title":"Amenities","points":["..."]},{"type":"food","title":"Food & Drinks","points":["..."]},{"type":"rooms","title":"Rooms","points":["..."]},{"type":"activities","title":"Activities","points":["..."]},{"type":"policies","title":"Policies","points":["..."]},{"type":"info","title":"Good to Know","points":["..."]}]}

Rules: Each point max 12 words. Distance/travel info → "travel". Never duplicate across sections. 2-5 points per section.` }],
        }),
      });
      const data = await response.json();
      const raw = (data.content || []).map(b => b.text || '').join('');
      const parsed = JSON.parse(raw.replace(/```json|```/g, '').trim());
      if (parsed.sections?.length > 0) {
        setSections(parsed.sections);
        setState('done');
        sessionStorage.setItem(cacheKey, JSON.stringify(parsed.sections));
        sdLog.info('SmartDescription parsed and cached', { itemId, sections: parsed.sections.length });
      } else { setState('error'); sdLog.warn('SmartDescription: no sections returned', { itemId }); }
    } catch (err) { setState('error'); sdLog.error('SmartDescription parse failed', err.message); }
  };

  if (!text) return null;

  // Very short — plain text
  if (text.length < 80) {
    return <p style={{ fontSize: 12, color: '#6a6a6a', lineHeight: 1.65, fontFamily: "'Jost',sans-serif" }}>{text}</p>;
  }

  // Loading shimmer
  if (state === 'loading') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 6 }}>
      {[75, 55, 85, 60].map((w, i) => (
        <div key={i} style={{ height: 9, borderRadius: 5, background: 'rgba(255,255,255,0.07)', width: `${w}%`, animation: 'pulse 1.5s ease infinite', animationDelay: `${i * 0.15}s` }} />
      ))}
    </div>
  );

  // Fallback — plain text with expand/collapse
  if (state === 'error' || state === 'idle') {
    const lines = text.split('\n').filter(Boolean);
    return (
      <div>
        <p style={{ fontSize: 12, color: '#6a6a6a', lineHeight: 1.75, fontFamily: "'Jost',sans-serif", whiteSpace: 'pre-line' }}>
          {(expanded || lines.length <= 3) ? text : lines.slice(0, 3).join('\n') + '…'}
        </p>
        {lines.length > 3 && (
          <button onClick={() => setExpanded(e => !e)}
            style={{ marginTop: 5, fontSize: 12, color: '#7d6233', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Jost',sans-serif" }}>
            {expanded ? '▲ Show less' : '▼ Show more'}
          </button>
        )}
      </div>
    );
  }

  // Structured AI sections
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
      {sections.map((sec, si) => (
        <div key={si} style={{ borderRadius: 8, padding: '9px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#7d6233', marginBottom: 6, fontFamily: "'Jost',sans-serif", display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>{SECTION_ICONS[sec.type] || 'ℹ️'}</span>{sec.title}
          </div>
          {(sec.points || []).map((pt, pi) => (
            <div key={pi} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12, color: '#6a6a6a', lineHeight: 1.55, marginBottom: 3, fontFamily: "'Jost',sans-serif" }}>
              <span style={{ color: '#7d6233', flexShrink: 0, fontSize: 12, marginTop: 2 }}>›</span>
              <span>{pt}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const Toast = ({ toasts }) => (
  <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 9998, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        background: '#f7f7f7',
        border: `1px solid rgba(184,151,90,0.3)`,
        borderRadius: 12, padding: '14px 18px',
        boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
        animation: 'toastSlide .35s cubic-bezier(.34,1.56,.64,1)',
        minWidth: 260, maxWidth: 340, pointerEvents: 'all',
        backdropFilter: 'blur(20px)',
      }}>
        <div style={{ width: 32, height: 32, background: 'rgba(184,151,90,0.12)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15 }}>{t.icon || '💬'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#7d6233', marginBottom: 3, fontFamily: "'Jost',sans-serif" }}>{t.title}</div>
          <div style={{ fontSize: 13, color: '#222222', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Jost',sans-serif" }}>{t.body}</div>
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// ── Skeleton bar — matches the existing pulse animation aesthetic ─────────────
// Used to buffer loading states instead of raw spinners.
const SkeletonBar = ({ w = '100%', h = 12, style: extra = {} }) => (
  <div style={{
    height: h, width: w, borderRadius: 6,
    background: 'rgba(184,151,90,0.08)',
    animation: 'pulse 1.5s ease infinite',
    ...extra,
  }} />
);

// ── Breakpoint hooks ──────────────────────────────────────────────────────────
// Phone: ≤ 640px. Tablet (iPad portrait and landscape): ≤ 1024px.
const useMaxWidth = (px) => {
  const [match, setMatch] = React.useState(() => window.innerWidth <= px);
  React.useEffect(() => {
    const fn = () => setMatch(window.innerWidth <= px);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [px]);
  return match;
};
const useMobile = () => useMaxWidth(640);
const useTablet = () => useMaxWidth(1024);

// ── Masonry ───────────────────────────────────────────────────────────────────
// Cards stack straight under the card above them in their column, so a short
// card (no description, no video) never leaves a gap under it the way CSS grid
// rows do. Each card goes to the currently shortest column, which keeps the
// reading order left-to-right. Heights are estimated (in column widths) from
// what the card shows — exact enough to balance columns without measuring.
const Masonry = ({ items, columns, gap, estimate, renderItem }) => {
  if (columns <= 1) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap }}>{items.map((it, i) => renderItem(it, i))}</div>;
  }
  const cols = Array.from({ length: columns }, () => ({ h: 0, list: [] }));
  items.forEach((it, i) => {
    const target = cols.reduce((best, c) => (c.h < best.h - 0.01 ? c : best), cols[0]);
    target.list.push([it, i]);
    target.h += estimate(it) + 0.05;
  });
  return (
    <div style={{ display: 'flex', gap, alignItems: 'flex-start' }}>
      {cols.map((c, ci) => (
        <div key={ci} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap }}>
          {c.list.map(([it, i]) => renderItem(it, i))}
        </div>
      ))}
    </div>
  );
};

// Estimated card height for a product card, in column widths.
const productHasVideo = (item) => Boolean(item.videoUrl || item.videoSource === 'upload');
// Height of a pin's video block in column widths (label + player).
const pinVideoHeight = (item) => (productHasVideo(item) ? 0.66 : 0);
const estimateProductCard = (item) =>
  0.75                                                     // 4:3 image
  + ((item.additionalImages || []).length > 0 ? 0.17 : 0)  // thumbnail strip
  + 0.18                                                   // name + price
  + (item.description ? 0.2 : 0)                           // 3-line description
  + (productHasVideo(item) ? 0.68 : 0);                    // video embed

// ── Empty State helper ────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, sub, children }) => (
  <div style={{ textAlign: 'center', padding: '80px 0' }}>
    <div style={{ fontSize: 48, marginBottom: 18 }}>{icon}</div>
    <h3 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 22, fontWeight: 400, color: '#222222', marginBottom: 10, lineHeight: 1.2 }}>{title}</h3>
    <p style={{ fontSize: 13, color: '#6a6a6a', fontFamily: "'Jost',sans-serif", fontWeight: 400 }}>{sub}</p>
    {children}
  </div>
);

const ClientPortalView = () => {
  const { slug } = useParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The order was deleted (or the link never existed): show the archived page.
  const [archived, setArchived] = useState(false);
  // ?tab=chat (from a message notification) opens the Message Board.
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'chat' ? 'chat' : 'catalogue'));
  const [msg, setMsg] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [clientName, setClientName] = useState('');
  const [nameSet, setNameSet] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [wishlisted, setWishlisted] = useState(new Set());
  const [hamperOpen, setHamperOpen] = useState(false);
  const [hamperInitialRange, setHamperInitialRange] = useState({});
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoaded, setShipmentsLoaded] = useState(false);
  const [shipFilter, setShipFilter] = useState('all');
  const [shipStateFilter, setShipStateFilter] = useState('');
  const [shipCityFilter, setShipCityFilter] = useState('');
  const [shipNameSearch, setShipNameSearch] = useState('');
  const [shipPage, setShipPage] = useState(1);
  const [shipPageSize, setShipPageSize] = useState(25);
  const [shipViewing, setShipViewing] = useState(null);   // shipment whose photo/details are open
  const [toasts, setToasts] = useState([]);
  // Event portals: the option open in the detail sheet, and picks not yet saved.
  const [eventSheet, setEventSheet] = useState(null);   // { item, idx }
  const pendingPicks = useRef(null);
  const guestTimer = useRef(null);
  const chatEnd = useRef(null);
  const fileRef = useRef(null);
  const prevMsgCount = useRef(0);
  const pollTimer = useRef(null);
  const isMobile = useMobile();
  const isTablet = useTablet();

  const showToast = (title, body, icon) => {
    const id = Date.now();
    setToasts(p => [...p, { id, title, body, icon }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4500);
  };

  const toggleWish = id => {
    setWishlisted(prev => {
      const s = new Set(prev);
      const wasWished = s.has(id);
      wasWished ? s.delete(id) : s.add(id);
      // Persist to DB so shortlist survives page refresh
      const ids = Array.from(s);
      log.debug('Updating shortlist', { itemId: id, total: ids.length });
      fetch(`${API_BASE}/api/portal/public/${slug}/shortlist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      }).catch(err => log.warn('Shortlist sync failed', err.message));

      // Removing an item from the shortlist also zeroes out its Cost
      // Calculator quantity — a de-shortlisted item shouldn't silently keep
      // contributing to the total.
      if (wasWished) {
        patchCalculatorState({ [id]: { qty: 0 } });
        const nextCalc = { ...(portal?.calculatorState || {}), [id]: { ...(portal?.calculatorState?.[id] || {}), qty: 0 } };
        fetch(`${API_BASE}/api/portal/public/${slug}/calculator`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ calculatorState: nextCalc }),
        }).catch(err => log.warn('Calculator qty reset failed', err.message));
      }
      return s;
    });
  };

  // Optimistically merges a calculator-state patch into local `portal` state
  // the instant it happens. Without this, CostCalculator (which unmounts when
  // you leave the Cost Calculator tab) re-seeds its qty/fields purely from
  // `portal.calculatorState` on remount — and that prop is only refreshed by
  // the 12s silent poll, so a quick tab-away-and-back can show a stale value
  // (e.g. 0) until the next poll catches up. Updating it here in-memory keeps
  // `portal` current regardless of network/poll timing.
  const patchCalculatorState = (patch) => {
    setPortal(prev => {
      if (!prev) return prev;
      const nextCalc = { ...(prev.calculatorState || {}) };
      Object.entries(patch).forEach(([id, fields]) => {
        nextCalc[id] = { ...(nextCalc[id] || {}), ...fields };
      });
      return { ...prev, calculatorState: nextCalc };
    });
  };

  useEffect(() => {
    initNotifications(); // register SW, no permission prompt yet
    load();
    log.info('Portal view mounted', { slug });
    fetch(`${API_BASE}/api/portal/public/${slug}/view`, { method: 'POST' }).catch(err =>
      log.warn('View count increment failed', err.message)
    );
    pollTimer.current = setInterval(() => load(true), 12000);
    return () => {
      clearInterval(pollTimer.current);
      log.debug('Portal view unmounted', { slug });
    };
  }, [slug]);

  useEffect(() => { if (tab === 'chat') chatEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [portal?.messages, tab]);

  // Fetch shipments when shipments tab is first opened
  useEffect(() => {
    if (tab !== 'shipments' || shipmentsLoaded || !portal?.orderId) return;
    log.info('Fetching shipments', { slug });
    fetch(`${API_BASE}/api/portal/public/${slug}/shipments`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => { const arr = Array.isArray(data) ? data : []; log.info('Shipments loaded', { count: arr.length }); setShipments(arr); setShipmentsLoaded(true); })
      .catch(err => { log.error('Shipments fetch failed', err.message); setShipmentsLoaded(true); });
  }, [tab, shipmentsLoaded, portal, slug]);

  // City filter is scoped to the selected state — clear it whenever the state changes
  // so we never end up with a city selected that doesn't belong to the chosen state.
  useEffect(() => { setShipCityFilter(''); }, [shipStateFilter]);

  // Any change to a shipment filter should snap pagination back to page 1,
  // otherwise the user can land on an empty page 3 of a newly-narrowed list.
  useEffect(() => { setShipPage(1); }, [shipFilter, shipStateFilter, shipCityFilter, shipNameSearch, shipPageSize]);

  // Esc closes the shipment photo viewer.
  useEffect(() => {
    if (!shipViewing) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShipViewing(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shipViewing]);

  const load = async (silent = false) => {
    if (!silent) log.debug('Loading portal', { slug });
    try {
      const res = await fetch(`${API_BASE}/api/portal/public/${slug}`);
      if (res.status === 404 || res.status === 410) {
        // Also catches an order deleted while the client has the page open.
        if (!silent) log.info('Portal archived', { slug, status: res.status });
        setArchived(true);
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || `HTTP ${res.status}`);
      const data = await res.json();
      if (silent && prevMsgCount.current > 0) {
        const teamMsgs = (data.messages || []).filter(m => m.sender === 'team');
        if (teamMsgs.length > prevMsgCount.current) {
          const newest = teamMsgs[teamMsgs.length - 1];
          const preview = newest.text ? newest.text.slice(0, 55) + (newest.text.length > 55 ? '…' : '') : newest.attachments?.length ? `📎 ${newest.attachments[0].name}` : 'New message';
          log.info('New team message detected', { preview: preview.slice(0, 40) });
          showToast('Marqland Team', preview, '💬');
          pushNotif('Marqland Studios — New Message', preview, `/p/${slug}?tab=chat`, `portal-team-${slug}`);
        }
        prevMsgCount.current = (data.messages || []).filter(m => m.sender === 'team').length;
      } else {
        prevMsgCount.current = (data.messages || []).filter(m => m.sender === 'team').length;
        if (!silent) log.info('Portal loaded', { slug, type: data.type });
      }
      setArchived(false);
      if (pendingPicks.current) Object.assign(data, pendingPicks.current);
      setPortal(data);
      // Restore shortlist from DB on first load (not on silent polls).
      // Always sync — even an empty array should clear a stale local state.
      if (!silent) {
        setWishlisted(new Set(data.shortlistedIds || []));
      } else {
        // The team can remove products/combos from the order while the client
        // has the page open — drop those from the shortlist so its count stays true.
        const live = new Set([...(data.productItems || []), ...(data.offsiteItems || []), ...(data.comboItems || [])].map(i => String(i._id)));
        setWishlisted(prev => {
          const next = new Set([...prev].filter(id => live.has(String(id))));
          return next.size === prev.size ? prev : next;
        });
      }
      const name = data.orderPlacedBy || data.clientName || '';
      setClientName(name); if (name) setNameSet(true);
    } catch (e) { if (!silent) { log.error('Portal load failed', e.message); setError(e.message || 'Link invalid or expired.'); } }
    finally { if (!silent) setLoading(false); }
  };

  const send = async () => {
    if ((!msg.trim() && files.length === 0) || sending) return;
    // Ask for permission on first send — highest grant rate on user gesture
    const perm = await requestNotifPermission();
    if (perm === 'granted') subscribeToPortalPush(slug); // register for the team's replies
    const sender = sanitizeText(clientName?.trim() || portal?.orderPlacedBy || portal?.clientName || 'Client', 60);
    const cleanMsg = sanitizeText(msg.trim(), 2000);
    log.info('Sending message', { sender, hasText: !!cleanMsg, fileCount: files.length });
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('text', cleanMsg); fd.append('senderName', sender);
      files.forEach(f => fd.append('files', f));
      const r = await fetch(`${API_BASE}/api/portal/public/${slug}/message`, { method: 'POST', body: fd });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message || 'Send failed'); }
      log.info('Message sent successfully');
      setMsg(''); setFiles([]);
      showToast('Delivered', 'Your message has been sent to the team.', '✓');
      await load();
    } catch (e) { log.error('Message send failed', e.message); alert('Could not send: ' + e.message); }
    finally { setSending(false); }
  };

  // ── Event portals: picks and headcount ──────────────────────────────────────
  // Applied here at once, then saved; the server keeps them valid and posts
  // each new pick on the message board.
  const saveEventPicks = (patch) => {
    const next = { ...(pendingPicks.current || {}), ...patch };
    pendingPicks.current = next;
    setPortal(prev => (prev ? { ...prev, ...patch } : prev));
    const body = { picks: next.eventPicks ?? portal?.eventPicks ?? [], ...(next.eventGuests !== undefined ? { guests: next.eventGuests } : {}) };
    return fetch(`${API_BASE}/api/portal/public/${slug}/event-picks`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
      .then(r => (r.ok ? r.json() : r.json().catch(() => ({})).then(e => { throw new Error(e.message || `HTTP ${r.status}`); })))
      .then(data => {
        if (pendingPicks.current === next) pendingPicks.current = null;
        setPortal(prev => (prev ? {
          ...prev,
          eventPicks: pendingPicks.current?.eventPicks ?? data.eventPicks,
          eventGuests: pendingPicks.current?.eventGuests ?? data.eventGuests,
          messages: [...(prev.messages || []), ...(data.messages || []).filter(m => !(prev.messages || []).some(x => x._id === m._id))],
        } : prev));
      })
      .catch(err => {
        if (pendingPicks.current === next) pendingPicks.current = null;
        log.warn('Event picks save failed', err.message);
        showToast('Not saved', 'Your pick could not be saved. Please try again.', '!');
        load(true);
      });
  };
  const toggleEventPick = (item) => {
    const current = (pendingPicks.current?.eventPicks ?? portal?.eventPicks ?? []).map(String);
    saveEventPicks({ eventPicks: togglePickIn(portal?.eventItems || [], current, item) });
  };
  const setEventGuests = (n) => {
    setPortal(prev => (prev ? { ...prev, eventGuests: n } : prev));
    pendingPicks.current = { ...(pendingPicks.current || {}), eventGuests: n };
    clearTimeout(guestTimer.current);
    guestTimer.current = setTimeout(() => saveEventPicks({ eventGuests: n }), 700);
  };
  const jumpToEventSection = (key) => {
    setTab('catalogue');
    setTimeout(() => {
      const el = document.querySelector(`[data-section="${key}"]`);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 190, behavior: 'smooth' });
    }, 80);
  };

  // ── Global CSS injected once ──────────────────────────────────────────────
  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@300;400;500;600&family=Manrope:wght@400;500;600;700;800&display=swap');
    @keyframes obsidianFadeIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes toastSlide{from{opacity:0;transform:translateX(20px) scale(0.95)}to{opacity:1;transform:none}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
    *{box-sizing:border-box;margin:0}
    body{background:#ffffff}
    ::-webkit-scrollbar{width:3px}
    ::-webkit-scrollbar-track{background:transparent}
    ::-webkit-scrollbar-thumb{background:rgba(184,151,90,0.35);border-radius:10px}
    .desc-scroll::-webkit-scrollbar{width:4px}
    .desc-scroll::-webkit-scrollbar-track{background:rgba(0,0,0,0.04)}
    .desc-scroll::-webkit-scrollbar-thumb{background:rgba(184,151,90,0.5);border-radius:10px}
    .desc-scroll{scrollbar-width:thin;scrollbar-color:rgba(184,151,90,0.5) rgba(0,0,0,0.04)}
    textarea{resize:none}
    input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
    input[type=number]{-moz-appearance:textfield}
    .img-hover:hover{transform:scale(1.04)!important}
    .ghost-btn:hover{background:rgba(0,0,0,0.04)!important}
    .ms-pill{display:inline-block;padding:6px 14px;border-radius:40px;background:#f7f7f7;font-size:13px;font-weight:500;color:#222222;font-family:'Jost',sans-serif}
    .ms-lbl{font-size:12px;font-weight:600;color:#6a6a6a;font-family:'Jost',sans-serif}
    .cp-card{background:#fff;border:1px solid #ebebeb;overflow:hidden;transition:transform 0.5s cubic-bezier(0.16,1,0.3,1),box-shadow 0.5s,border-color 0.3s}
    .cp-card:hover{transform:translateY(-3px);box-shadow:0 16px 48px rgba(0,0,0,0.08);border-color:rgba(184,151,90,0.25)}
    .cp-btn-gold{display:inline-flex;align-items:center;gap:8px;background:#222222;color:#fff;padding:13px 24px;border:none;border-radius:8px;cursor:pointer;font-family:'Jost',sans-serif;font-size:15px;font-weight:600;transition:background 0.2s;text-decoration:none}
    .cp-btn-gold:hover{background:#000}
    .cp-pill{display:inline-block;padding:5px 12px;border-radius:40px;background:#f7f7f7;font-size:13px;font-weight:500;color:#222222;font-family:'Jost',sans-serif}
    .cp-divider{width:36px;height:1px;background:#b8975a;display:inline-block;flex-shrink:0}
    .cp-wish-btn{transition:transform 0.2s,background 0.2s}
    .cp-wish-btn:hover{transform:scale(1.1)}
    .ms-grain{position:relative}
    .ms-grain::after{content:'';position:absolute;inset:0;pointer-events:none;z-index:2;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");background-size:200px}
    .ms-card{overflow:hidden;cursor:pointer;position:relative;transition:transform 0.6s cubic-bezier(0.16,1,0.3,1);border:1px solid #ebebeb}
    .ms-card:hover{transform:translateY(-3px);box-shadow:0 20px 60px rgba(0,0,0,0.09)}
    .ms-tab-active{border-bottom:2px solid #222222 !important;color:#222222 !important}
    .ms-tab-idle{border-bottom:2px solid transparent !important;color:#6a6a6a}
    .ms-tab-idle:hover{color:#222222}
    .cp-root input:not([type=checkbox]):not([type=radio]),.cp-root select,.cp-root textarea{border-radius:8px}
    .cp-root button[style*="border: 1px solid"]{border-radius:8px}
    .cp-root article,.cp-root aside>div,.cp-root div[style*="border: 1px solid"][style*="background: rgb(255, 255, 255)"]{border-radius:12px}
    .cp-root article{overflow:hidden}
    .cp-root input[type=number]:not(.air-in *):not(.air-seg *),.cp-root input[type=text]:not(.air-in *):not(.air-seg *):not([role=listbox] *):not(.air-money *){border:1px solid #b0b0b0 !important;border-radius:8px !important;min-height:40px;font-size:15px !important;color:#222222;background:#fff}
    .cp-root input[type=number]:not(.air-in *):focus,.cp-root input[type=text]:not(.air-in *):not(.air-seg *):not(.air-money *):focus{border-color:#222222 !important;box-shadow:0 0 0 1px #222222;outline:none}
    .cp-root input[type=number]:not(.air-in *){text-align:center}
    .cp-root div[style*="border: 1px solid"][style*="overflow: hidden"]{border-radius:12px}
    .cp-root button[style*="width: 32px"][style*="height: 32px"],.cp-root button[style*="width: 28px"][style*="height: 28px"],.cp-root button[style*="width: 30px"][style*="height: 30px"]{border-radius:50%}
    .cp-root [role=tablist]{background:#f7f7f7;border-radius:40px;padding:4px;border:none !important}
    .air-seg:not(:disabled):hover{background:#ebebeb !important}
    .air-opt:hover{background:#f7f7f7 !important}
    .air-in:focus-within{background:#fff;box-shadow:0 6px 20px rgba(0,0,0,0.12)}
    .air-in:hover:not(:focus-within){background:#ebebeb}
  `;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#222222', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Jost',sans-serif" }}>
      <style>{globalStyles}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#b8975a', borderRadius: '50%', animation: 'spin .9s linear infinite', margin: '0 auto 20px' }} />
        <span className="cp-lbl" style={{ color: '#7d6233', opacity: 0.6 }}>LOADING</span>
      </div>
    </div>
  );

  // ── Archived / error ──────────────────────────────────────────────────────
  // Deleted orders land here, so the copy is a polite goodbye with a way to
  // reach the studio, not a dead end.
  if (archived || error) return (
    <div className="air" style={{ minHeight: '100vh', background: AIR_T.studio, display: 'flex', flexDirection: 'column' }}>
      <style>{globalStyles}{AIR_CSS}</style>
      <header style={{ background: '#fff', borderBottom: `1px solid ${AIR_T.line}`, padding: '12px 20px' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.svg" alt="Marqland Studios logo" width={40} height={40} style={{ borderRadius: '50%', boxShadow: `0 0 0 1px ${AIR_T.gold}` }} />
          <div style={{ fontFamily: AIR_T.serif, color: AIR_T.ink, fontWeight: 500, fontSize: 26, lineHeight: 1.1 }}>Marqland <em style={{ color: AIR_T.goldText }}>Studios.</em></div>
        </div>
      </header>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px 48px' }}>
        <section style={{ width: '100%', maxWidth: 560, background: '#fff', border: `1px solid ${AIR_T.line2}`, borderRadius: AIR_T.rCard, boxShadow: AIR_T.shadowCard, textAlign: 'center', padding: '52px 28px 48px' }}>
          <div aria-hidden="true" style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(184,151,90,0.10)', color: AIR_T.goldText, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="5" rx="1" /><path d="M4 8v11a2 2 0 002 2h12a2 2 0 002-2V8" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
          </div>
          {archived ? (<>
            <h1 style={{ fontFamily: AIR_T.serif, fontWeight: 400, fontSize: 'clamp(30px,6vw,38px)', color: AIR_T.ink, margin: '0 0 12px', lineHeight: 1.1 }}>These options have been archived</h1>
            <p style={{ color: AIR_T.muted, fontSize: 15, lineHeight: 1.7, margin: '0 auto 6px', maxWidth: 420 }}>The selection you are looking for is no longer active. Thank you for considering Marqland Studios.</p>
            <p style={{ color: AIR_T.muted, fontSize: 15, lineHeight: 1.7, margin: '0 auto 28px', maxWidth: 420 }}>We would be glad to help with this or anything new. You can still reach us by email.</p>
          </>) : (<>
            <h1 style={{ fontFamily: AIR_T.serif, fontWeight: 400, fontSize: 'clamp(30px,6vw,38px)', color: AIR_T.ink, margin: '0 0 12px', lineHeight: 1.1 }}>We couldn't open this page</h1>
            <p style={{ color: AIR_T.muted, fontSize: 15, lineHeight: 1.7, margin: '0 auto 28px', maxWidth: 420 }}>Something went wrong while loading your options. Please try again in a moment, or reach us by email.</p>
          </>)}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Enquiry about my Marqland link (${slug})`)}`}
              style={{ ...AIR.btn('primary'), textDecoration: 'none' }}>
              {Ic.mail} Email Marqland Studios
            </a>
            {!archived && <button type="button" onClick={() => window.location.reload()} style={AIR.btn('ghost')}>Try again</button>}
          </div>
          <p style={{ color: AIR_T.faint, fontSize: 13, margin: '18px 0 0' }}>{CONTACT_EMAIL}</p>
        </section>
      </main>
    </div>
  );

  // ── Completed ─────────────────────────────────────────────────────────────
  if (portal.status === 'completed') return (
    <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Jost',sans-serif", padding: 24 }}>
      <style>{globalStyles}</style>
      <div style={{ textAlign: 'center', maxWidth: 520 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(184,151,90,0.08)', border: '1px solid rgba(184,151,90,0.2)', borderRadius: 8, padding: '7px 16px', marginBottom: 40 }}>
          <div style={{ width: 20, height: 20, background: GOLD_GRAD, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#222222', fontWeight: 900, fontSize: 12 }}>M</div>
          <span style={{ color: '#7d6233', fontWeight: 700, fontSize: 12, fontFamily: "'Jost',sans-serif" }}>Marqland Studios</span>
        </div>
        <div style={{ fontSize: 64, marginBottom: 24 }}>🎉</div>
        <h1 className="cp-sf" style={{ fontSize: 'clamp(28px,5vw,52px)', fontWeight: 400, color: '#222222', marginBottom: 16, lineHeight: 1.05 }}>Thank you, <em style={{ color: '#7d6233' }}>{portal.clientName}.</em></h1>
        <p style={{ color: '#6a6a6a', fontSize: 15, lineHeight: 1.8, marginBottom: 40 }}>It was our pleasure working with you. We hope the experience exceeded expectations.</p>
        {portal.reviewLink && <a href={portal.reviewLink} target="_blank" rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: GOLD_GRAD, color: '#222222', padding: '14px 30px', borderRadius: 10, textDecoration: 'none', fontWeight: 700, fontSize: 14, fontFamily: "'Jost',sans-serif" }}>
          {Ic.star} Share your experience
        </a>}
        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, marginTop: 44, fontFamily: "'Jost',sans-serif" }}>REF · {portal.orderRef}</p>
      </div>
    </div>
  );

  const isEvent = portal.type === 'event';
  const items = (isEvent ? portal.eventItems : portal.type === 'product' ? portal.productItems : portal.offsiteItems) || [];
  const eventPicks = (portal.eventPicks || []).map(String);
  const eventGuests = portal.eventGuests || portal.eventInfo?.guests || 0;
  const showBudget = !isEvent || portal.eventInfo?.showBudget !== false;
  const newMsgs = portal.messages?.filter(m => m.sender === 'team').length || 0;
  // Only shortlisted items still on the order count towards the badge.
  const liveIds = new Set([...(items || []), ...(portal.comboItems || [])].map(i => String(i._id)));
  const shortlistCount = [...wishlisted].filter(id => liveIds.has(String(id))).length;

  return (
    <div className="cp-root air" style={{ minHeight: '100vh', background: '#ffffff', fontFamily: "'Jost',sans-serif", color: '#222222' }}>
      <style>{AIR_CSS}</style>
      <style>{globalStyles}</style>

      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} all={lightbox.all} startIdx={lightbox.startIdx} onClose={() => setLightbox(null)} />}
      <Toast toasts={toasts} />
      {isEvent && eventSheet && (
        <EventOptionSheet item={(portal.eventItems || []).find(i => i._id === eventSheet.item._id) || eventSheet.item} idx={eventSheet.idx}
          picked={eventPicks.includes(String(eventSheet.item._id))} onTogglePick={toggleEventPick} slug={slug} isMobile={isMobile}
          onClose={() => setEventSheet(null)}
          onAsk={(it) => { setEventSheet(null); setMsg(`About "${it.title}": `); setTab('chat'); window.scrollTo({ top: 0 }); }} />
      )}
      {portal.type === 'product' && (
        <HamperBuilder
          open={hamperOpen}
          onClose={() => setHamperOpen(false)}
          items={portal.productItems || []}
          existingClientCombos={(portal.comboItems || []).filter(c => c.createdBy === 'client')}
          initialCatRange={hamperInitialRange}
          slug={slug}
          isMobile={isMobile}
          onSaved={(msg) => { load(true); showToast(msg?.title || 'Hampers saved', msg?.body || 'Your custom combo(s) were added to the Combo tab', msg?.icon || '🎁'); }}
        />
      )}

      {/* ── Nav — HomePage navy bar ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: '#ffffff',
        borderBottom: '1px solid #ebebeb',
        padding: isMobile ? '0 20px' : isTablet ? '0 24px' : '0 48px',
        height: 68, display: 'flex', alignItems: 'center',
      }}>
        <div style={{ width: '100%', maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="cp-sf" style={{ color: '#222222', fontSize: 24, fontWeight: 500, letterSpacing: '0.01em' }}>
            Marqland Studios
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 20 }}>
            <button onClick={() => navigator.clipboard.writeText(window.location.href)}
              className="ghost-btn"
              style={{ background: '#fff', border: '1px solid #dddddd', borderRadius: 40, width: 40, height: 40, justifyContent: 'center', cursor: 'pointer', color: '#222222', display: 'flex', alignItems: 'center', transition: 'background .15s, box-shadow .15s', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
              title="Copy link">{Ic.share}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Tab Bar — sticky below nav ── */}
      <div style={{ position: 'sticky', top: 68, zIndex: 40, background: '#fff', borderBottom: '1px solid #ebebeb', padding: isMobile ? '0 4px' : isTablet ? '0 24px' : '0 48px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', gap: 0, overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {[
            { k: 'catalogue', l: isEvent ? 'Event Plan' : 'Catalogue Options', b: items.length + (portal.comboItems||[]).length },
            ...(portal.type === 'product' ? [{ k: 'selected', l: 'Selected Items', b: shortlistCount || null }] : []),
            ...(showBudget ? [{ k: 'calculator', l: isEvent ? 'Budget' : 'Cost Calculator', b: null }] : []),
            ...(portal.type === 'product' ? [{ k: 'shipments', l: 'Shipment Tracking', b: null }] : []),
            { k: 'chat', l: 'Message Board', b: newMsgs || null },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={tab === t.k ? 'ms-tab-active' : 'ms-tab-idle'}
              style={{
                padding: isMobile ? '14px 12px' : isTablet ? '16px 16px' : '18px 24px',
                background: 'none', border: 'none',
                fontSize: isMobile ? 13 : 14,
                fontWeight: tab === t.k ? 600 : 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8,
                flex: 'none',
                justifyContent: 'flex-start',
                transition: 'color .2s',
                fontFamily: "'Jost',sans-serif",
                whiteSpace: 'nowrap',
              }}>
              {t.l}
              {t.b != null && <span style={{
                background: tab === t.k ? '#222222' : '#f7f7f7',
                color: tab === t.k ? '#fff' : '#6a6a6a',
                fontSize: 12, fontWeight: 600, padding: '1px 8px', borderRadius: 20,
                fontFamily: "'Jost',sans-serif",
              }}>{t.b}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Hero — white, Airbnb-style ── */}
      {(tab === 'catalogue') && !isEvent && (
        <div style={{ background: '#ffffff', padding: isMobile ? '28px 20px 4px' : isTablet ? '40px 24px 8px' : '48px 48px 8px', position: 'relative' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
            <span style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 40, background: '#f7f7f7', fontFamily: "'Jost',sans-serif", fontSize: 13, fontWeight: 500, color: '#222222' }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: '#b8975a' }} />
              {portal.type === 'product' ? 'Product Gifting' : 'Exclusive Offsite'} · {portal.orderRef}
            </span>
            <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: isMobile ? 'clamp(32px,8vw,48px)' : 'clamp(40px,6vw,72px)', fontWeight: 500, color: '#222222', lineHeight: 1.05, marginBottom: 6 }}>
              {portal.title
                ? <>{portal.title} </>
                : <>Curated for{' '}<em style={{ color: '#7d6233' }}>{portal.clientName}.</em></>
              }
            </h1>
            {portal.type === 'product' && (() => {
              const cats = new Set((portal.productItems || []).map(i => i.category).filter(Boolean)).size;
              const n = (portal.productItems || []).length + (portal.comboItems || []).length;
              return (
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: isMobile ? 14 : 16, color: '#6a6a6a', margin: '12px 0 0', display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
                  {portal.title && portal.clientName && <span>Curated for <span style={{ color: '#222222', fontWeight: 600 }}>{portal.clientName}</span> ·</span>}
                  <span>{n} piece{n !== 1 ? 's' : ''}</span>
                  {cats > 1 && <span>· {cats} categories</span>}
                  {shortlistCount > 0 && <span>· {shortlistCount} shortlisted</span>}
                </p>
              );
            })()}
            {portal.teamNote && (
              <p style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontStyle: 'italic', color: '#6a6a6a', fontSize: isMobile ? 16 : 19, lineHeight: 1.75, maxWidth: 520, marginTop: 14 }}>
                {portal.teamNote}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: isMobile ? '16px 16px 80px' : isTablet ? '24px 24px 80px' : '28px 48px 80px' }}>

        {/* CATALOGUE — products + combos as a trailing category section */}
        {tab === 'catalogue' && isEvent && (
          <EventPlan portal={portal} picks={eventPicks} onTogglePick={toggleEventPick} isMobile={isMobile} isTablet={isTablet}
            onOpen={(item, idx) => setEventSheet({ item, idx })}
            onOpenBudget={showBudget ? () => { setTab('calculator'); window.scrollTo({ top: 0, behavior: 'smooth' }); } : null} />
        )}
        {tab === 'catalogue' && !isEvent && (
          portal.type === 'product'
            ? <ProductBento
                items={items}
                onZoom={setLightbox}
                wishlisted={wishlisted}
                onToggleWish={toggleWish}
                portal={portal}
                combos={portal.comboItems || []}
                onBuildHamper={(catFiltersSnapshot) => { setHamperInitialRange(catFiltersSnapshot || {}); setHamperOpen(true); }}
                slug={slug}
              />
            : items.length === 0
              ? <EmptyState icon="📋" title="Options being curated" sub="The Marqland team will update this shortly." />
              : <OffsiteCards items={items} onZoom={setLightbox} portal={portal}
                  onOpenCalculator={() => { setTab('calculator'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  onOpenChat={() => { setTab('chat'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
        )}

        {/* BUDGET (event portals) */}
        {tab === 'calculator' && isEvent && showBudget && (
          <EventBudget portal={portal} picks={eventPicks} guests={eventGuests} onGuests={setEventGuests} onTogglePick={toggleEventPick}
            onChange={jumpToEventSection} isMobile={isMobile}
            onSend={(summary) => { setMsg(picksMessage(summary).slice(0, 2000)); setTab('chat'); window.scrollTo({ top: 0 }); }} />
        )}

        {/* COST CALCULATOR */}
        {tab === 'calculator' && !isEvent && (
          <div>
            <TabHeader isMobile={isMobile} eyebrow="Cost calculator"
              title={portal.type === 'product' ? 'Estimate your order' : 'Compare what each option costs'}
              sub={portal.type === 'product'
                ? 'Set a quantity for each shortlisted piece to see an indicative order value. The final invoice adds GST, branding and shipping.'
                : 'Enter your guest count and room split to compare property costs side by side. Toggle add-ons to fine-tune your estimate.'} />
            <CostCalculator portal={portal} wishlisted={wishlisted} combos={portal.comboItems || []} onCalcPatch={patchCalculatorState}
              onOpenCatalogue={() => setTab('catalogue')} onOpenChat={() => setTab('chat')} />
          </div>
        )}

        {/* SELECTED ITEMS */}
        {tab === 'selected' && (() => {
          const allItems = portal.type === 'product' ? portal.productItems : portal.offsiteItems;
          const allCombos = portal.comboItems || [];
          const selProducts = allItems.filter(i => wishlisted.has(String(i._id)));
          const selCombos = allCombos.filter(c => wishlisted.has(String(c._id)));
          const totalSel = selProducts.length + selCombos.length;
          const priceOf = i => Number(portal.calculatorState?.[i._id]?.priceOverride ?? i.price ?? 0);
          const prices = [...selProducts.map(priceOf), ...selCombos.map(c => Number(c.totalPrice || 0))].filter(p => p > 0);
          const perKit = prices.reduce((s, p) => s + p, 0);
          const cats = new Set(selProducts.map(i => i.category).filter(Boolean)).size;
          const shareShortlist = () => {
            const lines = [
              ...selProducts.map(i => `• ${i.name} (${toINR(priceOf(i))})`),
              ...selCombos.map(c => `• ${c.label || 'Bundle'} (${toINR(c.totalPrice)})`),
            ];
            setMsg(`Our shortlist so far:\n${lines.join('\n')}`.slice(0, 2000));
            setTab('chat');
          };

          if (!totalSel) return (
            <div style={{ textAlign: 'center', padding: isMobile ? '56px 8px' : '96px 0', maxWidth: 520, margin: '0 auto' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', border: '1px solid #dddddd', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px', color: '#222222' }}>{pxHeart(false)}</div>
              <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontWeight: 400, fontSize: 34, color: '#222222', margin: '0 0 10px' }}>Nothing shortlisted yet</h1>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 14, color: '#6a6a6a', lineHeight: 1.65, margin: '0 0 28px' }}>Tap the heart on any piece in the catalogue and it will be kept here, ready to price and share with your planner.</p>
              <button type="button" onClick={() => setTab('catalogue')} style={pxBtn('primary')}>Browse the catalogue</button>
            </div>
          );
          return (
            <div>
              <TabHeader isMobile={isMobile} eyebrow="Your shortlist"
                title={`${totalSel} piece${totalSel !== 1 ? 's' : ''} you love`}
                sub={`${cats > 1 ? `${cats} categories · ` : ''}${perKit > 0 ? `${toINR(perKit)} for one of each` : ''}`}>
                <button type="button" onClick={shareShortlist} style={pxBtn('ghost')}>Send to planner</button>
                <button type="button" onClick={() => setTab('calculator')} style={pxBtn('primary')}>Estimate cost</button>
              </TabHeader>
              <SelectedItemsGroups
                selCombos={selCombos}
                selProducts={selProducts}
                wishlisted={wishlisted}
                toggleWish={toggleWish}
                setLightbox={setLightbox}
                portal={portal}
                isMobile={isMobile}
              />
            </div>
          );
        })()}

        {/* SHIPMENTS TAB — product portals only */}
        {tab === 'shipments' && (() => {
          // Status colours: soft tinted chip + readable text, one hue per stage.
          const STATUS_CHIP = {
            'Pending':          { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
            'Booked':           { bg: '#eef2ff', color: '#4338ca', border: '#e0e7ff' },
            'In Transit':       { bg: '#eff6ff', color: '#1d4ed8', border: '#dbeafe' },
            'Out for Delivery': { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
            'Delivered':        { bg: '#f0fdf4', color: '#15803d', border: '#dcfce7' },
            'Completed':        { bg: '#f0fdf4', color: '#15803d', border: '#dcfce7' },
            'Returned':         { bg: '#fdf2f8', color: '#be185d', border: '#fce7f3' },
            'Exception':        { bg: '#fef2f2', color: '#b91c1c', border: '#fee2e2' },
          };
          if (!shipmentsLoaded) return (
            <div>
              {/* Summary strip skeleton */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '20px 24px', background: '#fff', border: '1px solid #ebebeb' }}>
                <div style={{ width: 1, height: 24, background: 'rgba(184,151,90,0.3)', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <SkeletonBar w="18%" h={14} />
                  <SkeletonBar w="30%" h={10} />
                </div>
              </div>
              {/* Card skeletons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} style={{ background: '#fff', border: '1px solid #ebebeb', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, borderRadius: 4, animation: `obsidianFadeIn .5s ease ${i * 0.08}s both` }}>
                    <SkeletonBar w={10} h={10} style={{ borderRadius: '50%', flexShrink: 0 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <SkeletonBar w="35%" h={13} />
                      <SkeletonBar w="50%" h={10} />
                    </div>
                    <SkeletonBar w={100} h={26} style={{ borderRadius: 7, flexShrink: 0 }} />
                    <SkeletonBar w={70} h={22} style={{ borderRadius: 20, flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            </div>
          );
          if (shipments.length === 0) return (
            <div style={{ textAlign: 'center', padding: '64px 0', color: '#6a6a6a' }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📦</div>
              <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 18, fontWeight: 700, color: '#222222', marginBottom: 8 }}>No shipments yet</div>
              <div style={{ fontSize: 13, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>Shipment details will appear here once your parcels are dispatched.</div>
            </div>
          );
          const STATUSES = ['Pending', 'Booked', 'In Transit', 'Out for Delivery', 'Delivered', 'Returned', 'Exception'];
          const boxesLabel = n => (n || n === 0 ? `${n} box${n === 1 ? '' : 'es'}` : '');
          const photoOf = s => (typeof s.photoUrl === 'string' && /^https:\/\//i.test(s.photoUrl) ? s.photoUrl : '');

          // Options for the State / City filters are derived from the shipments
          // actually present, so the dropdown never offers a state/city with zero results.
          const availableStates = [...new Set(shipments.map(s => s.state).filter(Boolean))].sort();
          const cityPool = shipStateFilter ? shipments.filter(s => s.state === shipStateFilter) : shipments;
          const availableCities = [...new Set(cityPool.map(s => s.city).filter(Boolean))].sort();

          // Combine every active filter — status, state, city, and recipient-name/tracking-number search.
          let filtered = shipFilter === 'all' ? shipments : shipments.filter(s => s.status === shipFilter);
          if (shipStateFilter) filtered = filtered.filter(s => s.state === shipStateFilter);
          if (shipCityFilter) filtered = filtered.filter(s => s.city === shipCityFilter);
          if (shipNameSearch.trim()) {
            const q = shipNameSearch.trim().toLowerCase();
            filtered = filtered.filter(s =>
              (s.recipientName || '').toLowerCase().includes(q) ||
              (s.trackingId || '').toLowerCase().includes(q)
            );
          }

          // Pagination — the full list is fetched once (it also feeds the counts,
          // filters and CSV export) and shown one page at a time, so even
          // thousands of shipments render as 25/50/100 rows.
          const totalShipPages = Math.max(1, Math.ceil(filtered.length / shipPageSize));
          const shipPageSafe = Math.min(shipPage, totalShipPages);
          const paginated = filtered.slice((shipPageSafe - 1) * shipPageSize, shipPageSafe * shipPageSize);
          const goShipPage = (n) => {
            setShipPage(Math.min(totalShipPages, Math.max(1, n)));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          };
          // Page numbers to show: first, last, and a window around the current page.
          const pageNums = [...new Set([1, shipPageSafe - 1, shipPageSafe, shipPageSafe + 1, totalShipPages])]
            .filter(n => n >= 1 && n <= totalShipPages).sort((a, b) => a - b);

          const downloadExcel = () => {
            const headers = ['Recipient', 'City', 'State', 'Phone', 'Tracking ID', 'Partner', 'Boxes', 'Status', 'Updated'];
            const rows = shipments.map(s => [
              s.recipientName || '', s.city || '', s.state || '', s.phone || '',
              s.trackingId || '', s.shippingPartner || '', s.boxes ?? '', s.status || '',
              s.lastTrackedAt ? new Date(s.lastTrackedAt).toLocaleDateString('en-IN') : '',
            ]);
            const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `shipments-${portal.orderRef || 'export'}.csv`;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
          };
          const delivered = shipments.filter(s => s.status === 'Delivered' || s.status === 'Completed').length;
          const moving = shipments.filter(s => ['Booked', 'In Transit', 'Out for Delivery'].includes(s.status)).length;
          const issues = shipments.filter(s => ['Returned', 'Exception'].includes(s.status)).length;
          const waiting = shipments.length - delivered - moving - issues;
          const pct = n => `${(n / Math.max(1, shipments.length)) * 100}%`;
          const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');
          const statusCell = (s) => {
            const chip = STATUS_CHIP[s.status] || STATUS_CHIP['Pending'];
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', whiteSpace: 'nowrap' }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: chip.color, boxShadow: `0 0 0 3px ${chip.bg}` }} />
                {s.status || 'Pending'}
              </span>
            );
          };
          const trackBtn = (s) => s.shippingPartner && (
            <button type="button" onClick={() => window.open(getCourierTrackingUrl(s.shippingPartner), '_blank', 'noopener,noreferrer')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#7d6233' }}>
              {s.shippingPartner} {Ic.ext}
            </button>
          );
          const hasActiveFilters = shipFilter !== 'all' || shipStateFilter || shipCityFilter || shipNameSearch;
          const cellHead = { fontFamily: "'Jost',sans-serif", fontSize: 13, fontWeight: 600, color: '#6a6a6a', textAlign: 'left', padding: '14px 16px', borderBottom: '1px solid #dddddd', whiteSpace: 'nowrap' };
          const cell = { padding: '16px', borderBottom: '1px solid #ebebeb', verticalAlign: 'middle' };
          return (
            <div>
              <TabHeader isMobile={isMobile} eyebrow="Shipment tracking"
                title={`Tracking ${shipments.length.toLocaleString('en-IN')} parcel${shipments.length !== 1 ? 's' : ''}`}
                sub="Updated from each courier. Use the filters to find a person or a city.">
                <button type="button" onClick={downloadExcel} style={pxBtn('ghost')}>{Ic.dl} Download list</button>
              </TabHeader>

              {/* Progress */}
              <div style={{ marginBottom: isMobile ? 28 : 40 }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: isMobile ? '18px 12px' : 24, marginBottom: 18 }}>
                  {[
                    { l: 'Delivered', v: delivered, c: '#15803d' },
                    { l: 'On the way', v: moving, c: '#b8975a' },
                    { l: 'Awaiting pickup', v: waiting, c: '#94a3b8' },
                    { l: 'Needs attention', v: issues, c: '#b91c1c' },
                  ].map(k => (
                    <div key={k.l} style={{ borderTop: `2px solid ${k.c}`, paddingTop: 12 }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: isMobile ? 34 : 44, lineHeight: 1, color: '#222222', fontVariantNumeric: 'tabular-nums' }}>{k.v.toLocaleString('en-IN')}</div>
                      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', marginTop: 6 }}>{k.l}</div>
                    </div>
                  ))}
                </div>
                <div role="img" aria-label={`${delivered} of ${shipments.length} delivered`} style={{ display: 'flex', height: 6, background: '#ebebeb', borderRadius: 3, overflow: 'hidden' }}>
                  <span style={{ width: pct(delivered), background: '#15803d' }} />
                  <span style={{ width: pct(moving), background: '#b8975a' }} />
                  <span style={{ width: pct(issues), background: '#b91c1c' }} />
                </div>
              </div>

              {/* Filters — one Airbnb-style search pill: Who | State | City */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ ...AIR.bar, flex: '1 1 640px', maxWidth: 860, flexDirection: isMobile ? 'column' : 'row', borderRadius: isMobile ? 24 : 40, alignItems: isMobile ? 'stretch' : 'stretch' }}>
                  <label htmlFor="ship-search" className="air-in" style={{ ...AIR.seg, flex: '2 1 0', cursor: 'text', borderRadius: isMobile ? 24 : 40 }}>
                    <span style={AIR.segLabel}>Who</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input id="ship-search" value={shipNameSearch} onChange={e => setShipNameSearch(sanitizeText(e.target.value, 80))}
                        placeholder="Search name or tracking ID" style={AIR.segInput} />
                      {shipNameSearch && (
                        <button type="button" aria-label="Clear search" onClick={() => setShipNameSearch('')} style={{ width: 24, height: 24, flex: 'none', borderRadius: '50%', background: '#ebebeb', border: 'none', color: PX.ink, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Ic.close}</button>
                      )}
                    </span>
                  </label>
                  <span style={isMobile ? { height: 1, background: PX.line, margin: '0 20px' } : AIR.divider} />
                  <AirSelect label="State" value={shipStateFilter} onChange={setShipStateFilter} searchable={availableStates.length > 6}
                    options={[{ v: '', l: 'All states' }, ...availableStates]} placeholder="All states" style={{ flex: '1 1 0' }} />
                  <span style={isMobile ? { height: 1, background: PX.line, margin: '0 20px' } : AIR.divider} />
                  <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 0', minWidth: 0 }}>
                    <AirSelect label="City" value={shipCityFilter} onChange={setShipCityFilter} searchable={availableCities.length > 6} disabled={!shipStateFilter}
                      options={[{ v: '', l: 'All cities' }, ...availableCities]} placeholder={shipStateFilter ? 'All cities' : 'Any city'} align={isMobile ? 'left' : 'right'} style={{ flex: 1 }} />
                    {!isMobile && (
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px 0 4px' }}>
                        <button type="button" aria-label="Search shipments" onClick={() => document.getElementById('ship-search')?.focus()}
                          style={AIR.roundSearch}>
                          {Ic.search}
                        </button>
                      </span>
                    )}
                  </div>
                </div>
                {hasActiveFilters && (
                  <button type="button" onClick={() => { setShipFilter('all'); setShipStateFilter(''); setShipCityFilter(''); setShipNameSearch(''); }}
                    style={{ height: 44, background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: 14, fontWeight: 600, color: PX.ink, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                    Clear filters
                  </button>
                )}
              </div>

              {/* Status rail */}
              <nav aria-label="Delivery status" style={{ display: 'flex', gap: isMobile ? 20 : 30, overflowX: 'auto', scrollbarWidth: 'none', borderBottom: '1px solid #ebebeb', marginBottom: isMobile ? 12 : 4 }}>
                {['all', ...STATUSES].map(st => {
                  const count = st === 'all' ? shipments.length : shipments.filter(s => s.status === st).length;
                  if (st !== 'all' && count === 0) return null;
                  const on = shipFilter === st;
                  return (
                    <button key={st} type="button" aria-pressed={on} onClick={() => setShipFilter(st)}
                      style={{ flex: 'none', padding: '14px 0 12px', background: 'none', border: 'none', borderBottom: `2px solid ${on ? '#222222' : 'transparent'}`, marginBottom: -1, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: 14, fontWeight: on ? 600 : 500, color: on ? '#222222' : '#6a6a6a', whiteSpace: 'nowrap' }}>
                      {st === 'all' ? 'All' : st} <span style={{ fontSize: 12, color: '#6a6a6a', marginLeft: 3 }}>{count}</span>
                    </button>
                  );
                })}
              </nav>

              {filtered.length === 0 && (
                <p style={{ textAlign: 'center', padding: '40px 0', color: '#6a6a6a', fontSize: 14, fontFamily: "'Jost',sans-serif", margin: 0 }}>No shipments match these filters.</p>
              )}

              {/* List: table on iPad/laptop, cards on phones */}
              {filtered.length > 0 && !isMobile && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontFamily: "'Jost',sans-serif" }}>
                    <thead>
                      <tr>{['Recipient', 'Destination', 'Tracking', 'Boxes', 'Status', 'Updated'].map(h => <th key={h} scope="col" style={cellHead}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {paginated.map((s, idx) => (
                        <tr key={s._id || idx} onClick={e => { if (!e.target.closest('button, a')) setShipViewing(s); }} style={{ cursor: 'pointer' }}>
                          <td style={cell}>
                            <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 18, color: '#222222' }}>{s.recipientName}</div>
                            {s.phone && <div style={{ fontSize: 12, color: '#6a6a6a', marginTop: 2 }}>{s.phone}</div>}
                            {photoOf(s) && (
                              <button type="button" onClick={() => setShipViewing(s)} style={{ background: 'none', border: 'none', padding: '4px 0 0', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#7d6233', textDecoration: 'underline', textUnderlineOffset: 3 }}>View photo</button>
                            )}
                          </td>
                          <td style={{ ...cell, fontSize: 14, color: '#484848' }}>{[s.city, s.state].filter(Boolean).join(', ') || '—'}</td>
                          <td style={cell}>
                            {s.trackingId
                              ? <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, color: '#222222', letterSpacing: '0.02em' }}>{s.trackingId}</div>
                              : <div style={{ fontSize: 13, color: '#6a6a6a' }}>Awaiting tracking</div>}
                            {trackBtn(s)}
                          </td>
                          <td style={{ ...cell, fontSize: 14, color: '#484848', whiteSpace: 'nowrap' }}>{boxesLabel(s.boxes) || '—'}</td>
                          <td style={cell}>{statusCell(s)}</td>
                          <td style={{ ...cell, fontSize: 13, color: '#6a6a6a', whiteSpace: 'nowrap' }}>{fmtDate(s.lastTrackedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {filtered.length > 0 && isMobile && (
                <div>
                  {paginated.map((s, idx) => (
                    <div key={s._id || idx} onClick={e => { if (!e.target.closest('button, a')) setShipViewing(s); }} style={{ padding: '16px 0', borderBottom: '1px solid #ebebeb', display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                        <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 19, color: '#222222' }}>{s.recipientName}</span>
                        {statusCell(s)}
                      </div>
                      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#484848' }}>{[s.city, s.state].filter(Boolean).join(', ')}{s.phone ? ` · ${s.phone}` : ''}{boxesLabel(s.boxes) ? ` · ${boxesLabel(s.boxes)}` : ''}</div>
                      {photoOf(s) && (
                        <button type="button" onClick={() => setShipViewing(s)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#7d6233', textDecoration: 'underline', textUnderlineOffset: 3 }}>View photo</button>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: s.trackingId ? '#222222' : '#6a6a6a' }}>{s.trackingId || 'Awaiting tracking'}</span>
                        {trackBtn(s)}
                        <span style={{ marginLeft: 'auto', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a' }}>{fmtDate(s.lastTrackedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {filtered.length > SHIP_PAGE_SIZES[0] && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 22, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: '#6a6a6a', fontFamily: "'Jost',sans-serif", flexWrap: 'wrap' }}>
                    <span>{(shipPageSafe - 1) * shipPageSize + 1}–{Math.min(shipPageSafe * shipPageSize, filtered.length)} of {filtered.length.toLocaleString('en-IN')}</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Rows
                      <AirSelect boxed value={shipPageSize} onChange={setShipPageSize} style={{ width: 96 }}
                        options={SHIP_PAGE_SIZES.map(n => ({ v: n, l: String(n) }))} />
                    </label>
                  </div>
                  <nav aria-label="Shipment pages" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => goShipPage(shipPageSafe - 1)} disabled={shipPageSafe <= 1} aria-label="Previous page"
                      style={{ ...SHIP_PAGE_BTN, borderRadius: 40, height: 40, minWidth: 40, cursor: shipPageSafe <= 1 ? 'not-allowed' : 'pointer', opacity: shipPageSafe <= 1 ? 0.4 : 1 }}>{Ic.chevL}</button>
                    {pageNums.map((n, i) => (
                      <React.Fragment key={n}>
                        {i > 0 && n - pageNums[i - 1] > 1 && <span style={{ color: '#6a6a6a', fontSize: 12 }}>…</span>}
                        <button onClick={() => goShipPage(n)} aria-current={n === shipPageSafe ? 'page' : undefined}
                          style={{ ...SHIP_PAGE_BTN, borderRadius: 40, height: 40, minWidth: 40, cursor: 'pointer', ...(n === shipPageSafe ? { border: '1px solid #222222', background: '#222222', color: '#fff' } : {}) }}>{n}</button>
                      </React.Fragment>
                    ))}
                    <button onClick={() => goShipPage(shipPageSafe + 1)} disabled={shipPageSafe >= totalShipPages} aria-label="Next page"
                      style={{ ...SHIP_PAGE_BTN, borderRadius: 40, height: 40, minWidth: 40, cursor: shipPageSafe >= totalShipPages ? 'not-allowed' : 'pointer', opacity: shipPageSafe >= totalShipPages ? 0.4 : 1 }}>{Ic.chevR}</button>
                  </nav>
                </div>
              )}
              {shipViewing && (() => {
                const v = shipViewing;
                const photo = photoOf(v);
                const facts = [
                  ['Status', v.status || 'Pending'], ['Boxes', boxesLabel(v.boxes)], ['Courier', v.shippingPartner], ['Tracking ID', v.trackingId],
                  ['Destination', [v.city, v.state].filter(Boolean).join(', ')], ['Shipped', v.shippedDate ? new Date(v.shippedDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''],
                ].filter(([, x]) => x);
                return (
                  <div role="dialog" aria-modal="true" aria-label={`Shipment for ${v.recipientName}`} onClick={() => setShipViewing(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: photo ? 760 : 460, maxHeight: '92vh', overflowY: 'auto', padding: isMobile ? 18 : 28, position: 'relative', fontFamily: "'Jost',sans-serif" }}>
                      <button type="button" aria-label="Close" onClick={() => setShipViewing(null)}
                        style={{ position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#f7f7f7', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#222222' }}>{Ic.close}</button>
                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 24, color: '#222222', marginBottom: 14, paddingRight: 36 }}>{v.recipientName}</div>
                      {photo ? (
                        <a href={photo} target="_blank" rel="noopener noreferrer" title="Open full size">
                          <img src={photo} alt={`Shipment for ${v.recipientName}`} style={{ width: '100%', maxHeight: '58vh', objectFit: 'contain', borderRadius: 12, background: '#f7f7f7', display: 'block' }} />
                        </a>
                      ) : (
                        <p style={{ fontSize: 13, color: '#6a6a6a', margin: '0 0 6px' }}>No photo of this shipment yet.</p>
                      )}
                      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', margin: '16px 0 0', fontSize: 14 }}>
                        {facts.map(([k, x]) => (
                          <React.Fragment key={k}>
                            <dt style={{ color: '#6a6a6a', fontSize: 13 }}>{k}</dt>
                            <dd style={{ margin: 0, color: '#222222' }}>{x}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* MESSAGE BOARD */}
        {tab === 'chat' && (() => {
          const dayLabel = d => {
            const dt = new Date(d); const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1);
            if (dt.toDateString() === today.toDateString()) return 'Today';
            if (dt.toDateString() === y.toDateString()) return 'Yesterday';
            return dt.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
          };
          const timeOf = d => new Date(d).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
          const prompts = portal.type === 'product'
            ? ['Can we see samples before we confirm?', 'What is the lead time for our quantity?', 'Can you add our logo to these?', 'Can you deliver to multiple addresses?']
            : isEvent
            ? ['Can we mix two themes?', 'Can the DJ start earlier?', 'Is there a rain plan for the venue?', 'Can we add a vegan menu?']
            : ['Are these dates available?', 'Can we visit the property first?', 'Is transport from the office possible?', 'What is included in the package?'];
          const canSend = !sending && (msg.trim() || files.length > 0);
          let lastDay = '';
          return (
            <div style={{ maxWidth: 920, margin: '0 auto' }}>
              <TabHeader isMobile={isMobile} eyebrow="Message board" title="Talk to your planner"
                sub={`Questions, changes or files for ${portal.title || portal.orderRef} — everything stays in one thread.`} />

              <div style={{ background: '#fff', border: '1px solid #ebebeb', display: 'flex', flexDirection: 'column', minHeight: isMobile ? 'calc(100vh - 260px)' : 560 }}>
                {/* Thread header */}
                <div style={{ padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: '1px solid #ebebeb', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#222222', color: '#d9c49a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 22, flexShrink: 0 }}>M</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 20, color: '#222222', lineHeight: 1.2 }}>Marqland Studios</div>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#15803d' }} /> Usually replies within an hour
                    </div>
                  </div>
                </div>

                {/* Your name */}
                {!nameSet && (
                  <div style={{ padding: isMobile ? '12px 16px' : '14px 24px', background: '#f7f7f7', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label htmlFor="chat-name" style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#484848' }}>Your name</label>
                    <input id="chat-name" value={clientName} onChange={e => setClientName(sanitizeText(e.target.value, 60))}
                      onKeyDown={e => { if (e.key === 'Enter' && clientName.trim()) setNameSet(true); }}
                      placeholder="So the team knows who's writing" maxLength={60}
                      style={{ flex: '1 1 200px', height: 40, background: '#fff', border: '1px solid #dddddd', padding: '0 12px', color: '#222222', fontSize: 14, outline: 'none', fontFamily: "'Jost',sans-serif" }} />
                    <button type="button" onClick={() => { if (clientName.trim()) setNameSet(true); }} disabled={!clientName.trim()}
                      style={{ ...pxBtn('primary'), height: 40, opacity: clientName.trim() ? 1 : 0.4 }}>Save</button>
                  </div>
                )}

                {/* Thread */}
                <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '18px 14px' : '26px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {portal.messages.length === 0 && (
                    <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 520, padding: '24px 0' }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 26, color: '#222222', marginBottom: 8 }}>Start the conversation</div>
                      <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 14, color: '#6a6a6a', margin: '0 0 20px' }}>Pick a question to begin, or write your own below.</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                        {prompts.map(p => (
                          <button key={p} type="button" onClick={() => setMsg(p)}
                            style={{ minHeight: 40, padding: '0 16px', borderRadius: 20, border: '1px solid #dddddd', background: '#fff', fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', cursor: 'pointer' }}>{p}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {portal.messages.map(m => {
                    const isT = m.sender === 'team';
                    const day = dayLabel(m.createdAt);
                    const showDay = day !== lastDay; lastDay = day;
                    const dayRule = showDay && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '6px 0' }}>
                        <span style={{ flex: 1, height: 1, background: '#ebebeb' }} />
                        <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, fontWeight: 500, color: '#6a6a6a' }}>{day}</span>
                        <span style={{ flex: 1, height: 1, background: '#ebebeb' }} />
                      </div>
                    );
                    // Event portals: a pick shows as one line with the option's photo.
                    if (m.kind === 'pick') {
                      const title = (m.text || '').replace(/^(Picked|Added) /, '').replace(/ (for the .*|as an add-on)$/, '');
                      const pic = (portal.eventItems || []).find(i => i.title === title)?.images?.[0];
                      return (
                        <React.Fragment key={m._id}>
                          {dayRule}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, alignSelf: 'center', maxWidth: '92%', background: '#fff', border: '1px solid #ebebeb', borderRadius: 40, padding: '6px 16px 6px 6px', fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#484848' }}>
                            <span style={{ width: 30, height: 30, borderRadius: '50%', overflow: 'hidden', background: '#f7f7f7', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff385c' }}>
                              {pic ? <img src={pic} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '♥'}
                            </span>
                            <span>{m.senderName ? <b style={{ color: '#222222', fontWeight: 600 }}>{m.senderName} </b> : null}{(m.text || '').replace(/^Picked/, 'picked').replace(/^Added/, 'added')} · {timeOf(m.createdAt)}</span>
                          </div>
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={m._id}>
                        {dayRule}
                        <div style={{ display: 'flex', justifyContent: isT ? 'flex-start' : 'flex-end', gap: 10 }}>
                          {isT && <div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: '50%', background: '#222222', color: '#d9c49a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 16, flexShrink: 0, alignSelf: 'flex-end' }}>M</div>}
                          <div style={{ maxWidth: isMobile ? '82%' : '68%', minWidth: 0 }}>
                            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', marginBottom: 5, textAlign: isT ? 'left' : 'right' }}>{m.senderName} · {timeOf(m.createdAt)}</div>
                            <div style={{ background: isT ? '#f7f7f7' : '#222222', color: isT ? '#222222' : '#fff', borderRadius: isT ? '4px 18px 18px 18px' : '18px 4px 18px 18px', padding: '12px 16px' }}>
                              {m.text && <div style={{ fontSize: 14, lineHeight: 1.65, fontFamily: "'Jost',sans-serif", whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>}
                              {(m.attachments || []).map((a, i) => (
                                <AttachChip key={i} att={a} fileUrl={attachmentUrl(slug, m._id, i, a.name)} onImage={src => setLightbox({ src, alt: a.name })} />
                              ))}
                            </div>
                          </div>
                          {!isT && <div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: '50%', background: '#ebebeb', color: '#484848', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Jost',sans-serif", fontSize: 12, fontWeight: 600, flexShrink: 0, alignSelf: 'flex-end' }}>{(m.senderName || clientName || 'C').slice(0, 2).toUpperCase()}</div>}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={chatEnd} />
                </div>

                <FileStrip files={files} onRemove={i => setFiles(files.filter((_, idx) => idx !== i))} />

                {/* Composer */}
                <div style={{ padding: isMobile ? '12px' : '16px 24px 20px', borderTop: '1px solid #ebebeb' }}>
                  <div style={{ border: '1px solid #dddddd', background: '#fff' }}>
                    <textarea value={msg} onChange={e => setMsg(sanitizeText(e.target.value, 2000))} aria-label="Message"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Write a message…" rows={isMobile ? 2 : 3} maxLength={2000}
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', resize: 'none', border: 'none', outline: 'none', padding: '14px 16px', color: '#222222', fontSize: 15, fontFamily: "'Jost',sans-serif", lineHeight: 1.55, background: 'transparent' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 8px 8px 12px', borderTop: '1px solid #ebebeb' }}>
                      <button type="button" onClick={() => fileRef.current?.click()}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 40, background: 'none', border: 'none', cursor: 'pointer', color: '#484848', fontSize: 13, fontFamily: "'Jost',sans-serif", padding: '0 4px' }}>
                        {Ic.attach} Attach files
                      </button>
                      {!isMobile && <span style={{ fontSize: 12, color: '#6a6a6a', fontFamily: "'Jost',sans-serif" }}>Up to 5 files, 10 MB each · Shift + Enter for a new line</span>}
                      <button type="button" onClick={send} disabled={!canSend}
                        style={{ ...pxBtn('primary'), marginLeft: 'auto', height: 40, opacity: canSend ? 1 : 0.35, cursor: canSend ? 'pointer' : 'not-allowed' }}>
                        {sending ? 'Sending…' : <>Send {Ic.send}</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* File input at root — avoids display:none breaking programmatic .click() */}
      {/* Root-level file input — opacity:0 keeps it invisible but events still fire */}
      <input
        ref={fileRef}
        type="file"
        multiple
        style={{ position: 'fixed', top: -200, left: -200, width: 1, height: 1, opacity: 0 }}
        onChange={e => {
          const picked = Array.from(e.target.files || []);
          if (picked.length > 0) setFiles(prev => [...prev, ...picked].slice(0, 5));
          e.target.value = '';
        }}
      />

      {/* ── Footer — tonal shift ── */}
      <div style={{ background: '#ffffff', borderTop: `1px solid rgba(255,255,255,0.04)`, padding: isMobile ? '16px' : '20px 32px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginBottom: 5 }}>
          <div style={{ width: 16, height: 16, background: GOLD_GRAD, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#222222', fontWeight: 900, fontSize: 12 }}>M</div>
          <span style={{ color: '#6a6a6a', fontSize: 12, fontWeight: 600, fontFamily: "'Jost',sans-serif" }}>Marqland Studios</span>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.18)', fontSize: 12, fontFamily: "'Jost',sans-serif" }}>This is a private proposal. Please do not share this link.</p>
      </div>
    </div>
  );
};



// ─────────────────────────────────────────────────────────────────────────────
// COMBO THUMB GALLERY — adapted from old ClientPortalView
// Composite/collage image is the default main image (slide 0): the cover the
// server renders for every combo (services/comboCoverService.js), or a CSS
// mosaic of the item photos until that cover is ready.
// Each bundled product image follows as subsequent slides.
// Clicking a product thumbnail swaps the main image AND shows that product's
// name + description caption below the thumbnail strip.
// Arrow buttons appear on hover only (no pill/background).
// ─────────────────────────────────────────────────────────────────────────────
const ComboThumbGallery = ({ combo, onZoom, maxHeight = 280, minHeight = 180 }) => {
  const isMobile = useMobile();
  const [idx, setIdx] = React.useState(0);
  const [hovering, setHovering] = React.useState(false);

  const itemImgs = (combo.items || []).filter(c => c && c.imageUrl);

  // Build slides:
  // Slide 0 = always the collage (server image if exists, else CSS mosaic — flagged kind:'mosaic')
  // Slides 1…N = each individual item image
  const slides = [
    combo.collageImageUrl
      ? { kind: 'composite', imageUrl: combo.collageImageUrl, name: combo.label || 'Complete Bundle' }
      : { kind: 'mosaic', name: combo.label || 'Complete Bundle' },
    ...itemImgs.map(c => ({ kind: 'component', imageUrl: c.imageUrl, name: c.name, description: c.description, subCategory: c.subCategory, price: c.price })),
  ];

  if (slides.length === 0 || (slides.length === 1 && slides[0].kind === 'mosaic' && itemImgs.length === 0)) {
    return (
      <div style={{ height: minHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7', color: 'rgba(0,0,0,0.2)', fontSize: 12, fontWeight: 700, borderRadius: '12px 12px 0 0' }}>
        No image
      </div>
    );
  }

  const safeIdx = Math.min(idx, slides.length - 1);
  const active = slides[safeIdx];
  const allImgUrls = slides.filter(s => s.imageUrl).map(s => s.imageUrl);

  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + slides.length) % slides.length); };
  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % slides.length); };

  // CSS mosaic collage — polaroid-style 4-up grid matching reference image
  const MosaicSlide = () => {
    const imgs4 = itemImgs.slice(0, 4);
    const cols = imgs4.length === 1 ? 1 : imgs4.length === 2 ? 2 : imgs4.length === 3 ? 3 : 4;
    return (
      <div
        onClick={() => {}}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        style={{ position: 'relative', height: maxHeight, minHeight, background: '#ebebeb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 8 : 16, gap: isMobile ? 6 : 10, boxSizing: 'border-box', cursor: 'default' }}>
        {imgs4.map((item, i) => {
          // Slight rotation like polaroid frames
          const rot = [-2, 1.5, -1, 2][i] || 0;
          return (
            <div key={i} style={{
              flex: 1,
              height: '100%',
              maxWidth: `${100 / cols}%`,
              background: '#fff',
              borderRadius: isMobile ? 6 : 10,
              boxShadow: '0 4px 16px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.10)',
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              transform: `rotate(${rot}deg)`,
              transition: 'transform .3s ease',
            }}>
              {/* Photo area */}
              <div style={{ overflow: 'hidden', background: '#f7f7f7', minHeight: 0, flex: 1 }}>
                <img src={item.imageUrl} alt={item.name}
                  style={{ width: '100%', height: 'auto', display: 'block' }} />
              </div>
              {/* Polaroid caption strip */}
              <div style={{ padding: '4px 6px 6px', flexShrink: 0, background: '#fff' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: 'rgba(0,0,0,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.subCategory || item.name}
                </div>
              </div>
            </div>
          );
        })}
        {/* Dark bottom gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(0deg,rgba(10,20,34,0.55) 0%,transparent 55%)', pointerEvents: 'none', borderRadius: '12px 12px 0 0' }} />
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '12px 12px 0 0' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}>

      {/* Main view — mosaic for slide 0 when no collage, otherwise image */}
      {active.kind === 'mosaic' ? (
        <MosaicSlide />
      ) : (
        <div
          onClick={() => active.imageUrl && onZoom({ src: active.imageUrl, alt: active.name, all: allImgUrls, startIdx: Math.max(0, allImgUrls.indexOf(active.imageUrl)) })}
          style={{ cursor: active.imageUrl ? 'zoom-in' : 'default', position: 'relative', width: '100%', aspectRatio: active.kind === 'composite' || !isMobile ? '4/3' : '1/1', overflow: 'hidden', background: '#f7f7f7' }}
        >
          <img
            src={active.imageUrl}
            alt={active.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity .2s ease' }}
          />
          {/* The server-made combo cover has its own caption band — no fade over it */}
          {active.kind !== 'composite' && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(0deg,rgba(10,20,34,0.85),transparent)', pointerEvents: 'none' }} />
          )}
        </div>
      )}

      {/* Hover arrows — bigger, rounded, blur backdrop */}
      {slides.length > 1 && (
        <>
          <button onClick={prev} style={{
            position: 'absolute', left: 6, top: '50%',
            background: 'rgba(0,0,0,0.32)', border: 'none',
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#fff', zIndex: 4,
            opacity: hovering ? 1 : 0,
            transform: hovering ? 'translateY(-50%) scale(1.15)' : 'translateY(-50%) scale(1)',
            transition: 'opacity .18s, transform .18s',
            backdropFilter: 'blur(4px)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button onClick={next} style={{
            position: 'absolute', right: 6, top: '50%',
            background: 'rgba(0,0,0,0.32)', border: 'none',
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#fff', zIndex: 4,
            opacity: hovering ? 1 : 0,
            transform: hovering ? 'translateY(-50%) scale(1.15)' : 'translateY(-50%) scale(1)',
            transition: 'opacity .18s, transform .18s',
            backdropFilter: 'blur(4px)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}

      {/* Thumbnail strip — always shown when ≥2 slides */}
      {slides.length > 1 && (
        <div style={{ display: 'flex', gap: 4, padding: '6px 10px', background: '#fff', borderTop: '1px solid #ebebeb', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {slides.map((s, i) => {
            const isSel = i === safeIdx;
            return (
              <div key={s.kind === 'mosaic' ? 'mosaic' : (s.kind === 'composite' ? 'composite' : (s.name + i))}
                onClick={e => { e.stopPropagation(); setIdx(i); }}
                title={s.kind === 'mosaic' || s.kind === 'composite' ? 'All items' : s.name}
                style={{ width: 38, height: 38, borderRadius: 6, overflow: 'hidden', flexShrink: 0, border: `2px solid ${isSel ? '#b8975a' : 'transparent'}`, cursor: 'pointer', transition: 'border-color .2s', background: '#f7f7f7' }}>
                {s.kind === 'mosaic' ? (
                  /* Tiny 2×2 mosaic thumbnail */
                  <div style={{ width: '100%', height: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 1 }}>
                    {itemImgs.slice(0, 4).map((item, ti) => (
                      <div key={ti} style={{ overflow: 'hidden', background: '#ebebeb' }}>
                        <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <img src={s.imageUrl} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Active component caption — only for individual item slides */}
      {active.kind === 'component' && (active.name || active.description) && (
        <div style={{ padding: '8px 12px 10px', background: '#fff', borderTop: slides.length > 1 ? 'none' : '1px solid #ebebeb' }}>
          {active.name && (
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, fontWeight: 700, color: '#222222', letterSpacing: '0.02em', marginBottom: active.description ? 3 : 0 }}>
              {active.subCategory
                ? <><span style={{ color: '#7d6233', fontWeight: 600, fontSize: 12, marginRight: 6 }}>{active.subCategory}</span>{active.name}</>
                : active.name
              }
            </div>
          )}
          {active.description && (
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 12.5, color: '#6a6a6a', lineHeight: 1.55, margin: 0 }}>
              {active.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMBO BENTO — used in Selected Items tab to show shortlisted bundles
// Same GlassCard grid as product cards, ComboThumbGallery as image area
// ─────────────────────────────────────────────────────────────────────────────
const ComboBento = ({ combos, onZoom, wishlisted = new Set(), onToggleWish = () => {} }) => {
  const isMobile = useMobile();
  const isTablet = useTablet();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: isMobile ? 10 : 16, alignItems: 'start' }}>
      {combos.map((combo, idx) => {
        const loved = wishlisted.has(String(combo._id));
        return (
          <GlassCard key={combo._id} delay={idx * 0.05} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ position: 'relative' }}>
              <ComboThumbGallery combo={combo} onZoom={onZoom} maxHeight={200} minHeight={160} />
              {/* Heart */}
              <button
                onClick={e => { e.stopPropagation(); onToggleWish(String(combo._id)); }}
                title={loved ? 'Remove from shortlist' : 'Shortlist this bundle'}
                style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 10,
                  background: loved ? 'rgba(220,53,69,0.88)' : 'rgba(255,255,255,0.92)',
                  border: `1px solid ${loved ? 'rgba(220,53,69,0.3)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: 2, width: 32, height: 32,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: loved ? '#fff' : '#6a6a6a',
                  backdropFilter: 'blur(4px)', transition: 'all .2s',
                }}>
                {loved ? Ic.heart : Ic.heartO}
              </button>
            </div>
            {/* Info panel */}
            <div style={{ padding: isMobile ? '12px 14px 14px' : '16px 18px 18px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ fontSize: isMobile ? 12 : 12, fontWeight: 400, color: '#222222', lineHeight: 1.2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Jost',sans-serif", letterSpacing: '0.04em' }}>
                  {combo.label || `Bundle ${idx + 1}`}
                </div>
                <div style={{ fontSize: isMobile ? 13 : 15, fontWeight: 500, color: '#222222', fontFamily: "'Jost',sans-serif", flexShrink: 0 }}>
                  {toINR(combo.totalPrice)}
                </div>
              </div>
            </div>
          </GlassCard>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HAMPER BUILDER — client builds N custom combo hampers from the catalogue,
// filtered by budget + category/subcategory, then saves them into the
// portal's Combo tab (persisted via PUT /public/:slug/combo-items).
// ─────────────────────────────────────────────────────────────────────────────
const HamperBuilder = ({ open, onClose, items = [], existingClientCombos = [], initialCatRange = {}, slug, isMobile, onSaved }) => {
  const [totalBudget, setTotalBudget] = React.useState('');
  const [selCats, setSelCats] = React.useState([]);
  const [selSubs, setSelSubs] = React.useState([]);
  const [catRange, setCatRange] = React.useState({}); // { [cat]: { min, max } }
  const [hampers, setHampers] = React.useState([]);   // finished hampers this session — { id, label, productIds, isSaved }
  const [currentName, setCurrentName] = React.useState('');
  const [currentIds, setCurrentIds] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [collapsedCats, setCollapsedCats] = React.useState({}); // catalogue category sections — collapsed by default
  const [expandedImg, setExpandedImg] = React.useState({});     // per-product image-gallery expand state
  const [expandedHampers, setExpandedHampers] = React.useState({}); // per-hamper image-row expand state
  const [hoverPreview, setHoverPreview] = React.useState(null); // { src, x, y } — bigger image shown on thumbnail hover

  // Reset everything fresh every time the builder is opened, seeding
  // "My Combos" from whatever the client already saved previously so they
  // can add more without losing earlier hampers. Also auto-expands the very
  // first product (in category order) that has more than one image, the
  // first hamper row, and carries over any per-category min/max the client
  // had already set in the main catalogue filters.
  React.useEffect(() => {
    if (!open) return;
    setTotalBudget('');
    setCurrentName(''); setCurrentIds([]); setError('');
    setCollapsedCats({});

    // Carry over per-category min/max from the main catalogue's filter bar
    const seededRange = {};
    Object.entries(initialCatRange || {}).forEach(([cat, r]) => {
      if (r && ((r.min !== '' && r.min != null) || (r.max !== '' && r.max != null))) {
        seededRange[cat] = { min: r.min ?? '', max: r.max ?? '' };
      }
    });
    setCatRange(seededRange);
    setSelCats(Object.keys(seededRange));
    setSelSubs([]);

    const seededHampers = existingClientCombos.map((c, i) => ({
      id: String(c._id || `existing-${i}`),
      label: c.label || `My Hamper ${i + 1}`,
      productIds: (c.items || []).map(it => String(it.productId)),
      isSaved: true,
    }));
    setHampers(seededHampers);
    setExpandedHampers(seededHampers.length ? { [seededHampers[0].id]: true } : {});

    const byCat = new Map();
    items.forEach(it => { const c = it.category || 'Other'; if (!byCat.has(c)) byCat.set(c, []); byCat.get(c).push(it); });
    let defaultExpanded = {};
    for (const [, list] of byCat) {
      const firstMulti = list.find(it => [it.imageUrl, ...(it.additionalImages || [])].filter(Boolean).length > 1);
      if (firstMulti) { defaultExpanded = { [String(firstMulti._id)]: true }; break; }
    }
    setExpandedImg(defaultExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
  const subCatsFor = cat => [...new Set(items.filter(i => i.category === cat).map(i => i.subCategory).filter(Boolean))];

  // Every image for a product — cover photo + any additional angles
  const getImages = it => [it.imageUrl, ...(it.additionalImages || [])].filter(Boolean);

  const toggleCat = cat => setSelCats(p => {
    if (p.includes(cat)) {
      setSelSubs(sp => sp.filter(sc => !subCatsFor(cat).includes(sc)));
      setCatRange(cr => { const n = { ...cr }; delete n[cat]; return n; });
      return p.filter(c => c !== cat);
    }
    return [...p, cat];
  });
  const toggleSub = sc => setSelSubs(p => p.includes(sc) ? p.filter(s => s !== sc) : [...p, sc]);
  const setRange = (cat, patch) => setCatRange(p => ({ ...p, [cat]: { ...(p[cat] || { min: '', max: '' }), ...patch } }));
  // Category sections start COLLAPSED — only explicit `false` in state opens one
  const isCatOpen = cat => collapsedCats[cat] === false;
  const toggleCatalogueCat = cat => setCollapsedCats(p => ({ ...p, [cat]: isCatOpen(cat) }));
  const toggleImgExpand = id => setExpandedImg(p => ({ ...p, [id]: !p[id] }));
  const toggleHamperExpand = id => setExpandedHampers(p => ({ ...p, [id]: !p[id] }));

  // Hover-to-zoom: show a bigger version of a thumbnail near the cursor
  const showHoverPreview = (src, e) => setHoverPreview({ src, x: e.clientX, y: e.clientY });
  const moveHoverPreview = e => setHoverPreview(p => p ? { ...p, x: e.clientX, y: e.clientY } : p);
  const hideHoverPreview = () => setHoverPreview(null);

  const itemById = id => items.find(i => String(i._id) === String(id));

  // Catalogue filtered by the budget/category/subcategory constraints above
  const filteredCatalogue = items.filter(i => {
    if (selCats.length && !selCats.includes(i.category)) return false;
    if (selSubs.length && i.subCategory && !selSubs.includes(i.subCategory)) return false;
    const price = Number(i.price || 0);
    const range = catRange[i.category];
    if (range) {
      if (range.min !== '' && !isNaN(Number(range.min)) && price < Number(range.min)) return false;
      if (range.max !== '' && !isNaN(Number(range.max)) && price > Number(range.max)) return false;
    }
    return true;
  });

  // Grouped category-wise so the catalogue can collapse sections instead of
  // one long scroll — same pattern as the main catalogue.
  const catalogueGroups = (() => {
    const map = new Map();
    filteredCatalogue.forEach(it => {
      const c = it.category || 'Other';
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(it);
    });
    return Array.from(map.entries()).map(([cat, list]) => ({ cat, list }));
  })();

  const currentTotal = currentIds.reduce((s, id) => s + Number(itemById(id)?.price || 0), 0);
  const budgetNum = totalBudget === '' ? null : Number(totalBudget);
  const overBudget = budgetNum != null && currentTotal > budgetNum;

  const toggleCurrent = id => setCurrentIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const addHamperToList = () => {
    if (!currentIds.length) return;
    const newHamper = {
      id: `local-${Date.now()}`,
      label: sanitizeText(currentName.trim(), 80) || `My Hamper ${hampers.length + 1}`,
      productIds: currentIds,
      isSaved: false,
    };
    if (hampers.length === 0) setExpandedHampers({ [newHamper.id]: true });
    setHampers(p => [...p, newHamper]);
    setCurrentName(''); setCurrentIds([]);
  };

  // Removing a hamper that was already saved to the portal deletes it from
  // the Combo tab immediately (not just locally) — only the client's own
  // OTHER already-saved combos are resubmitted, so any not-yet-saved hampers
  // in progress are never accidentally persisted by this action.
  const removeHamper = async (id) => {
    const target = hampers.find(h => h.id === id);
    if (!target) return;
    setHampers(prev => prev.filter(h => h.id !== id));
    setExpandedHampers(prev => { const n = { ...prev }; delete n[id]; return n; });

    if (!target.isSaved) return;

    try {
      const remainingSaved = hampers.filter(h => h.isSaved && h.id !== id);
      const payload = remainingSaved.map(h => ({ label: h.label, items: h.productIds.map(pid => ({ productId: pid })) }));
      const res = await fetch(`${API_BASE}/api/portal/public/${slug}/combo-items`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comboItems: payload }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Delete failed');
      onSaved && onSaved({ title: 'Combo removed', body: `"${target.label}" was removed from your Combo tab.`, icon: '🗑️' });
    } catch (e) {
      // Roll back — put it back in the list so nothing silently vanishes on a network error
      setHampers(prev => [...prev, target]);
      setError(e.message || 'Could not delete that combo. Please try again.');
    }
  };

  const saveHampers = async () => {
    const unsaved = hampers.filter(h => !h.isSaved);
    if (!unsaved.length) { setError('Nothing new to save — every combo here is already in your Combo tab.'); return; }
    setSaving(true); setError('');
    try {
      const payload = hampers.map(h => ({ label: h.label, items: h.productIds.map(pid => ({ productId: pid })) }));
      const res = await fetch(`${API_BASE}/api/portal/public/${slug}/combo-items`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comboItems: payload }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Save failed');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save your hampers. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const chip = active => ({
    padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700,
    fontFamily: "'Jost',sans-serif", transition: 'all .15s',
    background: active ? '#222222' : '#fff',
    color: active ? '#fff' : '#222222',
    border: `1px solid ${active ? '#222222' : '#dddddd'}`
  });
  const num = { width: 80, padding: '6px 12px', borderRadius: 20, border: '1px solid #dddddd', fontSize: 12, fontFamily: "'Jost',sans-serif", outline: 'none', MozAppearance: 'textfield' };
  const primaryBtn = { background: '#222222', color: '#fff', border: 'none', padding: '14px 24px', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: "'Jost',sans-serif", borderRadius: 8 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 9000, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: isMobile ? '18px 18px 14px' : '22px 32px', borderBottom: '1px solid #ebebeb', flexShrink: 0 }}>
        <div>
          <span className="ms-pill" style={{ marginBottom: 8, display: 'inline-block' }}>Hamper Builder</span>
          <h2 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontWeight: 400, fontStyle: 'italic', fontSize: isMobile ? 20 : 26, color: '#222222', margin: 0 }}>
            Pick your products
          </h2>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6a6a6a', padding: 6, flexShrink: 0 }}>{Ic.close}</button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '16px 18px' : '20px 32px 32px' }}>

        {/* ── Budget & Target Definition — now inline, right at the top ──── */}
        <div style={{ background: '#f7f7f7', border: '1px solid #ebebeb', borderRadius: 8, padding: isMobile ? 14 : 18, marginBottom: 22 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#929292', marginBottom: 8, fontFamily: "'Jost',sans-serif" }}>Total Budget</div>
              <input type="number" inputMode="numeric" placeholder="e.g. 5000" value={totalBudget}
                onChange={e => setTotalBudget(e.target.value)}
                style={{ ...num, width: 140, height: 34, boxSizing: 'border-box', padding: '0 14px', fontSize: 13, background: '#fff' }} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#929292', marginBottom: 8, fontFamily: "'Jost',sans-serif" }}>Categories <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#929292' }}>(leave blank for all)</span></div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {categories.map(cat => (
                  <button key={cat} style={{ ...chip(selCats.includes(cat)), height: 34, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', padding: '0 16px' }} onClick={() => toggleCat(cat)}>{cat}</button>
                ))}
              </div>
            </div>
          </div>

          {selCats.map(cat => {
            const subs = subCatsFor(cat);
            const range = catRange[cat] || { min: '', max: '' };
            return (
              <div key={cat} style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #ebebeb' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#7d6233', marginBottom: 10, fontFamily: "'Jost',sans-serif" }}>{cat}</div>
                {subs.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {subs.map(sc => (
                      <button key={sc} style={{ ...chip(selSubs.includes(sc)), padding: '4px 12px', fontSize: 12 }} onClick={() => toggleSub(sc)}>{sc}</button>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#6a6a6a' }}>Per-item limit for {cat}:</span>
                  <AirPriceRange size="sm" ariaPrefix={cat} min={range.min} max={range.max} style={{ width: 260, maxWidth: '100%' }}
                    onMin={v => setRange(cat, { min: v })} onMax={v => setRange(cat, { max: v })} />
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Filtered catalogue + combo assembly ─────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24 }}>

          {/* Filtered catalogue — grouped category-wise, collapsed by default */}
          <div style={{ flex: 1.3, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: '#929292', marginBottom: 12, fontFamily: "'Jost',sans-serif" }}>
              <span style={{ fontWeight: 700, color: '#7d6233' }}>{filteredCatalogue.length}</span> product{filteredCatalogue.length !== 1 ? 's' : ''} match your filters — expand a category, then tap a product to add it to the hamper you're building
            </div>
            <div>
              {catalogueGroups.length === 0 && (
                <div style={{ padding: '32px 0', textAlign: 'center', color: '#929292', fontSize: 12 }}>No products match this budget/category combination. Adjust your filters above.</div>
              )}
              {catalogueGroups.map(group => {
                const open_ = isCatOpen(group.cat);
                return (
                  <div key={group.cat} style={{ marginBottom: 12, border: '1px solid #ebebeb', borderRadius: 8, overflow: 'hidden' }}>
                    <button onClick={() => toggleCatalogueCat(group.cat)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: '#f7f7f7', border: 'none', cursor: 'pointer', padding: '10px 14px', textAlign: 'left' }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#222222', fontFamily: "'Jost',sans-serif" }}>{group.cat}</span>
                      <div style={{ flex: 1 }} />
                      <span style={{ fontSize: 12, color: '#929292', fontWeight: 600, fontFamily: "'Jost',sans-serif" }}>{group.list.length} item{group.list.length !== 1 ? 's' : ''}</span>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#b8975a" strokeWidth="2.5"
                        style={{ transform: open_ ? 'none' : 'rotate(-90deg)', transition: 'transform .2s', flexShrink: 0 }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>

                    {open_ && (
                      <div style={{ padding: '10px 12px 12px' }}>
                        {group.list.map(item => {
                          const idStr = String(item._id);
                          const selected = currentIds.includes(idStr);
                          const imgs = getImages(item);
                          const hasGallery = imgs.length > 1;
                          const galleryOpen = !!expandedImg[idStr];
                          return (
                            <div key={item._id} style={{ marginTop: 8 }}>
                              <div onClick={() => toggleCurrent(idStr)}
                                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', border: `1px solid ${selected ? 'rgba(184,151,90,0.5)' : '#ebebeb'}`, borderRadius: 8, cursor: 'pointer', background: selected ? 'rgba(184,151,90,0.07)' : '#fff', transition: 'all .12s' }}>
                                <div
                                  onMouseEnter={item.imageUrl ? (e => showHoverPreview(item.imageUrl, e)) : undefined}
                                  onMouseMove={item.imageUrl ? moveHoverPreview : undefined}
                                  onMouseLeave={item.imageUrl ? hideHoverPreview : undefined}
                                  style={{ width: 42, height: 42, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: '#f7f7f7' }}>
                                  {item.imageUrl && <img src={item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, fontWeight: 500, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Jost',sans-serif" }}>{item.name}</div>
                                  <div style={{ fontSize: 12, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.category}{item.subCategory ? ` › ${item.subCategory}` : ''}</div>
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#7d6233', flexShrink: 0 }}>{toINR(item.price)}</div>
                                {hasGallery && (
                                  <button onClick={e => { e.stopPropagation(); toggleImgExpand(idStr); }}
                                    title={galleryOpen ? 'Hide photos' : `Show all ${imgs.length} photos`}
                                    style={{ background: 'none', border: '1px solid #dddddd', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6a6a6a', flexShrink: 0, padding: 0 }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                                      style={{ transform: galleryOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                                      <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                  </button>
                                )}
                                <div style={{ width: 19, height: 19, borderRadius: '50%', border: `1.5px solid ${selected ? '#b8975a' : 'rgba(0,0,0,0.2)'}`, background: selected ? '#b8975a' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#222222', fontSize: 12, fontWeight: 700 }}>
                                  {selected ? '✓' : ''}
                                </div>
                              </div>

                              {hasGallery && galleryOpen && (
                                <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 12px 4px 66px' }}>
                                  {imgs.map((src, i) => (
                                    <div key={i}
                                      onMouseEnter={e => showHoverPreview(src, e)}
                                      onMouseMove={moveHoverPreview}
                                      onMouseLeave={hideHoverPreview}
                                      style={{ width: 48, height: 48, borderRadius: 6, overflow: 'hidden', border: '1px solid #dddddd', flexShrink: 0, cursor: 'zoom-in' }}>
                                      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current hamper + saved hampers — sticks together as one fixed panel while the catalogue on the left scrolls */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start', position: isMobile ? 'static' : 'sticky', top: isMobile ? 'auto' : 16, maxHeight: isMobile ? 'none' : 'calc(100vh - 32px)' }}>

            <div style={{ background: '#f7f7f7', border: '1px solid #ebebeb', borderRadius: 8, padding: 16, flexShrink: 0 }}>
              <input type="text" placeholder="Name this hamper (optional)" value={currentName}
                onChange={e => setCurrentName(sanitizeText(e.target.value, 80))}
                maxLength={80}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dddddd', fontSize: 12, fontFamily: "'Jost',sans-serif", outline: 'none', marginBottom: 12, background: '#fff' }} />

              {currentIds.length === 0
                ? <div style={{ fontSize: 12, color: '#929292', padding: '8px 0' }}>Expand a category on the left and tap products to add them here.</div>
                : (
                  <div style={{ marginBottom: 12 }}>
                    {currentIds.map(id => {
                      const it = itemById(id);
                      if (!it) return null;
                      return (
                        <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                          <span style={{ fontSize: 12, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{it.name}</span>
                          <span style={{ fontSize: 12, color: '#7d6233', fontWeight: 600, flexShrink: 0 }}>{toINR(it.price)}</span>
                          <button onClick={() => toggleCurrent(id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 13, padding: '0 2px', flexShrink: 0 }}>×</button>
                        </div>
                      );
                    })}
                  </div>
                )}

              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #ebebeb' }}>
                <span style={{ fontWeight: 600, fontSize: 12, color: '#6a6a6a' }}>Subtotal</span>
                <span style={{ fontSize: 15, fontWeight: 600, color: overBudget ? '#c0392b' : '#222222', fontFamily: "'Jost',sans-serif" }}>{toINR(currentTotal)}</span>
              </div>
              {budgetNum != null && (
                <div style={{ fontSize: 12, color: overBudget ? '#c0392b' : '#6a6a6a', marginTop: 4 }}>
                  {overBudget ? `₹${(currentTotal - budgetNum).toLocaleString('en-IN')} over your ${toINR(budgetNum)} budget` : `${toINR(budgetNum - currentTotal)} remaining of ${toINR(budgetNum)}`}
                </div>
              )}

              <button onClick={addHamperToList} disabled={!currentIds.length}
                style={{ ...primaryBtn, width: '100%', marginTop: 14, opacity: currentIds.length ? 1 : 0.4, cursor: currentIds.length ? 'pointer' : 'not-allowed' }}>
                + Add to My Combos
              </button>
            </div>

            {hampers.length > 0 && (
              <div style={{ overflowY: 'auto', minHeight: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#929292', marginBottom: 10, fontFamily: "'Jost',sans-serif" }}>My Combos ({hampers.length})</div>
                {hampers.map(h => {
                  const hOpen = !!expandedHampers[h.id];
                  const thumbs = h.productIds.map(id => itemById(id)).filter(Boolean);
                  return (
                    <div key={h.id} style={{ border: '1px solid #ebebeb', borderRadius: 6, marginBottom: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1, cursor: thumbs.length ? 'pointer' : 'default' }}
                          onClick={() => thumbs.length && toggleHamperExpand(h.id)}>
                          {thumbs.length > 0 && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#b8975a" strokeWidth="2.5" style={{ flexShrink: 0, transform: hOpen ? 'none' : 'rotate(-90deg)', transition: 'transform .2s' }}>
                              <polyline points="6 9 12 15 18 9"/>
                            </svg>
                          )}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 500, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.label}</span>
                              {h.isSaved && <span style={{ fontSize: 12, fontWeight: 800, color: '#2e7d32', background: 'rgba(46,125,50,0.1)', padding: '1px 6px', borderRadius: 10, flexShrink: 0 }}>Saved</span>}
                            </div>
                            <div style={{ fontSize: 12, color: '#999' }}>{h.productIds.length} item{h.productIds.length !== 1 ? 's' : ''} · {toINR(h.productIds.reduce((s, id) => s + Number(itemById(id)?.price || 0), 0))}</div>
                          </div>
                        </div>
                        <button onClick={() => removeHamper(h.id)}
                          title={h.isSaved ? 'Delete this combo from the Combo tab' : 'Remove'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 15, flexShrink: 0 }}>×</button>
                      </div>

                      {hOpen && thumbs.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 12px 10px' }}>
                          {thumbs.map((it, i) => (
                            <div key={i}
                              onMouseEnter={it.imageUrl ? (e => showHoverPreview(it.imageUrl, e)) : undefined}
                              onMouseMove={it.imageUrl ? moveHoverPreview : undefined}
                              onMouseLeave={it.imageUrl ? hideHoverPreview : undefined}
                              title={it.name}
                              style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', border: '1px solid #dddddd', flexShrink: 0, background: '#f7f7f7', cursor: it.imageUrl ? 'zoom-in' : 'default' }}>
                              {it.imageUrl && <img src={it.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {error && <div style={{ fontSize: 12, color: '#c0392b', flexShrink: 0 }}>{error}</div>}

            {(() => {
              const unsavedCount = hampers.filter(h => !h.isSaved).length;
              const nothingToSave = unsavedCount === 0;
              return (
                <button style={{ ...primaryBtn, opacity: (saving || nothingToSave) ? 0.5 : 1, cursor: saving ? 'wait' : (nothingToSave ? 'not-allowed' : 'pointer'), flexShrink: 0 }}
                  disabled={saving || nothingToSave} onClick={saveHampers}>
                  {saving ? 'Saving…' : nothingToSave ? (hampers.length ? 'All combos saved ✓' : 'Save to Combo Tab') : `Save ${unsavedCount} to Combo Tab`}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Hover-to-zoom preview — bigger version of whichever thumbnail the cursor is over */}
      {hoverPreview && (
        <div style={{
          position: 'fixed',
          left: Math.min(hoverPreview.x + 24, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 260),
          top: Math.min(hoverPreview.y + 24, (typeof window !== 'undefined' ? window.innerHeight : 800) - 260),
          zIndex: 10000, pointerEvents: 'none', width: 240, height: 240, borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,0,0,0.4)', border: '3px solid #fff', background: '#f7f7f7',
        }}>
          <img src={hoverPreview.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT BENTO — existing component below
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// SELECTED ITEMS GROUPS — collapsible category sections in the Shortlist tab
// Combos first, then products grouped by category, each section collapsible.
// ─────────────────────────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════
// PRODUCT CATALOGUE — editorial grid
// Category rail with counts, one refine row (sort · price · shortlisted only),
// serif category headings and uniform 4:5 product tiles. Product photos sit on a
// warm studio tone (multiply blend, so white-background shots melt into it).
// Hovering a tile shows its second photo; clicking opens the full ProductViewer.
// ═════════════════════════════════════════════════════════════════════════════
// Airbnb-style palette: white page, near-black ink, soft grey lines, gold kept as the brand accent.
const PX = {
  navy: '#222222', gold: '#b8975a', goldText: '#7d6233', ink: '#222222', body: '#222222',
  sub: '#484848', muted: '#6a6a6a', studio: '#f7f7f7', line: '#ebebeb', line2: '#dddddd',
  serif: "'Cormorant Garamond',Georgia,serif", sans: "'Jost',sans-serif",
};
const pxEyebrow = { fontFamily: PX.sans, fontSize: 12, fontWeight: 600, color: PX.ink };
const pxField = { height: 40, padding: '0 14px', border: `1px solid ${PX.line2}`, background: '#fff', fontFamily: PX.sans, fontSize: 14, color: PX.ink, outline: 'none', boxSizing: 'border-box', borderRadius: 8 };
const pxHeart = (filled) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M12 20.5s-7.5-4.6-7.5-10.3A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.6c0 5.7-7.5 10.3-7.5 10.3z" />
  </svg>
);
const pxChevron = (open) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"
    style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .2s' }}><path d="M6 9l6 6 6-6" /></svg>
);

/**
 * One product pin — Pinterest-style. The image keeps its own shape (height from
 * the photo's aspect ratio, reported back so the masonry can balance columns),
 * rounded corners, a soft scrim on hover, a heart to shortlist, and a
 * short caption underneath.
 */
const PIN_RATIO_MIN = 0.75;   // wide photos never get flatter than 4:3
const PIN_RATIO_MAX = 1.6;    // tall photos never taller than 5:8
const clampRatio = r => Math.min(PIN_RATIO_MAX, Math.max(PIN_RATIO_MIN, r));

const PIN_THUMBS = 4;   // thumbnails shown under the photo before "+N"

const ProductTile = ({ item, price, loved, onToggleWish, onOpen, onRatio, ratio = 1.25, isMobile, slug, delay = 0 }) => {
  const [hover, setHover] = React.useState(false);
  const [shown, setShown] = React.useState(0);   // photo shown in the pin (thumbnail hover / tap)
  const photos = [item.imageUrl, ...(item.additionalImages || [])].filter(Boolean);
  const hasVideo = productHasVideo(item);
  const current = photos[shown] || photos[0];
  const showActions = hover || isMobile;
  const radius = isMobile ? 14 : 18;

  return (
    <article
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 10, animation: `obsidianFadeIn .5s ease ${delay}s both`, minWidth: 0 }}>
      <div style={{ position: 'relative', aspectRatio: `1 / ${ratio}`, background: PX.studio, borderRadius: radius, overflow: 'hidden', transition: 'aspect-ratio .3s ease' }}>
        <button type="button" onClick={() => onOpen(shown)} aria-label={`View ${item.name}`}
          style={{ position: 'absolute', inset: 0, padding: 0, border: 'none', background: 'transparent', cursor: photos.length ? 'zoom-in' : 'default' }}>
          {current ? (
            <img src={current} alt={item.name} loading="lazy"
              onLoad={e => { if (shown !== 0) return; const { naturalWidth: w, naturalHeight: h } = e.currentTarget; if (w && h) onRatio?.(String(item._id), clampRatio(h / w)); }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply', transition: 'transform .8s cubic-bezier(.2,.7,.2,1)', transform: hover ? 'scale(1.03)' : 'none' }} />
          ) : (
            <span style={{ ...pxEyebrow, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Image coming soon</span>
          )}
        </button>

        {/* Hover scrim (pointer-events off so the image button underneath stays clickable) */}
        {!isMobile && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.28)', opacity: hover ? 1 : 0, transition: 'opacity .25s ease', pointerEvents: 'none' }} />
        )}

        {/* Shortlist — heart only, top right */}
        <button type="button" onClick={e => { e.stopPropagation(); onToggleWish(String(item._id)); }}
          aria-pressed={loved} aria-label={loved ? `Remove ${item.name} from shortlist` : `Shortlist ${item.name}`}
          title={loved ? 'Shortlisted' : 'Shortlist'}
          style={{
            position: 'absolute', top: isMobile ? 8 : 12, right: isMobile ? 8 : 12, zIndex: 2,
            width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, padding: 0,
            borderRadius: '50%', border: 'none', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: loved ? PX.navy : 'rgba(255,255,255,0.92)', color: loved ? '#d9c49a' : PX.ink,
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            opacity: showActions || loved ? 1 : 0.85, transition: 'opacity .2s ease',
          }}>
          {pxHeart(loved)}
        </button>
      </div>

      {photos.length > 1 && (
        <div role="group" aria-label={`${item.name} photos`} style={{ display: 'flex', gap: isMobile ? 5 : 6, marginTop: isMobile ? -2 : -4 }}>
          {photos.slice(0, PIN_THUMBS).map((src, i) => {
            const more = i === PIN_THUMBS - 1 && photos.length > PIN_THUMBS ? photos.length - PIN_THUMBS + 1 : 0;
            return (
              <button key={i} type="button" aria-label={more ? `See all ${photos.length} photos` : `Photo ${i + 1}`} aria-pressed={shown === i}
                onMouseEnter={() => !isMobile && setShown(i)} onFocus={() => setShown(i)}
                onClick={() => (more ? onOpen(i) : (isMobile && shown !== i ? setShown(i) : onOpen(i)))}
                style={{ position: 'relative', flex: `0 0 ${isMobile ? 34 : 44}px`, height: isMobile ? 34 : 44, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: PX.studio, border: `1.5px solid ${shown === i ? PX.ink : 'transparent'}` }}>
                <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply', display: 'block' }} />
                {more > 0 && <span style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', fontFamily: PX.sans, fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+{more}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
        <h3 style={{ margin: 0, fontFamily: PX.serif, fontWeight: 500, fontSize: isMobile ? 15 : 17, lineHeight: 1.25, color: PX.ink, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          <button type="button" onClick={() => onOpen(0)} style={{ all: 'unset', cursor: 'pointer' }}>{item.name}</button>
        </h3>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontFamily: PX.sans, fontSize: isMobile ? 12 : 13, color: PX.muted }}>
          <span style={{ color: PX.ink, fontVariantNumeric: 'tabular-nums' }}>{toINR(price)}</span>
          {item.subCategory && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {item.subCategory}</span>}
          {photos.length > 1 && <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: isMobile ? 12 : 12 }}>{photos.length} photos</span>}
        </div>
      </div>

      {/* Product video — shown right under the product, as clients are used to */}
      {hasVideo && (
        <div style={{ marginTop: -4 }}>
          <div style={{ ...pxEyebrow, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill={PX.goldText} aria-hidden="true"><polygon points="6 4 20 12 6 20 6 4" /></svg>
            Product video
          </div>
          <ProductVideoEmbed item={item} slug={slug} />
        </div>
      )}
    </article>
  );
};

const ProductBento = ({ items, onZoom, wishlisted = new Set(), onToggleWish = () => { }, portal = null, combos = [], onBuildHamper = null, slug }) => {
  const [activeCategory, setActiveCategory] = React.useState(null);
  const [activeSubCat, setActiveSubCat] = React.useState(null);
  const [viewer, setViewer] = React.useState(null); // { item, idx } — full-screen product viewer
  const [collapsedCats, setCollapsedCats] = React.useState({});
  const [minPrice, setMinPrice] = React.useState('');
  const [maxPrice, setMaxPrice] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('none'); // 'none' | 'asc' | 'desc'
  const [onlyShortlisted, setOnlyShortlisted] = React.useState(false);
  // Per-category price range + sort. Also handed to the Hamper Builder as its starting range.
  const [catFilters, setCatFilters] = React.useState({}); // { [cat]: { min, max, sort } }
  const [refineOpen, setRefineOpen] = React.useState({});
  const getCatFilter = React.useCallback(cat => catFilters[cat] || { min: '', max: '', sort: 'none' }, [catFilters]);
  const setCatFilter = (cat, patch) => setCatFilters(prev => ({ ...prev, [cat]: { ...(prev[cat] || { min: '', max: '', sort: 'none' }), ...patch } }));
  const isMobile = useMobile();
  const isTablet = useTablet();

  const toggleCat = (cat) => setCollapsedCats(p => ({ ...p, [cat]: !p[cat] }));
  const getPrice = i => Number(portal?.calculatorState?.[i._id]?.priceOverride ?? i.price ?? 0);

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];
  const countIn = cat => items.filter(i => i.category === cat).length;
  const subCats = activeCategory && activeCategory !== 'Combo'
    ? [...new Set(items.filter(i => i.category === activeCategory).map(i => i.subCategory).filter(Boolean))]
    : [];
  const shortlistedCount = items.filter(i => wishlisted.has(String(i._id))).length + combos.filter(c => wishlisted.has(String(c._id))).length;

  const filtered = items.filter(i => {
    if (activeCategory === 'Combo') return false;
    if (onlyShortlisted && !wishlisted.has(String(i._id))) return false;
    const cOk = !activeCategory || i.category === activeCategory;
    const sOk = !activeSubCat || i.subCategory === activeSubCat;
    const price = getPrice(i);
    const minOk = minPrice === '' || isNaN(Number(minPrice)) || price >= Number(minPrice);
    const maxOk = maxPrice === '' || isNaN(Number(maxPrice)) || price <= Number(maxPrice);
    return cOk && sOk && minOk && maxOk;
  });
  if (sortOrder === 'asc') filtered.sort((a, b) => getPrice(a) - getPrice(b));
  else if (sortOrder === 'desc') filtered.sort((a, b) => getPrice(b) - getPrice(a));

  const groupMap = new Map();
  filtered.forEach(item => { const c = item.category || 'Other'; if (!groupMap.has(c)) groupMap.set(c, []); groupMap.get(c).push(item); });
  const groups = Array.from(groupMap.entries()).map(([cat, its]) => ({ cat, items: its }));

  const visibleCombos = onlyShortlisted ? combos.filter(c => wishlisted.has(String(c._id))) : combos;
  const showCombos = visibleCombos.length > 0 && (!activeCategory || activeCategory === 'Combo');
  const filtersOn = minPrice !== '' || maxPrice !== '' || sortOrder !== 'none' || onlyShortlisted;
  // Pinterest-style masonry: photo aspect ratios are learned as images load.
  const [ratios, setRatios] = React.useState({});
  const onRatio = React.useCallback((id, r) => setRatios(p => (Math.abs((p[id] || 0) - r) < 0.01 ? p : { ...p, [id]: r })), []);
  const pinColumns = isMobile ? 2 : isTablet ? 3 : 5;
  const pinGap = isMobile ? 10 : 18;
  // Card height in column widths: photo + caption (2 lines of name + price).
  const estimatePin = item => (ratios[String(item._id)] || 1.25) + (isMobile ? 0.42 : 0.3)
    + ([item.imageUrl, ...(item.additionalImages || [])].filter(Boolean).length > 1 ? (isMobile ? 0.22 : 0.2) : 0)
    + pinVideoHeight(item);

  const rail = [{ k: null, l: 'All', n: items.length + combos.length }]
    .concat(categories.map(c => ({ k: c, l: c, n: countIn(c) })))
    .concat(combos.length ? [{ k: 'Combo', l: 'Bundles', n: combos.length }] : []);

  return (
    <div>
      {viewer && (
        <ProductViewer
          item={viewer.item}
          startIdx={viewer.idx}
          price={getPrice(viewer.item)}
          slug={slug || portal?.slug}
          loved={wishlisted.has(String(viewer.item._id))}
          onToggleWish={onToggleWish}
          onClose={() => setViewer(null)}
        />
      )}

      {/* ── Category rail + refine row. Sticky under nav + tabs on iPad/laptop. ── */}
      <div style={{ position: isMobile ? 'static' : 'sticky', top: 116, zIndex: 30, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderBottom: `1px solid ${PX.line}`, margin: isMobile ? '0 -16px 28px' : '0 0 48px', padding: isMobile ? '0 16px' : 0 }}>
        <nav aria-label="Categories" style={{ display: 'flex', gap: isMobile ? 22 : 34, overflowX: 'auto', scrollbarWidth: 'none', borderBottom: `1px solid ${PX.line}` }}>
          {rail.map(r => {
            const on = activeCategory === r.k;
            return (
              <button key={r.l} type="button" aria-pressed={on}
                onClick={() => { setActiveCategory(on && r.k ? null : r.k); setActiveSubCat(null); }}
                style={{ flex: 'none', padding: isMobile ? '14px 0 12px' : '18px 0 14px', background: 'none', border: 'none', borderBottom: `2px solid ${on ? PX.ink : 'transparent'}`, marginBottom: -1, cursor: 'pointer', fontFamily: PX.sans, fontSize: 14, fontWeight: on ? 600 : 500, color: on ? PX.ink : PX.muted, whiteSpace: 'nowrap', transition: 'color .2s' }}>
                {r.l} <span style={{ fontSize: 12, fontWeight: 500, color: PX.muted, marginLeft: 4 }}>{r.n}</span>
              </button>
            );
          })}
        </nav>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: isMobile ? 10 : 16, padding: isMobile ? '14px 0 16px' : '20px 0' }}>
          {/* Airbnb-style search pill: Sort | Price | Shortlisted */}
          <div style={{ ...AIR.bar, flex: isMobile ? '1 1 100%' : '0 1 auto', minWidth: 0 }}>
            <AirSelect label="Sort" value={sortOrder} onChange={setSortOrder} pad={isMobile ? '10px 14px 10px 20px' : undefined} style={{ flex: isMobile ? '1.15 1 0' : '0 0 210px' }}
              options={[{ v: 'none', l: 'Curated order' }, { v: 'asc', l: 'Price: low to high' }, { v: 'desc', l: 'Price: high to low' }]} />
            <span style={AIR.divider} />
            <AirPriceSeg min={minPrice} max={maxPrice} onMin={setMinPrice} onMax={setMaxPrice} align={isMobile ? 'right' : 'left'}
              pad={isMobile ? '10px 16px' : undefined} style={{ flex: isMobile ? '1 1 0' : '0 0 230px' }} />
            {!isMobile && <span style={AIR.divider} />}
            {!isMobile && (
              <button type="button" aria-pressed={onlyShortlisted} onClick={() => setOnlyShortlisted(v => !v)} className="air-seg"
                style={{ ...AIR.seg, border: 'none', background: 'transparent', textAlign: 'left', paddingRight: 8 }}>
                <span style={AIR.segLabel}>Shortlisted</span>
                <span style={{ fontFamily: PX.sans, fontSize: 14, color: onlyShortlisted ? PX.ink : PX.muted, whiteSpace: 'nowrap' }}>{onlyShortlisted ? 'Showing only these' : `${shortlistedCount} saved`}</span>
              </button>
            )}
            {!isMobile && (
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px 0 4px' }}>
                <button type="button" aria-label={onlyShortlisted ? 'Show everything' : 'Show shortlisted only'} onClick={() => setOnlyShortlisted(v => !v)}
                  style={{ width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: onlyShortlisted ? PX.ink : GOLD_GRAD, color: '#fff' }}>
                  {pxHeart(onlyShortlisted)}
                </button>
              </span>
            )}
          </div>
          {isMobile && (
            <button type="button" aria-pressed={onlyShortlisted} onClick={() => setOnlyShortlisted(v => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px', borderRadius: 40, cursor: 'pointer', fontFamily: PX.sans, fontSize: 14, fontWeight: 500, border: `1px solid ${onlyShortlisted ? PX.ink : PX.line2}`, background: onlyShortlisted ? PX.ink : '#fff', color: onlyShortlisted ? '#fff' : PX.ink }}>
              <span style={{ display: 'flex' }}>{pxHeart(onlyShortlisted)}</span>
              Shortlisted · {shortlistedCount}
            </button>
          )}
          {filtersOn && (
            <button type="button" onClick={() => { setMinPrice(''); setMaxPrice(''); setSortOrder('none'); setOnlyShortlisted(false); }}
              style={{ height: 40, padding: '0 6px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: PX.sans, fontSize: 14, fontWeight: 600, color: PX.ink, textDecoration: 'underline', textUnderlineOffset: 3 }}>
              Clear all
            </button>
          )}
          {onBuildHamper && (
            <button type="button" onClick={() => onBuildHamper(catFilters)}
              style={{ ...AIR.btn('primary'), marginLeft: isMobile ? 0 : 'auto', width: isMobile ? 'auto' : 'auto', flex: isMobile ? '1 1 auto' : 'none', height: isMobile ? 40 : 48 }}>
              Build a hamper
            </button>
          )}
        </div>

        {subCats.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingBottom: 14 }}>
            {[null, ...subCats].map(sc => {
              const on = activeSubCat === sc;
              return (
                <button key={sc || 'all'} type="button" aria-pressed={on} onClick={() => setActiveSubCat(sc)}
                  style={{ minHeight: 34, padding: '0 14px', borderRadius: 17, border: `1px solid ${on ? PX.ink : PX.line2}`, background: on ? PX.ink : 'transparent', color: on ? '#fff' : PX.sub, fontFamily: PX.sans, fontSize: 12, cursor: 'pointer' }}>
                  {sc || `All ${activeCategory}`}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── "All": one continuous Pinterest-style feed ── */}
      {!activeCategory && filtered.length > 0 && (() => {
        // Curated order keeps categories (and their sub-categories) together; a price sort mixes them.
        let feed = filtered;
        if (sortOrder === 'none') {
          const catOrder = groups.map(g => g.cat);
          const subOrder = [];
          filtered.forEach(it => { const k = `${it.category || 'Other'}|${it.subCategory || ''}`; if (!subOrder.includes(k)) subOrder.push(k); });
          feed = [...filtered].sort((x, y) =>
            (catOrder.indexOf(x.category || 'Other') - catOrder.indexOf(y.category || 'Other'))
            || (subOrder.indexOf(`${x.category || 'Other'}|${x.subCategory || ''}`) - subOrder.indexOf(`${y.category || 'Other'}|${y.subCategory || ''}`)));
        }
        return (
          <section aria-label="All pieces" style={{ marginBottom: isMobile ? 56 : 88 }}>
            <Masonry
              items={feed}
              columns={pinColumns}
              gap={pinGap}
              estimate={estimatePin}
              renderItem={(item, idx) => (
                <ProductTile key={item._id} item={item} price={getPrice(item)} isMobile={isMobile}
                  loved={wishlisted.has(String(item._id))} onToggleWish={onToggleWish}
                  ratio={ratios[String(item._id)] || 1.25} onRatio={onRatio} slug={slug || portal?.slug}
                  onOpen={i => setViewer({ item, idx: i })}
                  delay={Math.min(idx * 0.02, 0.4)} />
              )}
            />
          </section>
        );
      })()}

      {/* ── A category picked: its section (heading, Refine, masonry) ── */}
      {activeCategory && groups.map((group, gi) => {
        const isCatCollapsed = !!collapsedCats[group.cat];
        const cf = getCatFilter(group.cat);
        let catItems = group.items.filter(i => {
          const price = getPrice(i);
          const minOk = cf.min === '' || isNaN(Number(cf.min)) || price >= Number(cf.min);
          const maxOk = cf.max === '' || isNaN(Number(cf.max)) || price <= Number(cf.max);
          return minOk && maxOk;
        });
        if (cf.sort === 'asc') catItems = [...catItems].sort((a, b) => getPrice(a) - getPrice(b));
        else if (cf.sort === 'desc') catItems = [...catItems].sort((a, b) => getPrice(b) - getPrice(a));
        // Keep sub-categories together (in order of first appearance) unless a price sort is on.
        if (sortOrder === 'none' && cf.sort === 'none') {
          const subOrder = [];
          catItems.forEach(it => { const s = it.subCategory || ''; if (!subOrder.includes(s)) subOrder.push(s); });
          catItems = [...catItems].sort((a, b) => subOrder.indexOf(a.subCategory || '') - subOrder.indexOf(b.subCategory || ''));
        }
        const subs = [...new Set(catItems.map(i => i.subCategory).filter(Boolean))];
        const prices = catItems.map(getPrice).filter(p => p > 0);
        const range = prices.length ? (Math.min(...prices) === Math.max(...prices) ? toINR(prices[0]) : `${toINR(Math.min(...prices))} – ${toINR(Math.max(...prices))}`) : '';
        const catRefine = !!refineOpen[group.cat];
        const catFilterOn = cf.min !== '' || cf.max !== '' || cf.sort !== 'none';
        return (
          <section key={group.cat} aria-labelledby={`cat-${gi}`} style={{ marginBottom: isMobile ? 56 : 88 }}>
            <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: isMobile ? 10 : 20, paddingBottom: isMobile ? 14 : 18, marginBottom: isCatCollapsed ? 0 : (isMobile ? 20 : 32), borderBottom: `1px solid ${PX.line2}` }}>
              <button type="button" onClick={() => toggleCat(group.cat)} aria-expanded={!isCatCollapsed}
                style={{ display: 'flex', alignItems: 'baseline', gap: 14, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: PX.ink, minWidth: 0 }}>
                <span style={{ ...pxEyebrow, color: PX.goldText, fontVariantNumeric: 'tabular-nums' }}>{String(gi + 1).padStart(2, '0')}</span>
                <h2 id={`cat-${gi}`} style={{ margin: 0, fontFamily: PX.serif, fontWeight: 400, fontSize: isMobile ? 30 : 42, lineHeight: 1, color: PX.ink }}>{group.cat}</h2>
                <span style={{ color: PX.muted, alignSelf: 'center', display: 'flex' }}>{pxChevron(!isCatCollapsed)}</span>
              </button>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: isMobile ? '4px 12px' : '4px 20px', fontFamily: PX.sans, fontSize: 13, color: PX.muted }}>
                {!isMobile && subs.length > 0 && <span>{subs.join(' · ')}</span>}
                <span>{catItems.length} piece{catItems.length !== 1 ? 's' : ''}{range ? ` · ${range}` : ''}</span>
                <button type="button" onClick={() => setRefineOpen(p => ({ ...p, [group.cat]: !p[group.cat] }))} aria-expanded={catRefine}
                  style={{ background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', fontFamily: PX.sans, fontSize: 12, color: catFilterOn ? PX.goldText : PX.sub, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  {catFilterOn ? 'Refined' : 'Refine'}
                </button>
              </div>
            </header>

            {catRefine && !isCatCollapsed && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: isMobile ? '-8px 0 20px' : '-16px 0 28px' }}>
                <AirPriceRange size="sm" ariaPrefix={group.cat} min={cf.min} max={cf.max} style={{ width: 240 }}
                  onMin={v => setCatFilter(group.cat, { min: v })} onMax={v => setCatFilter(group.cat, { max: v })} />
                <AirSelect boxed value={cf.sort} onChange={v => setCatFilter(group.cat, { sort: v })} style={{ width: 200 }}
                  options={[{ v: 'none', l: 'Curated order' }, { v: 'asc', l: 'Price: low to high' }, { v: 'desc', l: 'Price: high to low' }]} />
                {catFilterOn && (
                  <button type="button" onClick={() => setCatFilter(group.cat, { min: '', max: '', sort: 'none' })}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: PX.sans, fontSize: 12, color: PX.goldText, textDecoration: 'underline', padding: '8px 4px' }}>Clear</button>
                )}
              </div>
            )}

            {!isCatCollapsed && (
              catItems.length > 0 ? (
                <Masonry
                  items={catItems}
                  columns={pinColumns}
                  gap={pinGap}
                  estimate={estimatePin}
                  renderItem={(item, idx) => (
                    <ProductTile key={item._id} item={item} price={getPrice(item)} isMobile={isMobile}
                      loved={wishlisted.has(String(item._id))} onToggleWish={onToggleWish}
                      ratio={ratios[String(item._id)] || 1.25} onRatio={onRatio} slug={slug || portal?.slug}
                      onOpen={i => setViewer({ item, idx: i })}
                      delay={Math.min(idx * 0.03, 0.4)} />
                  )}
                />
              ) : (
                <p style={{ fontFamily: PX.sans, fontSize: 14, color: PX.muted, margin: 0 }}>Nothing in this price range.</p>
              )
            )}
          </section>
        );
      })}

      {filtered.length === 0 && !showCombos && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <h3 style={{ fontFamily: PX.serif, fontWeight: 400, fontSize: 28, color: PX.ink, margin: '0 0 8px' }}>{onlyShortlisted ? 'Nothing shortlisted yet' : 'No pieces match'}</h3>
          <p style={{ fontFamily: PX.sans, fontSize: 14, color: PX.muted, margin: 0 }}>{onlyShortlisted ? 'Tap the heart on any piece to keep it here.' : 'Try a different category or price range.'}</p>
        </div>
      )}

      {/* ── Bundles (combos) ── */}
      {showCombos && (() => {
        const isComboCollapsed = !!collapsedCats['__combo__'];
        return (
          <section aria-labelledby="cat-bundles" style={{ marginBottom: isMobile ? 56 : 88 }}>
            <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, paddingBottom: isMobile ? 14 : 18, marginBottom: isComboCollapsed ? 0 : (isMobile ? 20 : 32), borderBottom: `1px solid ${PX.line2}` }}>
              <button type="button" onClick={() => toggleCat('__combo__')} aria-expanded={!isComboCollapsed}
                style={{ display: 'flex', alignItems: 'baseline', gap: 14, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: PX.ink }}>
                <span style={{ ...pxEyebrow, color: PX.goldText }}>{String(groups.length + 1).padStart(2, '0')}</span>
                <h2 id="cat-bundles" style={{ margin: 0, fontFamily: PX.serif, fontWeight: 400, fontSize: isMobile ? 30 : 42, lineHeight: 1 }}>Bundles</h2>
                <span style={{ color: PX.muted, alignSelf: 'center', display: 'flex' }}>{pxChevron(!isComboCollapsed)}</span>
              </button>
              <span style={{ fontFamily: PX.sans, fontSize: 13, color: PX.muted }}>{visibleCombos.length} curated set{visibleCombos.length !== 1 ? 's' : ''}</span>
            </header>
            {!isComboCollapsed && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, minmax(0,1fr))' : 'repeat(3, minmax(0,1fr))', gap: isMobile ? 24 : 32 }}>
                {visibleCombos.map((combo, idx) => {
                  const loved = wishlisted.has(String(combo._id));
                  return (
                    <article key={combo._id} style={{ display: 'flex', flexDirection: 'column', gap: 14, animation: `obsidianFadeIn .5s ease ${Math.min(idx * 0.05, 0.4)}s both` }}>
                      <div style={{ position: 'relative', background: PX.studio }}>
                        <ComboThumbGallery combo={combo} onZoom={onZoom} maxHeight={isMobile ? 220 : 280} minHeight={isMobile ? 160 : 200} />
                        <button type="button" onClick={e => { e.stopPropagation(); onToggleWish(String(combo._id)); }}
                          aria-pressed={loved} aria-label={loved ? 'Remove bundle from shortlist' : 'Shortlist this bundle'}
                          style={{ position: 'absolute', top: 12, right: 12, width: 40, height: 40, borderRadius: '50%', border: loved ? 'none' : `1px solid ${PX.line}`, background: loved ? PX.navy : 'rgba(255,255,255,0.92)', color: loved ? '#d9c49a' : PX.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}>
                          {pxHeart(loved)}
                        </button>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                        <h3 style={{ margin: 0, fontFamily: PX.serif, fontWeight: 500, fontSize: 21, color: PX.ink, lineHeight: 1.2 }}>{combo.label || `Bundle ${idx + 1}`}</h3>
                        <span style={{ fontFamily: PX.sans, fontSize: 14, color: PX.ink, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{toINR(combo.totalPrice)}</span>
                      </div>
                      <p style={{ margin: 0, fontFamily: PX.sans, fontSize: 13, color: PX.muted, lineHeight: 1.6 }}>
                        {(combo.items || []).map(i => i.subCategory || i.name).join(' · ')}
                      </p>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        );
      })()}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// OFFSITE OPTIONS — one unified view for Day Outing and Night Stay
// A property can offer rooms, day packages or both; the "Plans & pricing" block
// shows whichever exist (with a switch when both do). "Getting there", facts and
// amenities come from item.info, which the API joins live from the Property.
// ═════════════════════════════════════════════════════════════════════════════
const OX = {
  navy: '#222222', gold: '#b8975a', goldText: '#7d6233', goldSoft: '#d9c49a',
  ink: '#222222', body: '#222222', sub: '#484848', muted: '#6a6a6a',
  cream: '#f7f7f7', panel: '#f7f7f7', line: '#ebebeb', line2: '#dddddd',
  serif: "'Cormorant Garamond',Georgia,serif", sans: "'Jost',sans-serif",
};
const oxINR = v => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const oxKm = km => (km == null ? '' : `${km < 10 ? Math.round(km * 10) / 10 : Math.round(km)} km`);
const oxMins = m => {
  if (m == null) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60); const r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};
const oxEyebrow = { fontFamily: OX.sans, fontSize: 14, fontWeight: 600, color: OX.ink, margin: 0 };
const oxChip = { fontFamily: OX.sans, fontSize: 13, fontWeight: 500, color: OX.ink, background: '#f7f7f7', borderRadius: 40, padding: '5px 12px', display: 'inline-block' };
const oxBtnGhost = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, padding: '0 18px', background: '#fff', border: `1px solid ${OX.ink}`, borderRadius: 8, fontFamily: OX.sans, fontSize: 14, fontWeight: 600, color: OX.ink, cursor: 'pointer', textDecoration: 'none' };

const OxIcon = {
  plane: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M2.5 19h19M3 13.5l4.5 1.5 4-6 2 .6-1.6 6.6 4.6 1.5 1.5-2.2 1.6.5-.6 3.6L3.6 15z" /></svg>,
  office: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M4 21V5l8-2v18M12 8l8 2v11M2 21h20M7 8h2M7 12h2M7 16h2M15 13h2M15 17h2" /></svg>,
  city: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M3 21V9l5-3v15M8 21V4l8 4v13M16 21V11l5 2v8M2 21h20" /></svg>,
  home: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M3 20h18M5 20V10l7-5 7 5v10M10 20v-5h4v5" /></svg>,
  arrow: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>,
  chev: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill={OX.gold} aria-hidden="true"><polygon points="12 3 14.8 9 21 9.6 16.3 13.8 17.7 20 12 16.8 6.3 20 7.7 13.8 3 9.6 9.2 9" /></svg>,
};

/** What a property offers, from its prices (type is only a fallback for unpriced items). */
const offsiteOffers = (item) => {
  const roomPrices = [item.singlePrice, item.doublePrice, item.triplePrice, item.quadPrice]
    .concat((item.roomCategories || []).flatMap(c => [c.singlePrice, c.doublePrice, c.triplePrice]));
  const stay = roomPrices.some(p => p > 0);
  const day = (item.dayPackages || []).some(p => p.sellingPrice > 0 || p.name) || item.packagePrice > 0;
  if (!stay && !day) return { stay: item.type === 'Night Stay', day: item.type !== 'Night Stay' };
  return { stay, day };
};

const minPositive = (arr) => { const v = arr.filter(n => n > 0); return v.length ? Math.min(...v) : null; };

/**
 * Room rows for one property on this order. Occupancies (and whole room
 * categories) the team switched off in the editor are dropped — their price
 * becomes 0, which the table and "from" prices already skip.
 */
const offsiteRoomRows = (item, itemCalc = {}) => {
  const off = itemCalc.disabledRooms || {};
  const keep = (key, price) => (off[key] ? 0 : (price || 0));
  const rows = [];
  if ([item.singlePrice, item.doublePrice, item.triplePrice, item.quadPrice].some(p => p > 0)) {
    rows.push({
      key: 'base', name: (item.roomCategories || []).length ? 'Standard' : 'Room',
      s: keep('single', item.singlePrice), d: keep('double', item.doublePrice), t: keep('triple', item.triplePrice), q: keep('quad', item.quadPrice),
    });
  }
  (item.roomCategories || []).forEach(c => {
    const id = c._id || c.name;
    if (off[`cat_${id}`]) return;
    rows.push({ key: id, name: c.name, s: keep(`cat_${id}_single`, c.singlePrice), d: keep(`cat_${id}_double`, c.doublePrice), t: keep(`cat_${id}_triple`, c.triplePrice), q: 0 });
  });
  return rows.filter(r => r.s > 0 || r.d > 0 || r.t > 0 || r.q > 0);
};

/** The leg shown beside the airport: the client's office when the team set one, else the nearest city. */
const offsiteNearLeg = (access = {}) => (access.office
  ? { k: 'From your office', short: 'Your office', name: access.office.name, leg: access.office, hi: true }
  : access.city ? { k: 'Nearest city', short: access.city.name, name: access.city.name, leg: access.city } : null);

const OffsiteCards = ({ items, onZoom, portal, onOpenCalculator, onOpenChat }) => {
  const isMobile = useMobile();
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [open, setOpen] = React.useState(() => ({ [String(items[0]?._id)]: true }));

  const withOffers = items.map(i => ({ item: i, offers: offsiteOffers(i) }));
  const dayCount = withOffers.filter(x => x.offers.day).length;
  const stayCount = withOffers.filter(x => x.offers.stay).length;
  const showFilter = dayCount > 0 && stayCount > 0 && items.length > 1;

  let visible = withOffers.filter(x => typeFilter === 'all' || (typeFilter === 'day' ? x.offers.day : x.offers.stay));
  if (portal?.sortByDriveTime) {
    const t = x => x.item.info?.access?.office?.mins ?? x.item.info?.access?.origin?.mins ?? Infinity;
    visible = [...visible].sort((a, b) => t(a) - t(b));
  }

  const filters = [
    { k: 'all', l: `All · ${items.length}` },
    { k: 'day', l: `Day outing · ${dayCount}` },
    { k: 'night', l: `Night stay · ${stayCount}` },
  ];

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 24 }}>
        {showFilter ? (
          <div role="group" aria-label="Filter options" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {filters.map(f => (
              <button key={f.k} type="button" onClick={() => setTypeFilter(f.k)} aria-pressed={typeFilter === f.k}
                style={{ minHeight: 40, padding: '0 18px', borderRadius: 40, border: `1px solid ${typeFilter === f.k ? OX.ink : OX.line2}`, cursor: 'pointer', fontFamily: OX.sans, fontWeight: 500, fontSize: 14, background: typeFilter === f.k ? OX.ink : '#fff', color: typeFilter === f.k ? '#fff' : OX.ink, transition: 'background .2s' }}>
                {f.l}
              </button>
            ))}
          </div>
        ) : (
          <p style={{ fontFamily: OX.sans, fontSize: 13, color: OX.muted, margin: 0 }}>{items.length} option{items.length !== 1 ? 's' : ''} selected for you</p>
        )}
        {(() => {
          const offices = [...new Set(items.map(i => i.info?.access?.office?.name).filter(Boolean))];
          const from = offices.length === 1 ? offices[0] : offices.length > 1 ? 'your office' : portal?.originCity;
          return from && (
            <p style={{ fontFamily: OX.sans, fontSize: 13, color: OX.muted, margin: 0 }}>
              Drive times from <span style={{ color: OX.ink }}>{from}</span>{portal.sortByDriveTime ? ' · nearest first' : ''}
            </p>
          );
        })()}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {visible.map(({ item, offers }) => {
          const idx = items.indexOf(item);
          const key = String(item._id);
          return open[key]
            ? <OffsiteOption key={key} item={item} offers={offers} idx={idx} portal={portal} onZoom={onZoom} isMobile={isMobile}
                onOpenCalculator={onOpenCalculator} onOpenChat={onOpenChat} onCollapse={() => setOpen(p => ({ ...p, [key]: false }))} />
            : <OffsiteSummary key={key} item={item} offers={offers} idx={idx} portal={portal} isMobile={isMobile}
                onExpand={() => setOpen(p => ({ ...p, [key]: true }))} />;
        })}
      </div>
    </div>
  );
};

// ── Photo block ───────────────────────────────────────────────────────────────
const OffsitePhoto = ({ src, alt, label, style, onClick, tone = '#ebebeb' }) => (
  <div onClick={onClick} style={{ position: 'relative', overflow: 'hidden', background: tone, cursor: src ? 'zoom-in' : 'default', ...style }}>
    {src
      ? <img src={src} alt={alt} className="img-hover" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .6s ease' }} />
      : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.muted }}>{label || 'Photo coming soon'}</div>}
  </div>
);

const OptionBadge = ({ idx }) => (
  <span style={{ position: 'absolute', top: 16, left: 16, background: '#fff', color: '#222222', borderRadius: 40, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', fontFamily: OX.sans, fontWeight: 600, fontSize: 12, padding: '6px 12px' }}>
    Option {String(idx + 1).padStart(2, '0')}
  </span>
);

const OfferChips = ({ offers, info }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
    {offers.stay && <span style={oxChip}>Night stay</span>}
    {offers.day && <span style={oxChip}>Day outing</span>}
    {(info?.facts?.propertyType || info?.facts?.area) && (
      <span style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.goldText, padding: '5px 0' }}>
        {[info.facts.propertyType, info.facts.area].filter(Boolean).join(' · ')}
      </span>
    )}
  </div>
);

// ── Collapsed option ──────────────────────────────────────────────────────────
const OffsiteSummary = ({ item, offers, idx, portal, isMobile, onExpand }) => {
  const a = item.info?.access || {};
  const near = offsiteNearLeg(a);
  const cells = [
    a.airport && { k: a.airport.code ? `${a.airport.code} airport` : 'Airport', v: [oxKm(a.airport.km), oxMins(a.airport.mins)].filter(Boolean).join(' · ') },
    near && { k: a.office ? 'From your office' : near.name, v: [oxKm(near.leg.km), oxMins(near.leg.mins)].filter(Boolean).join(' · ') || near.name, hi: near.hi },
    a.origin && !a.office && { k: `From ${a.origin.name.split(',')[0]}`, v: [oxKm(a.origin.km), oxMins(a.origin.mins)].filter(Boolean).join(' · '), hi: !a.office },
    (item.info?.facts?.stayCapacity || item.info?.facts?.dayCapacity) && { k: 'Capacity', v: `Up to ${item.info.facts.stayCapacity || item.info.facts.dayCapacity} guests` },
  ].filter(Boolean);
  const stayFrom = minPositive(offsiteRoomRows(item, portal?.calculatorState?.[item._id]).flatMap(r => [r.d, r.s, r.t, r.q]));
  const dayFrom = minPositive([...(item.dayPackages || []).map(p => p.sellingPrice), item.packagePrice]);
  const price = offers.stay && stayFrom ? { l: 'Stay from', v: stayFrom, u: 'per room / night' } : dayFrom ? { l: 'Packages from', v: dayFrom, u: 'per person' } : null;

  return (
    <article style={{ background: '#fff', border: `1px solid ${OX.line}`, display: 'flex', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flex: '1 1 300px', minHeight: isMobile ? 220 : 250 }}>
        <OffsitePhoto src={item.imageUrl} alt={item.name} style={{ position: 'absolute', inset: 0 }} />
        <OptionBadge idx={idx} />
      </div>
      <div style={{ flex: '999 1 480px', minWidth: 0, padding: isMobile ? '22px 18px' : '28px 32px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <OfferChips offers={offers} info={item.info} />
            <h2 style={{ margin: '14px 0 0', fontFamily: OX.serif, fontWeight: 500, fontSize: isMobile ? 28 : 32, lineHeight: 1.1, color: OX.ink }}>{item.name}</h2>
            <div style={{ marginTop: 6, fontFamily: OX.sans, fontSize: 14, color: OX.sub }}>
              {item.location || 'Location to be confirmed'}{item.info?.rating ? ` · ${item.info.rating.value} on Google` : ''}
            </div>
          </div>
          {price && (
            <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.muted }}>{price.l}</div>
              <div style={{ fontFamily: OX.serif, fontSize: 30, color: OX.ink, lineHeight: 1.15 }}>{oxINR(price.v)}</div>
              <div style={{ fontFamily: OX.sans, fontSize: 12, color: OX.muted }}>{price.u}</div>
            </div>
          )}
        </div>
        {cells.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: OX.line, border: `1px solid ${OX.line}` }}>
            {cells.map(c => (
              <div key={c.k} style={{ background: c.hi ? OX.panel : OX.cream, padding: '12px 14px' }}>
                <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: c.hi ? OX.goldText : OX.muted }}>{c.k}</div>
                <div style={{ fontFamily: OX.sans, fontSize: 15, color: OX.ink, marginTop: 4 }}>{c.v}</div>
              </div>
            ))}
          </div>
        )}
        <button type="button" onClick={onExpand} aria-expanded="false" style={{ ...oxBtnGhost, alignSelf: 'flex-start' }}>View full details {OxIcon.chev}</button>
      </div>
    </article>
  );
};

// ── Expanded option ───────────────────────────────────────────────────────────
const OffsiteOption = ({ item, offers, idx, portal, onZoom, isMobile, onOpenCalculator, onOpenChat, onCollapse }) => {
  const calcState = portal?.calculatorState || {};
  const itemCalc = calcState[item._id] || {};
  const addonVisible = (k) => !(itemCalc.disabledAddons || {})[k];
  const info = item.info || {};
  const access = info.access || {};
  const facts = info.facts || {};
  const [plan, setPlan] = React.useState(offers.stay ? 'stay' : 'day');
  const showStay = offers.stay && (plan === 'stay' || !offers.day);
  const showDay = offers.day && (plan === 'day' || !offers.stay);

  // Gallery
  const photos = [item.imageUrl, ...(info.galleryImages || [])].filter(Boolean);
  const zoomAt = (i) => photos[i] && onZoom({ src: photos[i], alt: item.name, all: photos, startIdx: i });

  // Room rows: base occupancy prices + room categories (respecting rooms the team hid)
  const roomRows = offsiteRoomRows(item, itemCalc);
  // Only occupancies that have a price on this order get a column.
  const roomCols = [['Single', 's'], ['Double', 'd'], ['Triple', 't'], ['Quad', 'q']].filter(([, k]) => roomRows.some(r => r[k] > 0));

  // Day packages (legacy flat package price becomes one package)
  const packages = (item.dayPackages || []).length
    ? item.dayPackages
    : (item.packagePrice > 0 ? [{ name: 'Day package', sellingPrice: item.packagePrice, activities: '' }] : []);

  // Add-ons, same visibility rules as the cost calculator
  const addons = [
    { k: 'djCost', n: 'DJ', p: item.djCost, pp: item.djCostPerPerson },
    { k: 'licenseFeeDJ', n: 'DJ licence fee', p: item.licenseFeeDJ, pp: item.licenseFeeDJPerPerson },
    { k: 'cocktailSnacks', n: 'Cocktails & snacks', p: item.cocktailSnacks, pp: item.cocktailSnacksPerPerson },
    { k: 'banquetHall', n: 'Banquet hall', p: item.banquetHall, pp: item.banquetHallPerPerson },
    ...(item.adhocAddons || []).map((a, i) => ({ k: `adhoc_${i}`, n: a.name, p: a.sellingPrice, pp: a.perPerson })),
  ].filter(a => a.p > 0 && addonVisible(a.k))
    .concat((itemCalc.portalCustomAddons || []).map((a, i) => ({
      k: `c${i}`, n: a.name, p: a.price, pp: a.pricingType === 'per_person',
      q: a.pricingType === 'per_unit' ? (itemCalc.addonQty?.[`portalCustom_${i}`] ?? a.qty ?? 0) : null,
    })).filter(a => a.q === null || a.q > 0));

  const stayFrom = minPositive(roomRows.flatMap(r => [r.d, r.s, r.t, r.q]));
  const dayFrom = minPositive(packages.map(p => p.sellingPrice));
  const summaryPrice = showStay && stayFrom
    ? { l: 'Stay from', v: stayFrom, u: 'per room, per night' }
    : dayFrom ? { l: 'Day packages from', v: dayFrom, u: 'per person' } : null;

  const nearLeg = offsiteNearLeg(access);
  const legs = [
    access.airport && { k: 'Nearest airport', name: access.airport.code ? `${access.airport.name} (${access.airport.code})` : access.airport.name, leg: access.airport },
    nearLeg,
    // The team's starting point for this option wins over the portal-wide one.
    access.origin && !access.office && { k: 'From your office', name: access.origin.name, leg: access.origin, hi: true },
    access.rail && { k: 'Nearest railway', name: access.rail.name, leg: access.rail },
  ].filter(Boolean);

  const factRows = [
    facts.rooms && { k: 'Rooms', v: `${facts.rooms} keys` },
    facts.stayCapacity && { k: 'Stay capacity', v: `${facts.stayCapacity} guests` },
    facts.dayCapacity && { k: 'Day capacity', v: `${facts.dayCapacity} guests` },
    facts.eventHall && { k: 'Event hall', v: facts.eventHall },
    (facts.checkIn || facts.checkOut) && { k: 'Check-in · out', v: [facts.checkIn, facts.checkOut].filter(Boolean).join(' · ') },
    facts.food && { k: 'Food', v: facts.food },
    facts.alcoholDj && { k: 'Alcohol & DJ', v: facts.alcoholDj },
  ].filter(Boolean);

  const pad = isMobile ? '0 18px' : '0 clamp(20px, 3vw, 44px)';
  const checked = access.checkedAt ? new Date(access.checkedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
  const tabBtn = (active) => ({ minHeight: 36, padding: '0 16px', border: 'none', borderRadius: 40, cursor: 'pointer', fontFamily: OX.sans, fontWeight: 600, fontSize: 14, background: active ? '#fff' : 'transparent', boxShadow: active ? '0 1px 4px rgba(0,0,0,0.16)' : 'none', color: OX.ink, flex: isMobile ? 1 : 'none' });

  return (
    <article style={{ background: '#fff', border: `1px solid ${OX.line}`, animation: 'obsidianFadeIn .5s ease both' }}>
      {/* Gallery */}
      {isMobile || photos.length < 2 ? (
        <div style={{ position: 'relative' }}>
          <OffsitePhoto src={photos[0]} alt={item.name} onClick={() => zoomAt(0)} style={{ height: isMobile ? 260 : 420 }} />
          <OptionBadge idx={idx} />
          {photos.length > 1 && <button type="button" onClick={() => zoomAt(0)} style={{ ...oxBtnGhost, position: 'absolute', right: 14, bottom: 14, background: '#fff', border: 'none', minHeight: 40 }}>{photos.length} photos</button>}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 4, padding: 4 }}>
          <div style={{ position: 'relative' }}>
            <OffsitePhoto src={photos[0]} alt={item.name} onClick={() => zoomAt(0)} style={{ height: 440 }} />
            <OptionBadge idx={idx} />
            <button type="button" onClick={() => zoomAt(0)} style={{ ...oxBtnGhost, position: 'absolute', right: 16, bottom: 16, background: '#fff', border: 'none', minHeight: 40 }}>View all {photos.length} photos</button>
          </div>
          <div style={{ display: 'grid', gridTemplateRows: photos.length > 2 ? '1fr 1fr' : '1fr', gap: 4 }}>
            <OffsitePhoto src={photos[1]} alt={`${item.name} — photo 2`} onClick={() => zoomAt(1)} />
            {photos.length > 2 && <OffsitePhoto src={photos[2]} alt={`${item.name} — photo 3`} onClick={() => zoomAt(2)} />}
          </div>
        </div>
      )}

      {/* Title */}
      <div style={{ padding: isMobile ? '24px 18px 0' : '36px clamp(20px, 3vw, 44px) 0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
        <div style={{ flex: '1 1 420px', minWidth: 0 }}>
          <OfferChips offers={offers} info={info} />
          <h2 style={{ margin: '16px 0 0', fontFamily: OX.serif, fontWeight: 500, fontSize: isMobile ? 32 : 'clamp(32px, 3.6vw, 46px)', lineHeight: 1.08, color: OX.ink }}>{item.name}</h2>
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 18px', fontFamily: OX.sans, fontSize: 14, color: OX.sub }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ color: OX.goldText, display: 'flex' }}>{Ic.pin}</span>{info.address || item.location || 'Location to be confirmed'}</span>
            {info.rating && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{OxIcon.star}{info.rating.value} on Google{info.rating.count ? ` · ${Number(info.rating.count).toLocaleString('en-IN')} reviews` : ''}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {item.website && <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noreferrer" style={oxBtnGhost}>Website {OxIcon.arrow}</a>}
          {onCollapse && <button type="button" onClick={onCollapse} aria-expanded="true" style={oxBtnGhost}>Collapse</button>}
        </div>
      </div>

      {/* Getting there */}
      {legs.length > 0 && (
        <section aria-label="Getting there" style={{ margin: isMobile ? '24px 18px 0' : '32px clamp(20px, 3vw, 44px) 0', borderTop: `1px solid ${OX.line}`, borderBottom: `1px solid ${OX.line}`, padding: '26px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            <h3 style={oxEyebrow}>Getting there</h3>
            <span style={{ fontFamily: OX.sans, fontSize: 12, color: OX.muted }}>Road distance and typical drive time{checked ? ` · checked ${checked}` : ''}</span>
          </div>

          {!isMobile && access.airport && nearLeg && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }} aria-hidden="true">
              {[
                { icon: OxIcon.plane, label: access.airport.code ? `${access.airport.code} airport` : 'Airport' },
                { leg: access.airport },
                { icon: OxIcon.home, label: item.name, main: true },
                { leg: nearLeg.leg },
                { icon: access.office ? OxIcon.office : OxIcon.city, label: nearLeg.short },
              ].map((n, i) => n.leg ? (
                <div key={i} style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginBottom: 22 }}>
                  <span style={{ fontFamily: OX.serif, fontSize: 20, color: OX.ink }}>{oxKm(n.leg.km)}</span>
                  <span style={{ width: '100%', borderTop: '1px dashed #8f8676' }} />
                  <span style={{ fontFamily: OX.sans, fontSize: 12, color: OX.muted }}>{oxMins(n.leg.mins)}</span>
                </div>
              ) : (
                <div key={i} style={{ flex: '0 0 auto', width: 130, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: n.main ? 56 : 44, height: n.main ? 56 : 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: n.main ? OX.navy : 'transparent', border: n.main ? 'none' : `1px solid ${OX.navy}`, color: n.main ? OX.goldSoft : OX.navy }}>{n.icon}</span>
                  <span style={{ fontFamily: OX.sans, fontSize: 12, color: n.main ? OX.ink : OX.sub, fontWeight: n.main ? 500 : 400, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{n.label}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 1, background: OX.line, border: `1px solid ${OX.line}` }}>
            {legs.map(l => (
              <div key={l.k} style={{ background: l.hi ? OX.panel : OX.cream, padding: '16px 18px' }}>
                <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: l.hi ? OX.goldText : OX.muted }}>{l.k}</div>
                <div style={{ fontFamily: OX.serif, fontSize: 21, color: OX.ink, marginTop: 6, lineHeight: 1.2 }}>{l.name}</div>
                <div style={{ fontFamily: OX.sans, fontSize: 13, color: OX.sub, marginTop: 4 }}>{[oxKm(l.leg.km), l.leg.mins != null ? `approx. ${oxMins(l.leg.mins)}` : ''].filter(Boolean).join(' · ')}</div>
              </div>
            ))}
          </div>
          {info.mapsUrl && (
            <a href={info.mapsUrl} target="_blank" rel="noreferrer" style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36, fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.goldText, textDecoration: 'none' }}>
              Open in Google Maps {OxIcon.arrow}
            </a>
          )}
        </section>
      )}

      {/* Body */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 44, padding: isMobile ? '28px 18px 32px' : '36px clamp(20px, 3vw, 44px) 44px' }}>
        <div style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 38 }}>

          {factRows.length > 0 && (
            <section aria-label="At a glance">
              <h3 style={{ ...oxEyebrow, marginBottom: 16 }}>At a glance</h3>
              <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 140 : 170}px, 1fr))`, gap: '20px 26px' }}>
                {factRows.map(f => (
                  <div key={f.k} style={{ borderTop: `1px solid ${OX.line2}`, paddingTop: 10 }}>
                    <dt style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.muted }}>{f.k}</dt>
                    <dd style={{ margin: '6px 0 0', fontFamily: OX.serif, fontSize: 20, lineHeight: 1.25, color: OX.ink }}>{f.v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {(info.shortDescription || item.details || (info.amenities || []).length > 0) && (
            <section aria-label="The property">
              <h3 style={{ ...oxEyebrow, marginBottom: 14 }}>The property</h3>
              {info.shortDescription && (
                <p style={{ margin: '0 0 14px', fontFamily: OX.serif, fontSize: isMobile ? 19 : 21, lineHeight: 1.6, color: OX.body, maxWidth: 680, whiteSpace: 'pre-line' }}>{info.shortDescription}</p>
              )}
              {item.details && <SmartDescription text={item.details} itemId={String(item._id || idx)} />}
              {(info.amenities || []).length > 0 && (
                <ul aria-label="Amenities" style={{ listStyle: 'none', margin: '18px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {info.amenities.map(a => (
                    <li key={a} style={{ fontFamily: OX.sans, fontSize: 13, color: OX.body, border: `1px solid ${OX.line2}`, padding: '7px 13px' }}>{a}</li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {(roomRows.length > 0 || packages.length > 0 || addons.length > 0) && (
            <section aria-label="Plans and pricing">
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <h3 style={oxEyebrow}>Plans &amp; pricing</h3>
                {offers.stay && offers.day && roomRows.length > 0 && packages.length > 0 && (
                  <div role="tablist" aria-label="Plan type" style={{ display: 'flex', border: `1px solid ${OX.navy}`, width: isMobile ? '100%' : 'auto' }}>
                    <button type="button" role="tab" aria-selected={plan === 'stay'} onClick={() => setPlan('stay')} style={tabBtn(plan === 'stay')}>Night stay</button>
                    <button type="button" role="tab" aria-selected={plan === 'day'} onClick={() => setPlan('day')} style={tabBtn(plan === 'day')}>Day outing</button>
                  </div>
                )}
              </div>

              {showStay && roomRows.length > 0 && (isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {roomRows.map(r => (
                    <div key={r.key} style={{ border: `1px solid ${OX.line}`, padding: 16 }}>
                      <div style={{ fontFamily: OX.serif, fontSize: 20, color: OX.ink }}>{r.name}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, roomCols.length)}, minmax(0,1fr))`, gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${OX.line}` }}>
                        {roomCols.map(([l, k]) => [l, r[k]]).map(([l, v]) => (
                          <div key={l}><div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.muted }}>{l}</div><div style={{ fontFamily: OX.sans, fontSize: 14, color: OX.ink, marginTop: 2 }}>{v > 0 ? oxINR(v) : '—'}</div></div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p style={{ margin: '2px 0 0', fontFamily: OX.sans, fontSize: 12, color: OX.muted }}>Per room, per night{facts.checkIn ? ` · check-in ${facts.checkIn}` : ''}</p>
                </div>
              ) : (
                <div>
                  <div style={{ overflowX: 'auto', border: `1px solid ${OX.line}` }}>
                    <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontFamily: OX.sans, fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: OX.panel }}>
                          {['Room · per night', ...roomCols.map(([l]) => l)].map((h, i) => (
                            <th key={h} scope="col" style={{ textAlign: i ? 'right' : 'left', padding: '13px 18px', fontSize: 12, color: OX.sub, fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {roomRows.map(r => (
                          <tr key={r.key} style={{ borderTop: `1px solid ${OX.line}` }}>
                            <th scope="row" style={{ textAlign: 'left', padding: '15px 18px', fontWeight: 400, fontFamily: OX.serif, fontSize: 19, color: OX.ink }}>{r.name}</th>
                            {roomCols.map(([, k]) => r[k]).map((v, i) => (
                              <td key={i} style={{ textAlign: 'right', padding: '15px 18px', fontVariantNumeric: 'tabular-nums', color: v > 0 ? OX.ink : OX.muted }}>{v > 0 ? oxINR(v) : '—'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ margin: '10px 0 0', fontFamily: OX.sans, fontSize: 12, color: OX.muted }}>Taxes as applicable{facts.checkIn ? ` · check-in ${facts.checkIn}` : ''}{facts.checkOut ? `, check-out ${facts.checkOut}` : ''}</p>
                </div>
              ))}

              {showDay && packages.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 220 : 260}px, 1fr))`, gap: 14 }}>
                  {packages.map((pkg, pi) => (
                    <div key={pi} style={{ border: `1px solid ${OX.line2}`, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                        <div style={{ fontFamily: OX.serif, fontSize: 22, color: OX.ink, lineHeight: 1.2 }}>{pkg.name || `Package ${pi + 1}`}</div>
                        {pkg.sellingPrice > 0 && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontFamily: OX.serif, fontSize: 24, color: OX.ink, lineHeight: 1 }}>{oxINR(pkg.sellingPrice)}</div>
                            <div style={{ fontFamily: OX.sans, fontSize: 12, color: OX.muted, marginTop: 3 }}>per person</div>
                          </div>
                        )}
                      </div>
                      {pkg.activities && (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
                          {pkg.activities.split(/[\n,]+/).map(s => s.trim()).filter(Boolean).map((line, li) => (
                            <li key={li} style={{ display: 'flex', gap: 10, fontFamily: OX.sans, fontSize: 14, color: OX.body, lineHeight: 1.45 }}><span aria-hidden="true" style={{ color: OX.goldText }}>—</span>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {addons.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.sub, marginBottom: 8 }}>Optional add-ons</div>
                  <div style={{ borderTop: `1px solid ${OX.line}` }}>
                    {addons.map(a => (
                      <div key={a.k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '12px 0', borderBottom: `1px solid ${OX.line}`, fontFamily: OX.sans, fontSize: 14, color: OX.ink }}>
                        <span>{a.n}{a.q != null && <span style={{ color: OX.muted }}> × {a.q}</span>}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{a.q != null
                          ? (a.p > 0 ? <>{oxINR(a.p * a.q)} <span style={{ color: OX.muted, fontSize: 12 }}>{oxINR(a.p)} each</span></> : <span style={{ color: OX.muted, fontSize: 12 }}>Included</span>)
                          : <>{oxINR(a.p)} <span style={{ color: OX.muted, fontSize: 12 }}>{a.pp ? 'per person' : 'flat'}</span></>}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {(item.note || (item.attachments || []).length > 0) && (
            <section aria-label="Notes and documents" style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              {item.note && (
                <blockquote style={{ flex: '1 1 300px', margin: 0, padding: '20px 22px', background: '#f7f7f7', borderRadius: 12 }}>
                  <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.goldText, marginBottom: 10 }}>From your Marqland planner</div>
                  <p style={{ margin: 0, fontFamily: OX.serif, fontStyle: 'italic', fontSize: 19, lineHeight: 1.55, color: '#222222' }}>{item.note}</p>
                </blockquote>
              )}
              {(item.attachments || []).length > 0 && (
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.sub, marginBottom: 10 }}>Documents</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {item.attachments.map((att, ai) => <PropAttachChip key={ai} att={att} fileUrl={propAttachmentUrl(portal.slug, item._id, ai, att.name)} />)}
                  </div>
                </div>
              )}
            </section>
          )}

          {(item.siteVideo || (item.youtubeUrl && getYouTubeId(item.youtubeUrl))) && (
            <section aria-label="Property video">
              <h3 style={{ ...oxEyebrow, marginBottom: 4 }}>Property film</h3>
              <div style={{ maxWidth: 800 }}><OffsiteVideoEmbed item={item} slug={portal?.slug} /></div>
            </section>
          )}
        </div>

        {/* Summary panel */}
        <aside aria-label="Option summary" style={{ flex: '1 1 280px', minWidth: 0 }}>
          <div style={{ position: isMobile ? 'static' : 'sticky', top: 140, border: `1px solid ${OX.line2}`, background: OX.cream, padding: '24px 24px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {summaryPrice && (
              <div>
                <div style={{ fontFamily: OX.sans, fontWeight: 600, fontSize: 12, color: OX.muted }}>{summaryPrice.l}</div>
                <div style={{ fontFamily: OX.serif, fontSize: 40, lineHeight: 1.1, color: OX.ink, marginTop: 4 }}>{oxINR(summaryPrice.v)}</div>
                <div style={{ fontFamily: OX.sans, fontSize: 13, color: OX.sub }}>{summaryPrice.u}</div>
              </div>
            )}
            {(() => {
              const rows = [
                (facts.stayCapacity || facts.dayCapacity) && ['Group size', `Up to ${showStay ? (facts.stayCapacity || facts.dayCapacity) : (facts.dayCapacity || facts.stayCapacity)} guests`],
                access.origin && !access.office && [`From ${access.origin.name.split(',')[0]}`, oxMins(access.origin.mins) || oxKm(access.origin.km)],
                access.airport && [`From ${access.airport.code || 'airport'}`, oxMins(access.airport.mins) || oxKm(access.airport.km)],
                nearLeg && [access.office ? 'From your office' : `From ${access.city.name}`, oxMins(nearLeg.leg.mins) || oxKm(nearLeg.leg.km)],
              ].filter(Boolean).filter(([, v]) => v);
              return rows.length > 0 && (
                <div style={{ borderTop: summaryPrice ? `1px solid ${OX.line2}` : 'none', paddingTop: summaryPrice ? 14 : 0, display: 'flex', flexDirection: 'column', gap: 11, fontFamily: OX.sans, fontSize: 14 }}>
                  {rows.map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span style={{ color: OX.muted }}>{k}</span><span style={{ color: OX.ink, textAlign: 'right' }}>{v}</span></div>
                  ))}
                </div>
              );
            })()}
            {onOpenCalculator && (
              <button type="button" onClick={onOpenCalculator} style={AIR.btn('primary')}>
                Estimate cost for my team
              </button>
            )}
            {onOpenChat && (
              <button type="button" onClick={onOpenChat} style={AIR.btn('ghost')}>Ask a question</button>
            )}
          </div>
        </aside>
      </div>
    </article>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// UNIFIED COST CALCULATOR
// One component for both portal types. Desktop/tablet only.
// ═════════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════════
// TAB PAGES — shared header and buttons for Shortlist / Calculator / Shipments /
// Message board, in the same editorial style as the catalogue.
// ═════════════════════════════════════════════════════════════════════════════
const pxBtn = (kind = 'primary') => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, padding: '0 24px', borderRadius: 8,
  fontFamily: "'Jost',sans-serif", fontSize: 15, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  border: '1px solid #222222', background: kind === 'primary' ? '#222222' : '#fff', color: kind === 'primary' ? '#fff' : '#222222',
});

const TabHeader = ({ eyebrow, title, sub, children, isMobile }) => (
  <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, paddingBottom: isMobile ? 18 : 26, marginBottom: isMobile ? 24 : 36, borderBottom: '1px solid #ebebeb' }}>
    <div style={{ minWidth: 0, flex: '1 1 420px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 14, fontWeight: 600, color: '#7d6233', marginBottom: 8 }}>{eyebrow}</div>
      <h1 style={{ margin: 0, fontFamily: "'Cormorant Garamond',Georgia,serif", fontWeight: 500, fontSize: isMobile ? 34 : 48, lineHeight: 1.05, color: '#222222' }}>{title}</h1>
      {sub && <p style={{ margin: '10px 0 0', fontFamily: "'Jost',sans-serif", fontSize: 16, lineHeight: 1.6, color: '#6a6a6a', maxWidth: 600 }}>{sub}</p>}
    </div>
    {children && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>{children}</div>}
  </header>
);

// ── Shortlist tab body: same pins as the catalogue, grouped by category ──────
const SelectedItemsGroups = ({ selCombos, selProducts, wishlisted, toggleWish, setLightbox, portal, isMobile }) => {
  const isTablet = useTablet();
  const [viewer, setViewer] = React.useState(null);
  const [ratios, setRatios] = React.useState({});
  const onRatio = React.useCallback((id, r) => setRatios(p => (Math.abs((p[id] || 0) - r) < 0.01 ? p : { ...p, [id]: r })), []);
  const getPrice = i => Number(portal?.calculatorState?.[i._id]?.priceOverride ?? i.price ?? 0);
  const columns = isMobile ? 2 : isTablet ? 3 : 5;
  const estimate = item => (ratios[String(item._id)] || 1.25) + (isMobile ? 0.42 : 0.3)
    + ([item.imageUrl, ...(item.additionalImages || [])].filter(Boolean).length > 1 ? 0.2 : 0)
    + pinVideoHeight(item);

  // One continuous feed (categories kept together), like "All" in the catalogue.
  const catOrder = [];
  selProducts.forEach(i => { const c = i.category || 'Other'; if (!catOrder.includes(c)) catOrder.push(c); });
  const feed = [...selProducts].sort((a, b) => catOrder.indexOf(a.category || 'Other') - catOrder.indexOf(b.category || 'Other'));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 40 : 56 }}>
      {viewer && (
        <ProductViewer item={viewer.item} startIdx={viewer.idx} price={getPrice(viewer.item)} slug={portal?.slug}
          loved={wishlisted.has(String(viewer.item._id))} onToggleWish={toggleWish} onClose={() => setViewer(null)} />
      )}
      {feed.length > 0 && (
        <Masonry items={feed} columns={columns} gap={isMobile ? 10 : 18} estimate={estimate}
          renderItem={(item, idx) => (
            <ProductTile key={item._id} item={item} price={getPrice(item)} isMobile={isMobile} slug={portal?.slug}
              loved={wishlisted.has(String(item._id))} onToggleWish={toggleWish}
              ratio={ratios[String(item._id)] || 1.25} onRatio={onRatio}
              onOpen={i => setViewer({ item, idx: i })} delay={Math.min(idx * 0.03, 0.4)} />
          )} />
      )}
      {selCombos.length > 0 && (
        <section aria-label="Bundles">
          {groupHead('Bundles', `${selCombos.length} set${selCombos.length !== 1 ? 's' : ''}`)}
          <ComboBento combos={selCombos} onZoom={setLightbox} wishlisted={wishlisted} onToggleWish={toggleWish} />
        </section>
      )}
    </div>
  );
};

// ── Cost calculator: order list (products + bundles) ─────────────────────────
// Quantities are plain number fields (no spinners), as before. A "same quantity
// for all" shortcut fills every row at once — handy for kits sent to N people.
const UnifiedCalcTable = ({ rows, qty, setQ, portal, INR, grandTotalQty, onReset }) => {
  const isMobile = useMobile();
  const [expanded, setExpanded] = React.useState({});
  const [bulk, setBulk] = React.useState('');
  const priceOf = row => row.kind === 'product'
    ? Number(portal?.calculatorState?.[row.item._id]?.priceOverride ?? (row.item.price || 0))
    : Number(row.combo.totalPrice || 0);
  const idOf = row => (row.kind === 'product' ? row.item._id : row.combo._id);
  const qtyField = { width: isMobile ? 72 : 84, height: 44, padding: '0 10px', border: `1px solid ${PX.line2}`, background: '#fff', fontFamily: PX.sans, fontSize: 15, color: PX.ink, textAlign: 'center', outline: 'none', boxSizing: 'border-box', MozAppearance: 'textfield' };

  return (
    <div>
      {/* Bulk fill */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: isMobile ? '14px' : '16px 20px', background: PX.studio, marginBottom: 2 }}>
        <label htmlFor="bulk-qty" style={{ fontFamily: PX.sans, fontSize: 13, color: PX.sub, flex: isMobile ? '1 1 100%' : '0 1 auto' }}>Same quantity for every item</label>
        <input id="bulk-qty" type="number" inputMode="numeric" min="0" value={bulk} placeholder="e.g. 50"
          onChange={e => setBulk(e.target.value)} style={{ ...qtyField, width: 110, height: 40 }} />
        <button type="button" onClick={() => { const n = Number(bulk); if (n >= 0 && bulk !== '') rows.forEach(r => setQ(idOf(r), n)); }}
          disabled={bulk === ''} style={{ ...pxBtn('primary'), height: 40, opacity: bulk === '' ? 0.4 : 1 }}>Apply</button>
        {grandTotalQty > 0 && (
          <button type="button" onClick={onReset} style={{ marginLeft: isMobile ? 0 : 'auto', background: 'none', border: 'none', cursor: 'pointer', fontFamily: PX.sans, fontSize: 12, color: PX.goldText, textDecoration: 'underline', textUnderlineOffset: 3, padding: '8px 0' }}>
            Clear all quantities
          </button>
        )}
      </div>

      {/* Column labels */}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 120px 100px 130px', gap: 20, padding: '14px 20px', borderBottom: `1px solid ${PX.line2}` }}>
          {['', 'Item', 'Unit price', 'Quantity', 'Amount'].map((h, i) => (
            <span key={i} style={{ ...pxEyebrow, textAlign: i >= 2 ? (i === 3 ? 'center' : 'right') : 'left' }}>{h}</span>
          ))}
        </div>
      )}

      {rows.map(row => {
        const id = idOf(row);
        const isCombo = row.kind === 'combo';
        const name = isCombo ? (row.combo.label || `Bundle ${(row.ci ?? 0) + 1}`) : row.item.name;
        const img = isCombo ? (row.combo.collageImageUrl || (row.combo.items || []).find(i => i.imageUrl)?.imageUrl) : row.item.imageUrl;
        const sub = isCombo ? `Bundle · ${(row.combo.items || []).length} items` : [row.item.category, row.item.subCategory].filter(Boolean).join(' · ');
        const unit = priceOf(row);
        const q = qty[id] || 0;
        const line = unit * q;
        const qtyInput = (
          <input type="number" inputMode="numeric" min="0" max="99999" value={q || ''} placeholder="0" aria-label={`Quantity for ${name}`}
            onChange={e => setQ(id, e.target.value)} onClick={e => e.stopPropagation()}
            style={{ ...qtyField, borderColor: q > 0 ? PX.ink : PX.line2 }} />
        );
        return (
          <div key={row.id} style={{ borderBottom: `1px solid ${PX.line}`, background: q > 0 ? '#fff' : 'transparent', transition: 'background .2s' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '60px 1fr' : '72px 1fr 120px 100px 130px', gap: isMobile ? 12 : 20, alignItems: 'center', padding: isMobile ? '14px' : '14px 20px' }}>
              <div style={{ width: isMobile ? 60 : 72, height: isMobile ? 72 : 88, background: PX.studio, borderRadius: 8, overflow: 'hidden' }}>
                {img && <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: isCombo ? 'cover' : 'contain', mixBlendMode: isCombo ? 'normal' : 'multiply', display: 'block' }} />}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: PX.serif, fontSize: isMobile ? 17 : 19, color: PX.ink, lineHeight: 1.25 }}>{name}</div>
                <div style={{ fontFamily: PX.sans, fontSize: 12, color: PX.muted, marginTop: 3 }}>{sub}</div>
                {isCombo && (row.combo.items || []).length > 0 && (
                  <button type="button" onClick={() => setExpanded(p => ({ ...p, [row.id]: !p[row.id] }))} aria-expanded={!!expanded[row.id]}
                    style={{ marginTop: 6, background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', fontFamily: PX.sans, fontSize: 12, color: PX.goldText, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                    {expanded[row.id] ? 'Hide items' : 'Show items'}
                  </button>
                )}
                {isMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10 }}>
                    <div style={{ fontFamily: PX.sans, fontSize: 13, color: PX.sub }}>
                      {INR(unit)} <span style={{ color: PX.muted }}>each</span>
                      {q > 0 && <div style={{ color: PX.ink, fontWeight: 500, marginTop: 2 }}>{INR(line)}</div>}
                    </div>
                    {qtyInput}
                  </div>
                )}
              </div>
              {!isMobile && <div style={{ fontFamily: PX.sans, fontSize: 14, color: PX.sub, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{INR(unit)}</div>}
              {!isMobile && <div style={{ display: 'flex', justifyContent: 'center' }}>{qtyInput}</div>}
              {!isMobile && <div style={{ fontFamily: PX.sans, fontSize: 15, color: q > 0 ? PX.ink : '#929292', fontWeight: q > 0 ? 500 : 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{q > 0 ? INR(line) : '—'}</div>}
            </div>
            {isCombo && expanded[row.id] && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: isMobile ? '0 14px 16px' : '0 20px 18px 112px' }}>
                {(row.combo.items || []).map((it, i) => (
                  <div key={i} title={it.name} style={{ width: 72, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ width: 72, height: 72, background: PX.studio, borderRadius: 8, overflow: 'hidden' }}>
                      {it.imageUrl && <img src={it.imageUrl} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' }} />}
                    </div>
                    <span style={{ fontFamily: PX.sans, fontSize: 12, color: PX.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.subCategory || it.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CostCalculator = ({ portal, wishlisted = new Set(), combos = [], onCalcPatch = () => {}, onOpenCatalogue = null, onOpenChat = null }) => {
  const isMobile = useMobile();
  const isTablet = useTablet();
  const INR = v => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const slug = portal.slug;
  const isProduct = portal.type === 'product';
  const productItems = portal.productItems || [];
  // Only shortlisted products appear in the calculator
  const shortlisted = productItems.filter(i => wishlisted.has(String(i._id)));
  const shortlistedCombos = combos.filter(c => wishlisted.has(String(c._id)));
  const offsiteItems = portal.offsiteItems || [];

  // ── Shared helpers ──────────────────────────────────────────────────────────
  const inputSt = {
    padding: '5px 7px', border: '1px solid #dddddd',
    fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222',
    textAlign: 'center', outline: 'none', background: '#fff', transition: 'border-color .15s',
  };

  // ── SHARED PERSISTENCE ───────────────────────────────────────────────────────
  // calculatorState is keyed by item/combo _id, so product qty, combo qty, and
  // offsite calculator fields all live side by side in the same object.
  // fullStateRef accumulates every change locally (starting from whatever was
  // already persisted) so that a product-qty edit and an offsite-calculator
  // edit in the same session never overwrite each other's last save.
  const persistedCalc = portal.calculatorState || {};
  const fullStateRef = React.useRef({ ...persistedCalc });
  const persistTimer = React.useRef(null);
  // patch: { [id]: { ...fieldsToMerge } } — only the fields that changed
  const persistCalcState = React.useCallback((patch) => {
    // Update the parent's portal.calculatorState immediately so a remount
    // (e.g. from switching tabs) always seeds from the latest edit, not a
    // stale pre-poll snapshot.
    onCalcPatch(patch);
    Object.entries(patch).forEach(([id, fields]) => {
      fullStateRef.current[id] = { ...(fullStateRef.current[id] || {}), ...fields };
    });
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      log.debug('Persisting calculator state', { slug });
      fetch(`${API_BASE}/api/portal/public/${slug}/calculator`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calculatorState: fullStateRef.current }),
      }).catch(err => log.warn('Calculator state persist failed', err.message));
    }, 800);
  }, [slug, onCalcPatch]);

  // ── PRODUCT STATE ───────────────────────────────────────────────────────────
  // Seed quantities from whatever was last persisted, so a refresh doesn't lose them.
  const [qty, setQty] = React.useState(() => {
    const m = {};
    productItems.forEach(i => { const q = persistedCalc[i._id]?.qty; if (q) m[i._id] = q; });
    combos.forEach(c => { const q = persistedCalc[c._id]?.qty; if (q) m[c._id] = q; });
    return m;
  });
  const setQ = (id, val) => {
    const n = Math.max(0, Math.min(99999, Number(val) || 0));
    setQty(prev => ({ ...prev, [id]: n }));
    persistCalcState({ [id]: { qty: n } });
  };

  // Unit price respects the team's price override, same as the order list shows.
  const unitOf = i => Number(persistedCalc[i._id]?.priceOverride ?? (i.price || 0));
  const pLines = productItems.map(i => ({ ...i, unit: unitOf(i), q: qty[i._id] || 0, line: (qty[i._id] || 0) * unitOf(i) }));
  const pTotal = pLines.reduce((s, l) => s + l.line, 0);
  const pTotalQty = pLines.reduce((s, l) => s + l.q, 0);
  const comboTotal = shortlistedCombos.reduce((s, c) => s + (qty[c._id] || 0) * (c.totalPrice || 0), 0);
  const comboTotalQty = shortlistedCombos.reduce((s, c) => s + (qty[c._id] || 0), 0);
  const grandTotal = pTotal + comboTotal;
  const grandTotalQty = pTotalQty + comboTotalQty;
  const pActive = pLines.filter(l => l.q > 0);

  // ── OFFSITE STATE ───────────────────────────────────────────────────────────
  const initProp = item => {
    const saved = persistedCalc[item._id] || {};
    // Start with base keys
    const state = {
      nights: saved.nights ?? 1,
      single: saved.single ?? 0,
      double: saved.double ?? 0,
      triple: saved.triple ?? 0,
      quad: saved.quad ?? 0,
      pax: saved.pax ?? 0,
      pkgId: saved.pkgId !== undefined ? saved.pkgId : (item.dayPackages?.length > 0 ? 0 : null),
      addons: saved.addons || {},
      addonQty: saved.addonQty || {},
    };
    // Restore any cat_* room counts set by team or client in a previous session
    Object.keys(saved).forEach(k => {
      if (k.startsWith('cat_')) state[k] = saved[k];
    });
    return state;
  };

  const [calcs, setCalcs] = React.useState(() => { const m = {}; offsiteItems.forEach(i => { m[i._id] = initProp(i); }); return m; });
  const [propOpen, setPropOpen] = React.useState(() => { const m = {}; offsiteItems.forEach((i, idx) => { m[i._id] = idx === 0; }); return m; });
  const [typeFilter, setTypeFilter] = React.useState('all');

  const hasDay = offsiteItems.some(i => i.type !== 'Night Stay');
  const hasNight = offsiteItems.some(i => i.type === 'Night Stay');

  const visibleItems = typeFilter === 'all' ? offsiteItems
    : typeFilter === 'day' ? offsiteItems.filter(i => i.type !== 'Night Stay')
      : offsiteItems.filter(i => i.type === 'Night Stay');

  const upd = (id, field, val) => {
    setCalcs(p => ({ ...p, [id]: { ...p[id], [field]: val } }));
    persistCalcState({ [id]: { [field]: val } });
  };
  const toggleAddon = (id, key) => {
    setCalcs(p => {
      const nextAddons = { ...p[id].addons, [key]: !p[id].addons[key] };
      persistCalcState({ [id]: { addons: nextAddons } });
      return { ...p, [id]: { ...p[id], addons: nextAddons } };
    });
  };

  // Quantity add-ons (e.g. "Volvo 40 seater" × 2) start at the count the team
  // set and are included while the count is above zero; the client can edit it.
  const addonQtyOf = (c, a) => Math.max(0, Number(c.addonQty?.[a.key] ?? a.defaultQty) || 0);
  const addonActive = (c, a) => (a.unit ? addonQtyOf(c, a) > 0 : !!c.addons[a.key]);
  const addonAmount = (c, a, guests) => (a.unit ? a.value * addonQtyOf(c, a) : a.perPerson ? a.value * Math.max(1, guests) : a.value);
  const setAddonQty = (id, key, val) => {
    const n = Math.max(0, Math.min(999, Math.floor(Number(val) || 0)));
    setCalcs(p => {
      const nextQty = { ...(p[id].addonQty || {}), [key]: n };
      persistCalcState({ [id]: { addonQty: nextQty } });
      return { ...p, [id]: { ...p[id], addonQty: nextQty } };
    });
  };
  const unitAddonRow = (item, c, a) => {
    const q = addonQtyOf(c, a);
    return (
      <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, border: `1px solid ${q > 0 ? '#222222' : '#dddddd'}`, background: '#fff' }}>
        <span style={{ flex: 1, fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222' }}>{a.label}</span>
        <input type="number" min={0} max={999} value={q} aria-label={`${a.label} quantity`}
          onChange={e => setAddonQty(item._id, a.key, e.target.value)}
          style={{ width: 56, padding: '5px 6px', border: '1px solid rgba(0,0,0,0.15)', fontFamily: "'Jost',sans-serif", fontSize: 13, textAlign: 'center', color: '#222222' }} />
        {a.value > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0, minWidth: 70 }}>
            <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#7d6233' }}>{INR(a.value * q)}</span>
            <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292' }}>{INR(a.value)} × {q}</span>
          </div>
        )}
      </div>
    );
  };

  const addonList = item => {
    // disabledAddons from team editor — these are hidden for this specific order
    const disabledMap = (persistedCalc[item._id]?.disabledAddons) || {};
    const l = [];
    if (item.djCost > 0 && !disabledMap['djCost']) l.push({ key: 'dj', label: 'DJ', value: item.djCost, perPerson: !!item.djCostPerPerson });
    if (item.licenseFeeDJ > 0 && !disabledMap['licenseFeeDJ']) l.push({ key: 'djlic', label: 'DJ Licence', value: item.licenseFeeDJ, perPerson: !!item.licenseFeeDJPerPerson });
    if (item.cocktailSnacks > 0 && !disabledMap['cocktailSnacks']) l.push({ key: 'cocktail', label: 'Cocktails & Snacks', value: item.cocktailSnacks, perPerson: item.cocktailSnacksPerPerson !== false });
    if (item.banquetHall > 0 && !disabledMap['banquetHall']) l.push({ key: 'banquet', label: 'Banquet Hall', value: item.banquetHall, perPerson: !!item.banquetHallPerPerson });
    (item.adhocAddons || []).filter(a => a.sellingPrice > 0).forEach((a, i) => {
      if (!disabledMap[`adhoc_${i}`]) l.push({ key: `adhoc_${i}`, label: a.name, value: a.sellingPrice, perPerson: !!a.perPerson });
    });
    // Portal-only custom add-ons set by team for this specific order
    (persistedCalc[item._id]?.portalCustomAddons || []).forEach((a, i) => {
      if (a.pricingType === 'per_unit') l.push({ key: `portalCustom_${i}`, label: a.name, value: a.price || 0, unit: true, defaultQty: a.qty || 0 });
      else l.push({ key: `portalCustom_${i}`, label: a.name, value: a.price || 0, perPerson: a.pricingType === 'per_person' });
    });
    return l;
  };

  const calcTotal = item => {
    const c = calcs[item._id] || initProp(item);
    const isDayOut = item.type !== 'Night Stay';
    let base = 0;
    if (isDayOut) {
      if (item.dayPackages?.length > 0 && c.pkgId !== null) base = (item.dayPackages[c.pkgId]?.sellingPrice || 0) * (c.pax || 0);
      else base = (item.packagePrice || 0) * (c.pax || 0);
    } else {
      const n = Math.max(1, c.nights || 1);
      base = (item.singlePrice || 0) * (c.single || 0) * n + (item.doublePrice || 0) * (c.double || 0) * n
        + (item.triplePrice || 0) * (c.triple || 0) * n + (item.quadPrice || 0) * (c.quad || 0) * n;
      // Add room category costs
      (item.roomCategories || []).forEach(cat => {
        const catId = cat._id || cat.name;
        if (persistedCalc[item._id]?.disabledRooms?.[`cat_${catId}`]) return;
        ['single', 'double', 'triple'].forEach(rk => {
          const compKey = `cat_${catId}_${rk}`;
          if (persistedCalc[item._id]?.disabledRooms?.[compKey]) return;
          base += (cat[`${rk}Price`] || 0) * (c[compKey] || 0) * n;
        });
      });
    }
    // Guest headcount for per-person addon multiplication
    const guests = isDayOut
      ? (c.pax || 0)
      : (() => {
        let g = (c.single || 0) * 1 + (c.double || 0) * 2 + (c.triple || 0) * 3 + (c.quad || 0) * 4;
        (item.roomCategories || []).forEach(cat => {
          const catId = cat._id || cat.name;
          if (persistedCalc[item._id]?.disabledRooms?.[`cat_${catId}`]) return;
          g += (c[`cat_${catId}_single`] || 0) * 1 + (c[`cat_${catId}_double`] || 0) * 2 + (c[`cat_${catId}_triple`] || 0) * 3;
        });
        return g;
      })();
    const addonCost = addonList(item).reduce((s, a) => (addonActive(c, a) ? s + addonAmount(c, a, guests) : s), 0);
    return base + addonCost;
  };

  const oTotals = offsiteItems.map(i => ({ _id: i._id, name: i.name, type: i.type, total: calcTotal(i) }));
  const oMaxTotal = Math.max(...oTotals.map(t => t.total), 1);
  const oAnyNonZero = oTotals.some(t => t.total > 0);

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div>

      {/* ════ PRODUCT CALCULATOR ════ */}
      {isProduct && (() => {
        const allRows = [
          ...shortlisted.map((item, idx) => ({ kind: 'product', id: item._id, item, idx })),
          ...shortlistedCombos.map((combo, ci) => ({ kind: 'combo', id: combo._id || `combo-${ci}`, combo, ci })),
        ];
        const hasAny = allRows.length > 0;
        const activeCombos = shortlistedCombos.filter(c => (qty[c._id] || 0) > 0);
        const lineCount = pActive.length + activeCombos.length;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: isTablet ? '1fr' : 'minmax(0,1fr) 340px', gap: isMobile ? 24 : 36, alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              {!hasAny ? (
                <div style={{ padding: isMobile ? '48px 8px' : '72px 0', textAlign: 'center', border: '1px dashed rgba(0,0,0,0.18)' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: isMobile ? 24 : 30, color: '#222222', marginBottom: 10 }}>Shortlist pieces to price them</div>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 14, color: '#6a6a6a', lineHeight: 1.65, maxWidth: 380, margin: '0 auto 24px' }}>Tap the heart on anything in the catalogue. It will show up here, ready for quantities.</p>
                  {onOpenCatalogue && <button type="button" onClick={onOpenCatalogue} style={pxBtn('primary')}>Browse the catalogue</button>}
                </div>
              ) : (
                <UnifiedCalcTable rows={allRows} qty={qty} setQ={setQ} portal={portal} INR={INR}
                  grandTotalQty={grandTotalQty} onReset={() => { allRows.forEach(r => setQ(r.kind === 'product' ? r.item._id : r.combo._id, 0)); }} />
              )}
            </div>

            <aside aria-label="Order estimate" style={{ position: isTablet ? 'static' : 'sticky', top: 136 }}>
              <div style={{ background: '#fff', border: '1px solid #dddddd', borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', color: '#222222', padding: isMobile ? '24px 20px' : '30px 28px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#7d6233' }}>Order estimate</div>
                <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: isMobile ? 40 : 48, lineHeight: 1.05, marginTop: 14, fontVariantNumeric: 'tabular-nums' }}>{INR(grandTotal)}</div>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#6a6a6a', marginTop: 6 }}>
                  {grandTotalQty > 0 ? `${grandTotalQty.toLocaleString('en-IN')} units · ${lineCount} item${lineCount !== 1 ? 's' : ''}` : 'Add quantities to see your total'}
                </div>

                {lineCount > 0 && (
                  <div style={{ marginTop: 22, borderTop: '1px solid #ebebeb' }}>
                    {pActive.map(l => (
                      <div key={l._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderBottom: '1px solid #ebebeb' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', marginTop: 2 }}>{l.q.toLocaleString('en-IN')} × {INR(l.unit)}</div>
                        </div>
                        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{INR(l.line)}</div>
                      </div>
                    ))}
                    {activeCombos.map((c, i) => (
                      <div key={c._id || i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0', borderBottom: '1px solid #ebebeb' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label || `Bundle ${i + 1}`}</div>
                          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', marginTop: 2 }}>{(qty[c._id] || 0).toLocaleString('en-IN')} × {INR(c.totalPrice)}</div>
                        </div>
                        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{INR((qty[c._id] || 0) * (c.totalPrice || 0))}</div>
                      </div>
                    ))}
                  </div>
                )}

                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a', lineHeight: 1.6, margin: '18px 0 0' }}>
                  Indicative only. The final invoice adds GST, branding or customisation, and shipping.
                </p>
                {onOpenChat && grandTotalQty > 0 && (
                  <button type="button" onClick={onOpenChat}
                    style={{ ...pxBtn('primary'), width: '100%', marginTop: 20, background: 'linear-gradient(135deg, #c9a663, #b8975a)', border: 'none', color: '#fff', fontWeight: 600 }}>
                    Confirm with your planner
                  </button>
                )}
              </div>
            </aside>
          </div>
        );
      })()}


      {/* ════ OFFSITE CALCULATOR ════ */}
      {!isProduct && (
        <div>
          {/* Type filter — only if both types present */}
          {hasDay && hasNight && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
              {[
                { k: 'all', l: `All (${offsiteItems.length})` },
                { k: 'day', l: `☀️ Day Outing (${offsiteItems.filter(i => i.type !== 'Night Stay').length})` },
                { k: 'night', l: `🌙 Night Stay (${offsiteItems.filter(i => i.type === 'Night Stay').length})` },
              ].map(opt => (
                <button key={opt.k} onClick={() => setTypeFilter(opt.k)}
                  style={{
                    height: 40, padding: '0 18px', borderRadius: 40, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: 500, fontSize: 14, transition: 'all .18s',
                    border: `1px solid ${typeFilter === opt.k ? '#222222' : '#dddddd'}`,
                    background: typeFilter === opt.k ? '#222222' : '#fff',
                    color: typeFilter === opt.k ? '#fff' : '#222222'
                  }}>
                  {opt.l}
                </button>
              ))}
            </div>
          )}

          {/* Comparison bar — shown once any total is non-zero */}
          {visibleItems.length > 1 && oAnyNonZero && (
            <div style={{ marginBottom: 28, padding: isMobile ? '18px 16px' : '22px 26px', background: '#fff', border: '1px solid #dddddd', borderRadius: 12, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 200, height: 200, border: '1px solid rgba(184,151,90,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />
              <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 16 }}>Property Comparison</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                {visibleItems.map((item, i) => {
                  const t = oTotals.find(x => x._id === item._id) || { total: 0 };
                  return (
                    <div key={item._id} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: isMobile ? 90 : 150, fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 13, color: '#6a6a6a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</div>
                      <div style={{ flex: 1, height: 5, background: '#ebebeb', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${t.total > 0 ? Math.round(t.total / oMaxTotal * 100) : 0}%`, background: 'linear-gradient(90deg,#b8975a,#d4b06a)', transition: 'width .5s cubic-bezier(.4,0,.2,1)' }} />
                      </div>
                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: isMobile ? 14 : 16, color: t.total > 0 ? '#222222' : '#929292', width: isMobile ? 84 : 100, textAlign: 'right', flexShrink: 0 }}>
                        {t.total > 0 ? INR(t.total) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-property rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleItems.map((item, idx) => {
              const isDayOut = item.type !== 'Night Stay';
              const c = calcs[item._id] || initProp(item);
              const total = calcTotal(item);
              const addons = addonList(item);
              const hasPackages = isDayOut && item.dayPackages?.length > 0;
              const open = !!propOpen[item._id];

              return (
                <div key={item._id} style={{ border: '1px solid #ebebeb', overflow: 'hidden' }}>

                  {/* ── Collapsible header row ── */}
                  <button onClick={() => setPropOpen(p => ({ ...p, [item._id]: !p[item._id] }))}
                    style={{ display: 'flex', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', width: '100%', padding: isMobile ? '14px 16px' : '16px 22px', background: open ? '#f7f7f7' : '#fff', border: 'none', cursor: 'pointer', gap: isMobile ? 8 : 14, transition: 'background .2s' }}>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: open ? '#6a6a6a' : '#b0b0b0', flexShrink: 0 }}>Option {idx + 1}</span>
                    <h3 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 18, fontWeight: 400, color: open ? '#222222' : '#222222', margin: 0, lineHeight: 1.15, flex: isMobile ? '1 1 100%' : 1, order: isMobile ? -1 : 0, textAlign: 'left' }}>{item.name}</h3>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: isDayOut ? '#f59e0b' : '#60a5fa', flexShrink: 0 }}>
                      {isDayOut ? '☀️ Day' : '🌙 Night'}
                    </span>
                    {total > 0 && (
                      <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 18, fontWeight: 400, color: '#222222', flexShrink: 0, lineHeight: 1 }}>{INR(total)}</span>
                    )}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={open ? '#6a6a6a' : '#b0b0b0'} strokeWidth="2"
                      style={{ transform: open ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s', flexShrink: 0 }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {/* ── Expanded body ── */}
                  {open && (
                    <div style={{ padding: isMobile ? '18px 14px' : '22px 22px 18px', background: '#fff', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24 }}>
                      {isDayOut ? (
                        <>
                          {/* Day Outing left: pax + package */}
                          <div>
                            <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 14 }}>Guests</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <button onClick={() => upd(item._id, 'pax', Math.max(0, (c.pax || 0) - 1))}
                                className="air-step">−</button>
                              <input type="number" min="0" value={c.pax || ''} placeholder="0"
                                onChange={e => upd(item._id, 'pax', Math.max(0, Number(e.target.value) || 0))}
                                onFocus={e => e.target.style.borderColor = '#b8975a'}
                                onBlur={e => e.target.style.borderColor = '#dddddd'}
                                style={{ ...inputSt, width: 80 }} />
                              <button onClick={() => upd(item._id, 'pax', (c.pax || 0) + 1)}
                                className="air-step">+</button>
                              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a' }}>guests</span>
                            </div>
                            {hasPackages && (
                              <div style={{ marginTop: 18 }}>
                                <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 10 }}>Package</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {item.dayPackages.map((pkg, pi) => (
                                    <label key={pi} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: `1px solid ${c.pkgId === pi ? '#b8975a' : '#ebebeb'}`, cursor: 'pointer', background: c.pkgId === pi ? 'rgba(184,151,90,0.04)' : '#fff', transition: 'all .15s' }}>
                                      <input type="radio" name={`pkg-${item._id}`} checked={c.pkgId === pi} onChange={() => upd(item._id, 'pkgId', pi)} style={{ accentColor: '#222222' }} />
                                      <div style={{ flex: 1, fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222' }}>{pkg.name || `Package ${pi + 1}`}</div>
                                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#7d6233', flexShrink: 0 }}>{INR(pkg.sellingPrice)}<span style={{ fontSize: 12, color: '#929292', fontFamily: "'Jost',sans-serif" }}>/person</span></div>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            {!hasPackages && item.packagePrice > 0 && (
                              <div style={{ marginTop: 14, padding: '10px 14px', background: '#f7f7f7', border: '1px solid #ebebeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a' }}>Day Package rate</span>
                                <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 16, color: '#7d6233' }}>{INR(item.packagePrice)}<span style={{ fontSize: 12, color: '#929292', fontFamily: "'Jost',sans-serif" }}>/person</span></span>
                              </div>
                            )}
                          </div>
                          {/* Day Outing right: add-ons */}
                          {addons.length > 0 && (
                            <div>
                              <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 14 }}>Add-ons</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {addons.map(a => {
                                  const guests = c.pax || 0;
                                  if (a.unit) return unitAddonRow(item, c, a);
                                  const effectiveVal = a.perPerson ? a.value * Math.max(1, guests) : a.value;
                                  const showMultiplied = a.perPerson && guests > 0 && c.addons[a.key];
                                  return (
                                    <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: `1px solid ${c.addons[a.key] ? '#222222' : '#dddddd'}`, borderRadius: 12, cursor: 'pointer', background: '#fff', transition: 'all .15s' }}>
                                      <input type="checkbox" className="air-toggle" checked={!!c.addons[a.key]} onChange={() => toggleAddon(item._id, a.key)} />
                                      <span style={{ flex: 1, fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222' }}>{a.label}</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                                        {a.perPerson && (
                                          <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#7d6233', background: '#f7f7f7', borderRadius: 40, padding: '2px 8px' }}>per person</span>
                                        )}
                                        <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#7d6233' }}>
                                          {showMultiplied ? INR(effectiveVal) : INR(a.value)}
                                        </span>
                                        {showMultiplied && (
                                          <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292' }}>{INR(a.value)} × {guests}</span>
                                        )}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                              {addons.some(a => addonActive(c, a)) && (
                                <div style={{ marginTop: 10, padding: '8px 14px', background: '#f7f7f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292' }}>Add-ons subtotal</span>
                                  <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 16, color: '#7d6233' }}>{INR(addons.filter(a => addonActive(c, a)).reduce((s, a) => s + addonAmount(c, a, c.pax || 0), 0))}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Night Stay left: occupancy table + nights */}
                          <div>
                            <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 14 }}>Occupancy</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  {['Type', 'Price / Night', 'Rooms', 'Guests'].map((h, hi) => (
                                    <th key={h} style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292', textAlign: hi === 0 || hi === 2 ? 'left' : 'center', paddingBottom: 10, fontWeight: 400 }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { key: 'single', label: 'Single', price: item.singlePrice, guests: 1 },
                                  { key: 'double', label: 'Double', price: item.doublePrice, guests: 2 },
                                  { key: 'triple', label: 'Triple', price: item.triplePrice, guests: 3 },
                                  { key: 'quad', label: 'Quad', price: item.quadPrice, guests: 4 },
                                ].filter(r => r.price > 0 && !(persistedCalc[item._id]?.disabledRooms?.[r.key])).map(r => (
                                  <tr key={r.key} style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                    <td style={{ padding: '9px 0', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#222222' }}>{r.label}</td>
                                    <td style={{ padding: '9px 0', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 14, color: '#6a6a6a', textAlign: 'center' }}>{INR(r.price)}</td>
                                    <td style={{ padding: '9px 8px', textAlign: 'left' }}>
                                      <input type="number" min="0" value={c[r.key] || ''} placeholder="0"
                                        onChange={e => upd(item._id, r.key, Math.max(0, Number(e.target.value) || 0))}
                                        onFocus={e => e.target.style.borderColor = '#b8975a'}
                                        onBlur={e => e.target.style.borderColor = '#dddddd'}
                                        style={{ ...inputSt, width: 64 }} />
                                    </td>
                                    <td style={{ padding: '9px 0', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292', textAlign: 'center' }}>
                                      {((c[r.key] || 0) * r.guests) > 0 ? `${(c[r.key] || 0) * r.guests} guests` : '—'}
                                    </td>
                                  </tr>
                                ))}
                                {/* Room Categories */}
                                {(item.roomCategories || []).map(cat => {
                                  const catId = cat._id || cat.name;
                                  if (persistedCalc[item._id]?.disabledRooms?.[`cat_${catId}`]) return null;
                                  const catRows = [
                                    { key: 'single', label: 'Single', price: cat.singlePrice, guests: 1 },
                                    { key: 'double', label: 'Double', price: cat.doublePrice, guests: 2 },
                                    { key: 'triple', label: 'Triple', price: cat.triplePrice, guests: 3 },
                                  ].filter(r => r.price > 0 && !(persistedCalc[item._id]?.disabledRooms?.[`cat_${catId}_${r.key}`]));
                                  if (catRows.length === 0) return null;
                                  return (
                                    <React.Fragment key={catId}>
                                      <tr>
                                        <td colSpan={4} style={{ paddingTop: 10, paddingBottom: 4, fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#7d6233' }}>{cat.name}</td>
                                      </tr>
                                      {catRows.map(r => {
                                        const compKey = `cat_${catId}_${r.key}`;
                                        return (
                                          <tr key={compKey} style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                            <td style={{ padding: '9px 0 9px 8px', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#222222' }}>{r.label}</td>
                                            <td style={{ padding: '9px 0', fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 14, color: '#6a6a6a', textAlign: 'center' }}>{INR(r.price)}</td>
                                            <td style={{ padding: '9px 8px', textAlign: 'left' }}>
                                              <input type="number" min="0" value={c[compKey] || ''} placeholder="0"
                                                onChange={e => upd(item._id, compKey, Math.max(0, Number(e.target.value) || 0))}
                                                onFocus={e => e.target.style.borderColor = '#b8975a'}
                                                onBlur={e => e.target.style.borderColor = '#dddddd'}
                                                style={{ ...inputSt, width: 64 }} />
                                            </td>
                                            <td style={{ padding: '9px 0', fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292', textAlign: 'center' }}>
                                              {((c[compKey] || 0) * r.guests) > 0 ? `${(c[compKey] || 0) * r.guests} guests` : '—'}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                            {/* Nights stepper */}
                            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, paddingTop: 12, borderTop: '1px solid #ebebeb' }}>
                              <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', flex: 1 }}>Nights</span>
                              <button onClick={() => upd(item._id, 'nights', Math.max(1, (c.nights || 1) - 1))} className="air-step">−</button>
                              <input type="number" min="1" value={c.nights || 1}
                                onChange={e => upd(item._id, 'nights', Math.max(1, Number(e.target.value) || 1))}
                                onFocus={e => e.target.style.borderColor = '#b8975a'}
                                onBlur={e => e.target.style.borderColor = '#dddddd'}
                                style={{ ...inputSt, width: 56 }} />
                              <button onClick={() => upd(item._id, 'nights', (c.nights || 1) + 1)} className="air-step">+</button>
                              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#6a6a6a' }}>night{(c.nights || 1) !== 1 ? 's' : ''}</span>
                            </div>
                            {/* Guest summary tiles */}
                            {(() => {
                              let tG = (c.single || 0) * 1 + (c.double || 0) * 2 + (c.triple || 0) * 3 + (c.quad || 0) * 4;
                              let tR = (c.single || 0) + (c.double || 0) + (c.triple || 0) + (c.quad || 0);
                              (item.roomCategories || []).forEach(cat => {
                                const catId = cat._id || cat.name;
                                if (persistedCalc[item._id]?.disabledRooms?.[`cat_${catId}`]) return;
                                ['single', 'double', 'triple'].forEach((rk, ri) => {
                                  const compKey = `cat_${catId}_${rk}`;
                                  if (persistedCalc[item._id]?.disabledRooms?.[compKey]) return;
                                  const cnt = c[compKey] || 0;
                                  tR += cnt; tG += cnt * (ri + 1);
                                });
                              });
                              if (tG === 0) return null;
                              return (
                                <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(184,151,90,0.05)', border: '1px solid rgba(184,151,90,0.15)', display: 'flex', gap: 20 }}>
                                  {[{ v: tG, l: 'Guests' }, { v: tR, l: 'Rooms' }, { v: c.nights || 1, l: `Night${(c.nights || 1) !== 1 ? 's' : ''}` }].map(({ v, l }) => (
                                    <div key={l} style={{ textAlign: 'center' }}>
                                      <div style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 22, fontWeight: 400, color: '#7d6233', lineHeight: 1 }}>{v}</div>
                                      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292', marginTop: 3 }}>{l}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                          {/* Night Stay right: add-ons */}
                          {addons.length > 0 && (
                            <div>
                              <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292', marginBottom: 14 }}>Add-ons</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {addons.map(a => {
                                  const guests = (c.single || 0) * 1 + (c.double || 0) * 2 + (c.triple || 0) * 3 + (c.quad || 0) * 4;
                                  if (a.unit) return unitAddonRow(item, c, a);
                                  const effectiveVal = a.perPerson ? a.value * Math.max(1, guests) : a.value;
                                  const showMultiplied = a.perPerson && guests > 0 && c.addons[a.key];
                                  return (
                                    <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: `1px solid ${c.addons[a.key] ? '#222222' : '#dddddd'}`, borderRadius: 12, cursor: 'pointer', background: '#fff', transition: 'all .15s' }}>
                                      <input type="checkbox" className="air-toggle" checked={!!c.addons[a.key]} onChange={() => toggleAddon(item._id, a.key)} />
                                      <span style={{ flex: 1, fontFamily: "'Jost',sans-serif", fontSize: 13, color: '#222222' }}>{a.label}</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                                        {a.perPerson && (
                                          <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#7d6233', background: '#f7f7f7', borderRadius: 40, padding: '2px 8px' }}>per person</span>
                                        )}
                                        <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 15, color: '#7d6233' }}>
                                          {showMultiplied ? INR(effectiveVal) : INR(a.value)}
                                        </span>
                                        {showMultiplied && (
                                          <span style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292' }}>{INR(a.value)} × {guests}</span>
                                        )}
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                              {addons.some(a => addonActive(c, a)) && (
                                <div style={{ marginTop: 10, padding: '8px 14px', background: '#f7f7f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292' }}>Add-ons subtotal</span>
                                  <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 16, color: '#7d6233' }}>{INR(addons.filter(a => addonActive(c, a)).reduce((s, a) => s + addonAmount(c, a, (c.single || 0) * 1 + (c.double || 0) * 2 + (c.triple || 0) * 3 + (c.quad || 0) * 4), 0))}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Total footer — shown when expanded or has a total */}
                  {(open || total > 0) && total > 0 && (
                    <div style={{ padding: isMobile ? '12px 14px' : '12px 22px', background: '#f7f7f7', borderTop: '1px solid #ebebeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292' }}>* Excl. GST, branding &amp; transport</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontFamily: "'Jost',sans-serif", fontWeight: 600, fontSize: 12, color: '#929292' }}>Estimated Total</span>
                        <span style={{ fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 26, fontWeight: 400, color: '#7d6233', lineHeight: 1 }}>{INR(total)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Global disclaimer */}
          <div style={{ marginTop: 24, padding: '14px 18px', border: '1px solid #ebebeb', background: '#fff' }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: 12, color: '#929292', lineHeight: 1.75, letterSpacing: '0.04em' }}>
              These figures are indicative estimates. Final invoice will include applicable GST, branding / decoration, and logistics charges. Please confirm your preferences with the team via the message board.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ClientPortalView;